/* eslint-disable no-restricted-syntax */
import React from 'react';
import { useSelector } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import _, { isEmpty, capitalize, map, extend, union, concat, round, cloneDeep, isArray, get, some, includes, flatMap, uniqBy } from 'lodash';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import CryptoJS from 'crypto-js';
import { components } from 'react-select';
import { toasterMsg } from './Toaster/Toaster';
import noImage from '../assets/images/no-image.png';
import s3Config from '../utils/s3modulePath';
import { fetchVimeoMetaData, fetchYoutubeMetadata, getPreReadSignedWriteUrl } from '../services/s3Services';
import productImpact from '../assets/images/productImpact.svg';
import productJourney from '../assets/images/productjourney.svg';
import CustomTooltip from './Tooltip/CustomTooltip';
import careLabelInstruction from '../assets/images/careLabelInstruction.svg';

const moment = require('moment');

export const stripHtmlTags = (html) => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>?/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const shortenString = (text, max) => {
  return text && text.length > max ? `${text.slice(0, max)}...` : text;
};

/**
 * BM-9583: Resolve the company-configurable display name for the "Length" attribute (Variable 1).
 * Reads `variableOneName` from the persisted logged-in user info, falling back to the supplied
 * label (typically the i18n-resolved "Length" string) when no custom name is set. Pure function so
 * it is usable outside React components (column defs, export builders, etc.).
 * @param {string} [fallback] - Text to use when no custom name exists (e.g. t('...LENGTH')).
 * @returns {string} The custom Length attribute name, or the fallback.
 */
export const getLengthLabel = (fallback = 'Length') => {
  try {
    const loggedInUserInfo = JSON.parse(localStorage.getItem('loggedInUserInfo') || 'null');
    const customName = loggedInUserInfo?.variableOneName;
    return typeof customName === 'string' && customName.trim() ? customName : fallback;
  } catch (error) {
    return fallback;
  }
};

export const statusArray = [
  { value: 1, label: 'Active' },
  { value: 2, label: 'Cancelled' },
  { value: 3, label: 'Cancel Request' },
  { value: 4, label: 'Confirmed' },
  { value: 5, label: 'Delivered' },
  { value: 6, label: 'Draft' },
  { value: 7, label: 'Rejected' },
  { value: 8, label: 'Revised' },
];

export const shortenandHoverString = (text, max, placement) => {
  return text?.length > max ? (
    <OverlayTrigger
      placement={placement || 'auto'}
      overlay={
        <Tooltip className="text-tooltip" id={`tooltip_${Date.now()}`}>
          {text}
        </Tooltip>
      }
    >
      <span>{`${text.slice(0, max)}...`}</span>
    </OverlayTrigger>
  ) : (
    text
  );
};

export const colourWayOptions = (arr) => {
  let newArr = [];
  if (!isEmpty(arr)) {
    newArr = arr.filter((ele) => ele.isDeleted === false);
  }
  return newArr;
};

export const defaultCompanyRights = {
  default: false,
  productCosting: false,
  purchaseOrder: false,
  componentOrder: false,
  freightAndDuty: false,
  customModule: false,
  productForum: false,
  productDoc: false,
  supplierSuggestion: false,
  manualDocuments: false,
  reports: false,
  planningTopsDown: false,
  planningBottomUp: false,
  placeholderManagement: false,
  rangePlan: false,
  operationalCriticalPath: false,
  productCriticalPath: false,
  pim: false,
  dam: false,
  dpp: false,
  testing: false,
};

export const checkCompanyModuleRight = (companyRightsData, module) => {
  return !!companyRightsData[module];
};

// for matching route URL key with our company right access key
export const TabsToRightKeys = {
  suggestion: 'supplierSuggestion',
  costing: 'productCosting',
  'purchase-order': 'purchaseOrder',
  'product-purchase-order-todo': 'purchaseOrder',
  'placeholder-management': 'placeholderManagement',
  rangePlan: 'rangePlan',
  'operational-cp': 'operationalCriticalPath',
  'product-cp': 'productCriticalPath',
  'product-costing': 'productCosting',
  'planning-bottomup': 'planningBottomUp',
  'freight-duty': 'freightAndDuty',
  freight: 'freightAndDuty',
  duty: 'freightAndDuty',
  'supplier-suggestion': 'supplierSuggestion',
  'component-order': 'componentOrder',
  'todo-list': 'reports',
  'digital-assets-management': 'dam',
};

// fetch company rights from reducer and store module wise in separate state
export const RightsAccess = () => {
  const reducer = useSelector((redState) => redState);
  const {
    CompanyRightsReducer: { companyRightsData, excludesCompanyModules },
  } = reducer;

  const suggestionRight = checkCompanyModuleRight(companyRightsData, 'supplierSuggestion');
  const productCostingRight = checkCompanyModuleRight(companyRightsData, 'productCosting');
  const purchaseOrderRight = checkCompanyModuleRight(companyRightsData, 'purchaseOrder');
  const placeholderRight = checkCompanyModuleRight(companyRightsData, 'placeholderManagement');
  const rangePlanRight = checkCompanyModuleRight(companyRightsData, 'rangePlan');
  const PIMRight = checkCompanyModuleRight(companyRightsData, 'pim');
  const planningBottomUpRight = checkCompanyModuleRight(companyRightsData, 'planningBottomUp');
  const planningTopsDownRight = checkCompanyModuleRight(companyRightsData, 'planningTopsDown');
  const componentOrderRight = checkCompanyModuleRight(companyRightsData, 'componentOrder');
  const reportsRight = checkCompanyModuleRight(companyRightsData, 'reports');
  const frieghtRight = checkCompanyModuleRight(companyRightsData, 'freightAndDuty');
  const productCriticalPathRight = checkCompanyModuleRight(companyRightsData, 'productCriticalPath');
  const operationalCriticalPathRight = checkCompanyModuleRight(companyRightsData, 'operationalCriticalPath');
  const customModuleRight = checkCompanyModuleRight(companyRightsData, 'customModule');
  const forumRight = checkCompanyModuleRight(companyRightsData, 'productForum');
  const productDocRight = checkCompanyModuleRight(companyRightsData, 'productDoc');
  const manualDocsRight = checkCompanyModuleRight(companyRightsData, 'manualDocuments');
  const checkRightWithKey = (key) => checkCompanyModuleRight(companyRightsData, key);
  const DAMRight = checkCompanyModuleRight(companyRightsData, 'dam');
  const DPPRight = checkCompanyModuleRight(companyRightsData, 'dpp');
  const TestingRight = checkCompanyModuleRight(companyRightsData, 'testing');
  const excludesCompanyRights = excludesCompanyModules;

  return {
    suggestionRight,
    productCostingRight,
    purchaseOrderRight,
    placeholderRight,
    rangePlanRight,
    PIMRight,
    planningBottomUpRight,
    planningTopsDownRight,
    componentOrderRight,
    reportsRight,
    frieghtRight,
    productCriticalPathRight,
    checkRightWithKey,
    forumRight,
    productDocRight,
    manualDocsRight,
    operationalCriticalPathRight,
    customModuleRight,
    DAMRight,
    DPPRight,
    excludesCompanyRights,
    TestingRight,
  };
};

export const borderBottom = '1px solid #848484';
export const borderBottomNew = '1px solid #6a6a6a';

const opacity300 = 'opacity 300ms';
const menuMargin = '-1px 0 0 0';
const valueContainerFont = 'Aktiv Grotesk, sans-serif !important';

const colourFastnessType = 'Colour Fastness';
const dimensionalStabilityType = 'Dimensional Stability';
const CompositionAndAnalyticalChemistryType = 'Composition and Analytical Chemistry';
const zipPerformanceType = 'Zip Performance';
const colorMeasurementType = 'Colour Measurement';
const filledProductAndThermalResistanceType = 'Filled Product and Thermal Resistance';
const childrenSleepingBags = "Children's Sleeping Bags";
const chemistryHarmfulSubstances = 'Chemistry - Harmful Substances';
const flammabilityPreTreatments = 'Flammability - Pre-treatments';
const childrenGarmentAssesment = "Children's Garment Assesment";
const ThermalAndWaterVaporResistance = 'Thermal and Water Vapor Resistance';
const ShipmentDate = 'Shipment Date';
const seaAndTruck = 'Sea and Truck';
const productInformation = 'Product Information';
const billOfMaterials = 'Bill of Materials';
const costingInformation = 'Costing Information';
const gradingGroup = 'Grading Group';
// eslint-disable-next-line sonarjs/no-duplicate-string
const colourRef = 'Colour Ref';
const supplierCode = 'Supplier Code';
const supplierName = 'Supplier Name';
const additionalInformation = 'Additional Information';
const factoryCode = 'Factory Code';
const factoryName = 'Factory Name';
const HandoverCargoReadyDate = 'Handover/Cargo Ready Date';
const firstItem = 'First Item';
const ActualHandoverCargoReadyDate = 'Actual Handover/Cargo Ready Date';
const actualShipmentDate = 'Actual Shipment Date';
const deliveryDate = 'Delivery Date';
const actualDeliveryDate = 'Actual Delivery Date';
const category1 = 'Category 1';
// eslint-disable-next-line sonarjs/no-duplicate-string
const shipmentMethod = 'Shipment Method';
const productNumber = 'Product Number';
const category2 = 'Category 2';
const category3 = 'Category 3';
const category4 = 'Category 4';
const colourwayName = 'Colourway Name';
const DatePicker = 'Date picker';
const singleSelectDropdown = 'Single-select Dropdown';
const longTextArea = 'Long Text (Text Area)';
const markAsComplete = 'Mark as Complete';
const multiSelectDropdown = 'Multi-select Dropdown';
const numberDecimal = 'Number (Decimal)';
const numberInteger = 'Number (Integer)';
const numberPercentage = 'Number (Percentage)';
const radioButton = 'Radio button';
const shortTextBox = 'Short Text (Text Box)';
const TimePicker = 'Time picker';
const dateAdded = 'Date Added';
const percentageOfApproval = 'Percentage of Approval';
const addColumnNameAssignedToYou = 'Add Column Name (Assigned to you)';
const selectAll = 'Select All';
const statusChange = 'Status Change';
const supplierSuggestions = 'Supplier Suggestions';
const addColumnNameNotAssignedToYou = 'Add Column Name (not Assigned to you)';
const updateColumnNameAssignedToYou = 'Update Column Name (Assigned to you)';
const approveReject = 'Approve/Reject';
const archiveUnarchive = 'Archive/Unarchive';
const updateColumnNameNotAssignedToYou = 'Update Column Name (not Assigned to you)';
const deleteColumnNameAssignedToYou = 'Delete Column Name (Assigned to you)';
const deleteColumnNameNotAssignedToYou = 'Delete Column Name (not Assigned to you)';
const addCellDataAssignedToYou = 'Add Cell Data (Assigned to you)';
const addCellDataNotAssignedToYou = 'Add Cell Data (not Assigned to you)';
const updateCellDataAssignedToYou = 'Update Cell Data (Assigned to you)';
const updateCellDataNotAssignedToYou = 'Update Cell Data (not Assigned to you)';
const deleteCellDataAssignedToYou = 'Delete Cell Data (Assigned to you)';
const deleteCellDataNotAssignedToYou = 'Delete Cell Data (not Assigned to you)';
const assignmentRemovalOfYourGroup = 'Assignment/Removal (of you/your group)';
const assignmentRemovalOfOthers = 'Assignment/Removal (of others)';
const preparationInProgress = 'Preparation in Progress';
const preparedAndReadyForDownload = 'Prepared and Ready for Download';
const bulkUploadInProgress = 'Bulk Upload in Progress';
const bulkUploadCompleted = 'Bulk Upload Completed';

export const SystemMasterSelect = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderBottom,
    maxHeight: 25,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottomNew,
    borderRadius: 0,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '176px',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
  }),
  placeholder: (provided) => ({
    ...provided,
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};

export const SelectNoBorder = {
  ...SystemMasterSelect,

  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderBottom: 'none',
    maxHeight: 35,
  }),
};

export const styleInsidePopover = {
  ...SystemMasterSelect,
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderBottom,
    maxHeight: 26,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
    fontFamily: 'Aktiv Grotesk',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#6A6A6A',
    fontFamily: 'Aktiv Grotesk',
  }),
};

export const delay = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const restrictNumberUpDownUpdate = (e) => {
  return [38, 40].includes(e.keyCode) && e.preventDefault();
};

export const restrictPlusAndMinus = (e) => {
  if (e.key === '+' || e.key === '-') {
    e.preventDefault();
  }
};

/**
 * Strips every character that cannot form a non-negative decimal, keeping at most
 * one decimal point. Use on kWh/kg style measurement inputs the backend parses
 * with Number(), so text such as "High" can never be saved.
 *
 * @param {object} e - Input event whose target value is sanitised in place
 * @returns {void}
 */
export const allowOnlyDecimal = (e) => {
  const cleaned = e.target.value.replace(/[^0-9.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  e.target.value = rest.length > 0 ? `${whole}.${rest.join('')}` : whole;
};

/**
 * react-hook-form validate rule for non-negative decimal fields.
 *
 * @param {string} value - Current field value
 * @returns {boolean|string} - True when valid, otherwise the error message key
 */
export const validateNonNegativeDecimal = (value) => {
  if (!value || String(value).trim() === '') return true;
  return /^\d+(\.\d+)?$/.test(String(value).trim()) || 'Enter a non-negative number, e.g. 1.5';
};

export const allowOnlyNumber = (e) => {
  const ans = [];
  function range(start, end) {
    for (let i = start; i <= end; i++) {
      ans.push(i);
    }
    return ans;
  }
  return (range(65, 90) || range(97, 112)).includes(e.keyCode) && e.preventDefault();
};
export const months = [
  { _id: 'Jan', name: 'Jan' },
  { _id: 'Feb', name: 'Feb' },
  { _id: 'Mar', name: 'Mar' },
  { _id: 'Apr', name: 'Apr' },
  { _id: 'May', name: 'May' },
  { _id: 'Jun', name: 'Jun' },
  { _id: 'Jul', name: 'Jul' },
  { _id: 'Aug', name: 'Aug' },
  { _id: 'Sept', name: 'Sept' },
  { _id: 'Oct', name: 'Oct' },
  { _id: 'Nov', name: 'Nov' },
  { _id: 'Dec', name: 'Dec' },
];

export const monthsPlaceholder = [
  { _id: 'Jan', name: 'Jan' },
  { _id: 'Feb', name: 'Feb' },
  { _id: 'Mar', name: 'Mar' },
  { _id: 'Apr', name: 'Apr' },
  { _id: 'May', name: 'May' },
  { _id: 'Jun', name: 'Jun' },
  { _id: 'Jul', name: 'Jul' },
  { _id: 'Aug', name: 'Aug' },
  { _id: 'Sept', name: 'Sept' },
  { _id: 'Oct', name: 'Oct' },
  { _id: 'Nov', name: 'Nov' },
  { _id: 'Dec', name: 'Dec' },
];

export const regexForEmail = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
/*
 * Digits only, with an optional leading `+` and the usual separators. The
 * previous pattern was the North American 3-3-4 shape, which rejected every
 * number that is not exactly ten digits — wrong for a customer base that is
 * global, where national number lengths vary. Length is deliberately not
 * constrained here.
 */
export const regexForPhone = /^[+]?[\d\s().-]+$/;

export const checkStatus = (data) => {
  const status = ['status-gray', 'status-green', 'status-red', 'status-amber'];
  const element = data
    ? {
        ...data,
        colourWayRefsData: data.colourWayRefsData ? data.colourWayRefsData : [],
        bomApprovals: data.bomApprovals ? data.bomApprovals : [],
      }
    : {};
  let newVal = element;
  if (element && Object.keys(element).length && element.colourWayRefsData && element.bomApprovals) {
    if (element.colourWayRefsData.length === 0 && element.bomApprovals.length === 0) {
      element.status = status[0];
      newVal = element;
    } else {
      let quality;
      if (element.bomApprovals.length > 0) {
        const qualityRejctStatus = element.bomApprovals.filter((val) => val.status === 0);
        const approvedStatus = element.bomApprovals.filter((val) => val.status === 1);
        if (element.bomApprovals.length !== 0 && qualityRejctStatus.length === element.bomApprovals.length) {
          quality = 'rejected';
        } else if (element.bomApprovals.length !== 0 && approvedStatus.length === element.bomApprovals.length) {
          quality = 'approved';
        } else if (
          element.bomApprovals.length !== 0 &&
          approvedStatus.length !== element.bomApprovals.length &&
          qualityRejctStatus.length !== element.bomApprovals.length
        ) {
          quality = 'pending';
        }
      } else if (element.bomApprovals.length === 0) {
        quality = 'pending';
      }

      let totalColourLength = 0;
      let statusCount = 0;
      let approvedStatus = 0;
      let rejectStatus = 0;
      element.colourWayRefsData.forEach((item) => {
        if (item.colourWayId) {
          if (item.colourRefsDataAsPerSuppliers) {
            totalColourLength += item.colourRefsDataAsPerSuppliers.length;
            item.colourRefsDataAsPerSuppliers.forEach((ele) => {
              if (!ele.hasOwnProperty('status')) {
                statusCount += 1;
              }
              if (ele.status && ele.status === 1) {
                approvedStatus += 1;
              }
              if (ele.hasOwnProperty('status') && ele.status === 0) {
                rejectStatus += 1;
              }
            });
          }
        }
      });
      if (totalColourLength === 0 && quality === 'approved') {
        element.status = status[1];
        newVal = element;
      }
      if (totalColourLength === 0 && quality === 'rejected') {
        element.status = status[2];
        newVal = element;
      }
      if (totalColourLength) {
        if (approvedStatus === totalColourLength) {
          element.status = quality === 'approved' ? status[1] : status[3];
          newVal = element;
        } else if (rejectStatus === totalColourLength) {
          element.status = quality === 'rejected' ? status[2] : status[3];
          newVal = element;
        } else {
          element.status = status[3];
          newVal = element;
        }
      }
      if (quality && quality === 'pending' && (!element.status || element.status === '')) {
        element.status = status[3];
        newVal = element;
      }
    }
    return newVal?.status ? newVal.status : status[0];
  }
  return '';
};

export const SelectBoxStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '40px',
    borderBottom,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};

export const SelectBoxStylesForProductAttribute = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: borderBottom,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};

export const customStyles = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    // height: "100%",
    height: '40px',
    borderBottom,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.7 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
    fontSize: 14,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
    fontFamily: valueContainerFont,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
    fontSize: 14,
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#aaa', // Custom colour
    padding: '8px 2px',
    svg: {
      width: '22px',
      height: '22px',
    },
  }),
};

export const customReactSelect = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    borderBottom,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.7 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
    width: '450px',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};

export const customStylesProductAddEdit = {
  ...customStyles,
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
    zIndex: 99,
  }),
  placeholder: (base, state) => ({
    ...base,
    color: state.isDisabled ? '#a19e9e' : '#848484',
    position: 'absolute',
    top: !state.hasValue || state.selectProps.isFocused ? '50%' : '-50%',
    transition: 'top 0.2s, font-size 0.2s',

    fontSize: 14,
    background: '#FFF',
    width: '100%',
    paddingTop: '5px',
    paddingBottom: '5px',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: '#aaa', // Change the color of the clear button here
  }),
};

export const customStylesPlaceholderBlack = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '40px',
    borderBottom,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.7 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
    fontSize: 14,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
    fontFamily: valueContainerFont,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#000000',
    fontSize: 14,
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#aaa', // Custom colour
    padding: '8px 2px',
    svg: {
      width: '22px',
      height: '22px',
    },
  }),
};

export const valueOrEmpty = (obj) => {
  return obj || '';
};

export const customStylesForOCP = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '30px',
    borderBottom,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.7 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 140, // Default max-height when no maxMenuHeight prop is provided
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
    fontFamily: valueContainerFont,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
    fontSize: 14,
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#aaa', // Custom colour
    padding: '8px 2px',
    svg: {
      width: '22px',
      height: '22px',
    },
  }),
};

export const customStylesForProdAttribute = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '150px',
    height: '100%',
    borderBottom,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacity300;
    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};

export const customStylesWhite = {
  option: (provided, state) => ({
    ...provided,
    padding: '5px 10px',
    backgroundColor: state.isFocused ? '#f0f0f0' : 'white', // Change active (focused) color
    color: state.isFocused ? 'black' : '#333', // Text color when focused
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    background: '#FFF',
    height: '30px',
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition, display: 'inline-flex' };
  },
  menu: (provided, state) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
    fontSize: 14,
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: '0 0 0 8px',
    color: '#555',
    width: 32,
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: 'none',
  }),
};

export const customStylesWhiteBordered = {
  ...customStylesWhite,
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    background: '#FFFFFF',
    border: borderBottom,
    height: '34px',
    borderRadius: 2,
  }),
};
export const costinStylesWhiteBordered = {
  ...customStylesWhite,
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    background: '#FFFFFF',
    border: borderBottom,
    height: '30px',
    borderRadius: 2,
  }),
};
export const types = [
  { name: 'Appearance', _id: 'Appearance' },
  { name: 'Chemical', _id: 'Chemical' },
  { name: colourFastnessType, _id: colourFastnessType },
  { name: dimensionalStabilityType, _id: dimensionalStabilityType },
  {
    name: CompositionAndAnalyticalChemistryType,
    _id: CompositionAndAnalyticalChemistryType,
  },
  { name: 'Construction', _id: 'Construction' },
  { name: zipPerformanceType, _id: zipPerformanceType },
  { name: colorMeasurementType, _id: colorMeasurementType },
  {
    name: filledProductAndThermalResistanceType,
    _id: filledProductAndThermalResistanceType,
  },
  { name: childrenSleepingBags, _id: childrenSleepingBags },
  {
    name: chemistryHarmfulSubstances,
    _id: chemistryHarmfulSubstances,
  },
  {
    name: flammabilityPreTreatments,
    _id: flammabilityPreTreatments,
  },
  {
    name: childrenGarmentAssesment,
    _id: childrenGarmentAssesment,
  },
  { name: 'Other', _id: 'Other' },
  { name: 'Physical', _id: 'Physical' },
  { name: 'Flammability', _id: 'Flammability' },
  {
    name: ThermalAndWaterVaporResistance,
    _id: ThermalAndWaterVaporResistance,
  },
];

export const customStylesForTesting = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
    zIndex: 100,
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    borderBottom,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
    zIndex: 9999,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};

// 1. QA =u, CA=u  grey
// 2. QA=0 CA = 0 red
// 3. QA =1, CA=0 amber
// 4. QA = 0, CA=1 amber
// 5. QA =1, CA = u  green
// 6. QA= 1, CA=1 green

export const removeSpecials = (str) => {
  return str?.length > 0 ? str.replace(/[^a-zA-Z0-9]/g, '') : '';
};

export const shortenOptionsWithTooltip = (values, max) => {
  if (values && values.length > 0) {
    values.map((text) => {
      if (text && text?.label?.length > max) {
        return (
          <OverlayTrigger
            key={text?.value}
            placement="bottom"
            overlay={
              <Tooltip className="text-tooltip" id={`tooltip_${Date.now()}`}>
                {text?.label}
              </Tooltip>
            }
          >
            <span>{`${text?.label.slice(0, max)}...`}</span>
          </OverlayTrigger>
        );
      }
      return text?.label;
    });
  }
};

export const shortenStringWithTooltip = (text, max, placement) => {
  if (text && text.length > max) {
    return (
      <CustomTooltip tooltipText={text}>
        <span>{`${text.slice(0, max)}...`}</span>
      </CustomTooltip>
    );
  }
  return text;
};

export const shortenStringWithTooltipWithBold = (boldText, normalText, max) => {
  if (normalText && normalText.length > max) {
    return (
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip className="text-tooltip" id={`tooltip_${Date.now()}`}>
            <strong>{boldText}</strong>
            {normalText}
          </Tooltip>
        }
      >
        <span>
          <strong>{boldText.slice(0, 20)}</strong>
          {`${normalText.slice(0, max)}...`}
        </span>
      </OverlayTrigger>
    );
  }
  return (
    <span>
      <strong>{boldText}</strong>
      {normalText}
    </span>
  );
};

export const Page_Size = 20;

export const pageSize = 20;

export const hoverTooltip = (text, addedClass) => {
  if (text?.length) {
    return (
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip className="text-tooltip" id={`tooltip_${Date.now()}`}>
            {text}
          </Tooltip>
        }
      >
        <CustomTooltip placement="top" tooltipText={text}>
          <span className={addedClass ? 'txt-truncate' : ''}>{text}</span>
        </CustomTooltip>
      </OverlayTrigger>
    );
  }
  return text;
};

export const hoverMultipleTooltip = (text, toShow) => {
  if (text?.length) {
    return (
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip className="text-tooltip" id={`tooltip_${Date.now()}`}>
            {toShow}
          </Tooltip>
        }
      >
        <span>
          Multiple(<strong>{text}</strong>)
        </span>
      </OverlayTrigger>
    );
  }
  return text;
};

export const hoverMultipleSupplTooltip = (toShow) => {
  if (toShow?.length) {
    return (
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip className="text-tooltip" id={`tooltip_${Date.now()}`}>
            {toShow}
          </Tooltip>
        }
      >
        <span>
          <strong>Multiple</strong>
        </span>
      </OverlayTrigger>
    );
  }
  return toShow;
};

export const selectNewStyle = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: borderBottomNew,
    height: '32px',
    minWidth: '150px',
    padding: '5px 0 5px 5px',
    fontSize: '14px',
    background: '#FFF',
    borderRadius: 2,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
    fontSize: 14,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
    fontFamily: 'Aktiv Grotesk',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#6A6A6A',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};
export const selectLibraryStyle = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: borderBottomNew,
    height: '32px',
    padding: '5px 0 5px 5px',
    fontSize: '14px',
    background: '#FFF',
    borderRadius: 2,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
    fontSize: 14,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
    fontFamily: 'Aktiv Grotesk',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#6A6A6A',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};
export const customStylesBom = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: borderBottom,
    height: '35px',
    minWidth: '150px',
    padding: '5px 0 5px 5px',
    fontSize: '14px',
    background: '#FFF',
    borderRadius: 2,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
    fontSize: 14,
    zIndex: 9999,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#6A6A6A',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};
export const customStylesGradingFilter = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: borderBottom,
    height: '35px',
    minWidth: '32px',
    padding: '5px 0 5px 5px',
    fontSize: '14px',
    background: '#FFF',
    borderRadius: 2,
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
    fontSize: 14,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#6A6A6A',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};

export const suppliersIcon = (label) => {
  const icons = [
    { label: 'Artwork', icon: 'icon-prints', title: 'Artwork Supplier' },
    {
      label: 'Accessories',
      icon: 'icon-Accessories',
      title: 'Accessories Supplier',
    },
    { label: 'Footwear', icon: 'icon-Footwear', title: 'Footwear Supplier' },
    { label: 'Garment', icon: 'icon-Garment', title: 'Garment Supplier' },
    { label: 'Label', icon: 'icon-labels', title: 'Label Supplier' },
    { label: 'Material', icon: 'icon-materials', title: 'Material Supplier' },
    { label: 'Packaging', icon: 'icon-packaging', title: 'Packaging Supplier' },
    { label: 'Trim', icon: 'icon-trims', title: 'Trim Supplier' },
  ];

  return icons.filter((e) => e.label === label);
};

export const Moq_List = ['Per Order', 'Per ColourWay'];

export const CountriesList = [
  'Afghanistan',
  'Albania',
  'Algeria',
  'American Samoa',
  'Andorra',
  'Angola',
  'Anguilla',
  'Antarctica',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Aruba',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bermuda',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegowina',
  'Botswana',
  'Bouvet Island',
  'Brazil',
  'British Indian Ocean Territory',
  'Brunei Darussalam',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Cape Verde',
  'Cayman Islands',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Christmas Island',
  'Cocos (Keeling) Islands',
  'Colombia',
  'Comoros',
  'Congo',
  'Congo, the Democratic Republic of the',
  'Cook Islands',
  'Costa Rica',
  "Cote d'Ivoire",
  'Croatia (Hrvatska)',
  'Cuba',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'East Timor',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Ethiopia',
  'Falkland Islands (Malvinas)',
  'Faroe Islands',
  'Fiji',
  'Finland',
  'France',
  'France Metropolitan',
  'French Guiana',
  'French Polynesia',
  'French Southern Territories',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Gibraltar',
  'Greece',
  'Greenland',
  'Grenada',
  'Guadeloupe',
  'Guam',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Heard and Mc Donald Islands',
  'Holy See (Vatican City State)',
  'Honduras',
  'Hong Kong',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran (Islamic Republic of)',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  "Korea, Democratic People's Republic of",
  'Korea, Republic of',
  'Kuwait',
  'Kyrgyzstan',
  "Lao, People's Democratic Republic",
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libyan Arab Jamahiriya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Macau',
  'Macedonia, The Former Yugoslav Republic of',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Martinique',
  'Mauritania',
  'Mauritius',
  'Mayotte',
  'Mexico',
  'Micronesia, Federated States of',
  'Moldova, Republic of',
  'Monaco',
  'Mongolia',
  'Montserrat',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'Netherlands Antilles',
  'New Caledonia',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'Niue',
  'Norfolk Island',
  'Northern Mariana Islands',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Pitcairn',
  'Poland',
  'Portugal',
  'Puerto Rico',
  'Qatar',
  'Reunion',
  'Romania',
  'Russian Federation',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia (Slovak Republic)',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Georgia and the South Sandwich Islands',
  'Spain',
  'Sri Lanka',
  'St. Helena',
  'St. Pierre and Miquelon',
  'Sudan',
  'Suriname',
  'Svalbard and Jan Mayen Islands',
  'Swaziland',
  'Sweden',
  'Switzerland',
  'Syrian Arab Republic',
  'Taiwan, Province of China',
  'Tajikistan',
  'Tanzania, United Republic of',
  'Thailand',
  'Togo',
  'Tokelau',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Turks and Caicos Islands',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'United States Minor Outlying Islands',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Venezuela',
  'Vietnam',
  'Virgin Islands (British)',
  'Virgin Islands (U.S.)',
  'Wallis and Futuna Islands',
  'Western Sahara',
  'Yemen',
  'Yugoslavia',
  'Zambia',
  'Zimbabwe',
];

export const Events = ['Product Approve by Date', 'Handover Date', ShipmentDate];

const currentYear = moment().year();
const yearsArray = [];
for (let year = 2015; year <= currentYear; year++) {
  yearsArray.push({
    name: year.toString(),
    _id: year,
  });
}
export const Year = yearsArray;

export const colourYear = [
  { name: '2016', _id: 2016 },
  { name: '2017', _id: 2017 },
  { name: '2018', _id: 2018 },
  { name: '2019', _id: 2019 },
  { name: '2020', _id: 2020 },
  { name: '2021', _id: 2021 },
  { name: '2022', _id: 2022 },
  { name: '2023', _id: 2023 },
  { name: '2024', _id: 2024 },
  { name: '2025', _id: 2025 },
  { name: '2026', _id: 2026 },
  { name: '2027', _id: 2027 },
  { name: '2028', _id: 2028 },
];

export const checkUpdatedData = (newData, oldData) => {
  if (newData !== oldData) {
    return (
      <td>
        <div className="suggested-data">{shortenandHoverString(newData, 40) || '--'}</div>
      </td>
    );
  }
  return <td>{shortenandHoverString(newData, 40)}</td>;
};
export const hasDuplicates = (array) => {
  return new Set(array).size !== array.length;
};
export const testTypeList = [
  { label: 'Appearance', value: 'Appearance' },
  { label: 'Chemical', value: 'Chemical' },
  { label: colourFastnessType, value: colourFastnessType },
  { label: dimensionalStabilityType, value: dimensionalStabilityType },
  {
    label: CompositionAndAnalyticalChemistryType,
    value: CompositionAndAnalyticalChemistryType,
  },
  { label: 'Construction', value: 'Construction' },
  { label: zipPerformanceType, value: zipPerformanceType },
  { label: colorMeasurementType, value: colorMeasurementType },
  {
    label: filledProductAndThermalResistanceType,
    value: filledProductAndThermalResistanceType,
  },
  { label: childrenSleepingBags, value: childrenSleepingBags },
  {
    label: chemistryHarmfulSubstances,
    value: chemistryHarmfulSubstances,
  },
  {
    label: flammabilityPreTreatments,
    value: flammabilityPreTreatments,
  },
  {
    label: childrenGarmentAssesment,
    value: childrenGarmentAssesment,
  },
  { label: 'Other', value: 'Other' },
  { label: 'Physical', value: 'Physical' },
  { label: 'Flammability', value: 'Flammability' },
  {
    label: ThermalAndWaterVaporResistance,
    value: ThermalAndWaterVaporResistance,
  },
];

export const convertSelectLabel = (value, noChange) => {
  if (!isEmpty(value)) {
    return {
      label: !noChange ? capitalize(value) : value,
      value,
    };
  }
  return null;
};

export const checkIfSectionNameExist = (orgArr, arr, key, childKey) => {
  const sections = orgArr.map((val) => val[key].toLowerCase());
  const sectionNames = arr.map((val) => (childKey ? val[childKey].toLowerCase() : val[key].toLowerCase()));
  let existNames = [];
  arr.forEach((val) => {
    if (sections.includes(val[key].trim().toLowerCase())) {
      existNames.push(val[key]);
    }
  });
  const unique = sectionNames
    .map((name) => {
      return { count: 1, [key]: name };
    })
    .reduce((a, b) => {
      a[b[key]] = (a[b[key]] || 0) + b.count;
      return a;
    }, {});

  const duplicates = Object.keys(unique).filter((a) => unique[a] > 1);
  existNames = existNames.concat(duplicates);
  return existNames;
};

export const getTypeBasedCount = async (array, keys, arrayKeyCount) => {
  const tempObj = {};
  if (Array.isArray(array) && array.length > 0) {
    await keys.forEach(async (value) => {
      tempObj[value.updateKey] = await uniqueItems(array, value.key, arrayKeyCount);
    });
  }
  return tempObj;
};

const uniqueItems = async (list, keyFn, arrayKeyCount) => {
  list = list.filter((val) => !isEmpty(val[keyFn]));
  if (arrayKeyCount && keyFn === 'size') {
    const size = list.map((val) => val[keyFn]);
    const sizeLength = union.apply(_, size);
    return sizeLength.length;
  }
  return list.reduce((resultSet, item) => resultSet.add(typeof keyFn === 'string' ? item[keyFn] : 0), new Set()).size;
};

export const calculateTotValues = async (array, keys) => {
  const tempObj = {};
  for (const value of keys) {
    tempObj[value.updateKey] = uniqueItemsSum(array, value.key);
  }
  return cloneData(tempObj);
};

const uniqueItemsSum = (list, keyFn) => {
  list = list.filter((val) => keyFn in val && val[keyFn] > 0);
  return list.reduce((a, b) => +a + +b[keyFn], 0).toString();
};

export const setKeysValueToArray = async (array, keys, addKeyArry) => {
  array = await map(array, function (element) {
    return extend({}, element, { [addKeyArry]: [] });
  });

  await array.forEach((nested) => {
    const obj = {};
    keys.forEach((data) => {
      obj[data.key] = data.value;
    });
    nested[addKeyArry].push(obj);
  });
  return array;
};

export const SelectBoxStylesWhite = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    background: '#FFF',
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacity300;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: menuMargin,
    border: borderBottom,
    borderRadius: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 0 0 10px',
    color: '#555',
    fontSize: 13,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: 'none',
  }),
};

export const careTypeItem = [
  { label: 'Wash', value: 'Wash' },
  { label: 'Bleach', value: 'Bleach' },
  { label: 'Dry', value: 'Dry' },
  { label: 'Iron', value: 'Iron' },
  { label: 'Professional care', value: 'Professional care' },
  { label: 'Other', value: 'Other' },
];
export const disclaimerTypeItem = [
  { label: 'Wash', value: 'Wash' },
  { label: 'Caution', value: 'Caution' },
  { label: 'Warning', value: 'Warning' },
  { label: 'Appearance', value: 'Appearance' },
  { label: 'Animal Parts', value: 'Animal Parts' },
  { label: 'Others', value: 'Others' },
];

export const currencyCodes = [
  'AED',
  'AFN',
  'ALL',
  'AMD',
  'ANG',
  'AOA',
  'ARS',
  'AUD',
  'AWG',
  'AZN',
  'BAM',
  'BBD',
  'BDT',
  'BGN',
  'BHD',
  'BIF',
  'BMD',
  'BND',
  'BOB',
  'BOV',
  'BRL',
  'BSD',
  'BTN',
  'BWP',
  'BYR',
  'BZD',
  'CAD',
  'CDF',
  'CHE',
  'CHF',
  'CHW',
  'CLF',
  'CLP',
  'CNY',
  'COP',
  'COU',
  'CRC',
  'CUC',
  'CUP',
  'CVE',
  'CZK',
  'DJF',
  'DKK',
  'DOP',
  'DZD',
  'EGP',
  'ERN',
  'ETB',
  'EUR',
  'FJD',
  'FKP',
  'GBP',
  'GEL',
  'GHS',
  'GIP',
  'GMD',
  'GNF',
  'GTQ',
  'GYD',
  'HKD',
  'HNL',
  'HRK',
  'HTG',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'IQD',
  'IRR',
  'ISK',
  'JMD',
  'JOD',
  'JPY',
  'KES',
  'KGS',
  'KHR',
  'KMF',
  'KPW',
  'KRW',
  'KWD',
  'KYD',
  'KZT',
  'LAK',
  'LBP',
  'LKR',
  'LRD',
  'LSL',
  'LTL',
  'LVL',
  'LYD',
  'MAD',
  'MDL',
  'MGA',
  'MKD',
  'MMK',
  'MNT',
  'MOP',
  'MRO',
  'MUR',
  'MVR',
  'MWK',
  'MXN',
  'MXV',
  'MYR',
  'MZN',
  'NAD',
  'NGN',
  'NIO',
  'NOK',
  'NPR',
  'NZD',
  'OMR',
  'PAB',
  'PEN',
  'PGK',
  'PHP',
  'PKR',
  'PLN',
  'PYG',
  'QAR',
  'RON',
  'RSD',
  'RUB',
  'RWF',
  'SAR',
  'SBD',
  'SCR',
  'SDG',
  'SEK',
  'SGD',
  'SHP',
  'SLL',
  'SOS',
  'SRD',
  'SSP',
  'STD',
  'SYP',
  'SZL',
  'THB',
  'TJS',
  'TMT',
  'TND',
  'TOP',
  'TRY',
  'TTD',
  'TWD',
  'TZS',
  'UAH',
  'UGX',
  'USD',
  'USN',
  'USS',
  'UYI',
  'UYU',
  'UZS',
  'VEF',
  'VND',
  'VUV',
  'WST',
  'XAF',
  'XAG',
  'XAU',
  'XBA',
  'XBB',
  'XBC',
  'XBD',
  'XCD',
  'XDR',
  'XFU',
  'XOF',
  'XPD',
  'XPF',
  'XPT',
  'XTS',
  'XXX',
  'YER',
  'ZAR',
  'ZMW',
];

export const currencySymbols = {
  USD: {
    symbol: '$',
    name: 'US Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'USD',
    name_plural: 'US dollars',
  },
  CAD: {
    symbol: 'CA$',
    name: 'Canadian Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'CAD',
    name_plural: 'Canadian dollars',
  },
  EUR: {
    symbol: '€',
    name: 'Euro',
    symbol_native: '€',
    decimal_digits: 2,
    rounding: 0,
    code: 'EUR',
    name_plural: 'euros',
  },
  AED: {
    symbol: 'AED',
    name: 'United Arab Emirates Dirham',
    symbol_native: 'د.إ.',
    decimal_digits: 2,
    rounding: 0,
    code: 'AED',
    name_plural: 'UAE dirhams',
  },
  AFN: {
    symbol: 'Af',
    name: 'Afghan Afghani',
    symbol_native: '؋',
    decimal_digits: 0,
    rounding: 0,
    code: 'AFN',
    name_plural: 'Afghan Afghanis',
  },
  ALL: {
    symbol: 'ALL',
    name: 'Albanian Lek',
    symbol_native: 'Lek',
    decimal_digits: 0,
    rounding: 0,
    code: 'ALL',
    name_plural: 'Albanian lekë',
  },
  AMD: {
    symbol: 'AMD',
    name: 'Armenian Dram',
    symbol_native: 'դր.',
    decimal_digits: 0,
    rounding: 0,
    code: 'AMD',
    name_plural: 'Armenian drams',
  },
  ARS: {
    symbol: 'AR$',
    name: 'Argentine Peso',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'ARS',
    name_plural: 'Argentine pesos',
  },
  AUD: {
    symbol: 'AU$',
    name: 'Australian Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'AUD',
    name_plural: 'Australian dollars',
  },
  AZN: {
    symbol: 'man.',
    name: 'Azerbaijani Manat',
    symbol_native: 'ман.',
    decimal_digits: 2,
    rounding: 0,
    code: 'AZN',
    name_plural: 'Azerbaijani manats',
  },
  BAM: {
    symbol: 'KM',
    name: 'Bosnia-Herzegovina Convertible Mark',
    symbol_native: 'KM',
    decimal_digits: 2,
    rounding: 0,
    code: 'BAM',
    name_plural: 'Bosnia-Herzegovina convertible marks',
  },
  BDT: {
    symbol: '৳',
    name: 'Bangladeshi Taka',
    symbol_native: '৳',
    decimal_digits: 2,
    rounding: 0,
    code: 'BDT',
    name_plural: 'Bangladeshi takas',
  },
  BGN: {
    symbol: 'лв',
    name: 'Bulgarian Lev',
    symbol_native: 'лв',
    decimal_digits: 2,
    rounding: 0,
    code: 'BGN',
    name_plural: 'Bulgarian leva',
  },
  BHD: {
    symbol: 'BD',
    name: 'Bahraini Dinar',
    symbol_native: 'د.ب.‏',
    decimal_digits: 3,
    rounding: 0,
    code: 'BHD',
    name_plural: 'Bahraini dinars',
  },
  BIF: {
    symbol: 'FBu',
    name: 'Burundian Franc',
    symbol_native: 'FBu',
    decimal_digits: 0,
    rounding: 0,
    code: 'BIF',
    name_plural: 'Burundian francs',
  },
  BND: {
    symbol: 'BN$',
    name: 'Brunei Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'BND',
    name_plural: 'Brunei dollars',
  },
  BOB: {
    symbol: 'Bs',
    name: 'Bolivian Boliviano',
    symbol_native: 'Bs',
    decimal_digits: 2,
    rounding: 0,
    code: 'BOB',
    name_plural: 'Bolivian bolivianos',
  },
  BRL: {
    symbol: 'R$',
    name: 'Brazilian Real',
    symbol_native: 'R$',
    decimal_digits: 2,
    rounding: 0,
    code: 'BRL',
    name_plural: 'Brazilian reals',
  },
  BWP: {
    symbol: 'BWP',
    name: 'Botswanan Pula',
    symbol_native: 'P',
    decimal_digits: 2,
    rounding: 0,
    code: 'BWP',
    name_plural: 'Botswanan pulas',
  },
  BYN: {
    symbol: 'Br',
    name: 'Belarusian Ruble',
    symbol_native: 'руб.',
    decimal_digits: 2,
    rounding: 0,
    code: 'BYN',
    name_plural: 'Belarusian rubles',
  },
  BZD: {
    symbol: 'BZ$',
    name: 'Belize Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'BZD',
    name_plural: 'Belize dollars',
  },
  CDF: {
    symbol: 'CDF',
    name: 'Congolese Franc',
    symbol_native: 'FrCD',
    decimal_digits: 2,
    rounding: 0,
    code: 'CDF',
    name_plural: 'Congolese francs',
  },
  CHF: {
    symbol: 'CHF',
    name: 'Swiss Franc',
    symbol_native: 'CHF',
    decimal_digits: 2,
    rounding: 0.05,
    code: 'CHF',
    name_plural: 'Swiss francs',
  },
  CLP: {
    symbol: 'CL$',
    name: 'Chilean Peso',
    symbol_native: '$',
    decimal_digits: 0,
    rounding: 0,
    code: 'CLP',
    name_plural: 'Chilean pesos',
  },
  CNY: {
    symbol: 'CN¥',
    name: 'Chinese Yuan',
    symbol_native: 'CN¥',
    decimal_digits: 2,
    rounding: 0,
    code: 'CNY',
    name_plural: 'Chinese yuan',
  },
  COP: {
    symbol: 'CO$',
    name: 'Colombian Peso',
    symbol_native: '$',
    decimal_digits: 0,
    rounding: 0,
    code: 'COP',
    name_plural: 'Colombian pesos',
  },
  CRC: {
    symbol: '₡',
    name: 'Costa Rican Colón',
    symbol_native: '₡',
    decimal_digits: 0,
    rounding: 0,
    code: 'CRC',
    name_plural: 'Costa Rican colóns',
  },
  CVE: {
    symbol: 'CV$',
    name: 'Cape Verdean Escudo',
    symbol_native: 'CV$',
    decimal_digits: 2,
    rounding: 0,
    code: 'CVE',
    name_plural: 'Cape Verdean escudos',
  },
  CZK: {
    symbol: 'Kč',
    name: 'Czech Republic Koruna',
    symbol_native: 'Kč',
    decimal_digits: 2,
    rounding: 0,
    code: 'CZK',
    name_plural: 'Czech Republic korunas',
  },
  DJF: {
    symbol: 'Fdj',
    name: 'Djiboutian Franc',
    symbol_native: 'Fdj',
    decimal_digits: 0,
    rounding: 0,
    code: 'DJF',
    name_plural: 'Djiboutian francs',
  },
  DKK: {
    symbol: 'Dkr',
    name: 'Danish Krone',
    symbol_native: 'kr',
    decimal_digits: 2,
    rounding: 0,
    code: 'DKK',
    name_plural: 'Danish kroner',
  },
  DOP: {
    symbol: 'RD$',
    name: 'Dominican Peso',
    symbol_native: 'RD$',
    decimal_digits: 2,
    rounding: 0,
    code: 'DOP',
    name_plural: 'Dominican pesos',
  },
  DZD: {
    symbol: 'DA',
    name: 'Algerian Dinar',
    symbol_native: 'د.ج.‏',
    decimal_digits: 2,
    rounding: 0,
    code: 'DZD',
    name_plural: 'Algerian dinars',
  },
  EEK: {
    symbol: 'Ekr',
    name: 'Estonian Kroon',
    symbol_native: 'kr',
    decimal_digits: 2,
    rounding: 0,
    code: 'EEK',
    name_plural: 'Estonian kroons',
  },
  EGP: {
    symbol: 'EGP',
    name: 'Egyptian Pound',
    symbol_native: 'ج.م.‏',
    decimal_digits: 2,
    rounding: 0,
    code: 'EGP',
    name_plural: 'Egyptian pounds',
  },
  ERN: {
    symbol: 'Nfk',
    name: 'Eritrean Nakfa',
    symbol_native: 'Nfk',
    decimal_digits: 2,
    rounding: 0,
    code: 'ERN',
    name_plural: 'Eritrean nakfas',
  },
  ETB: {
    symbol: 'Br',
    name: 'Ethiopian Birr',
    symbol_native: 'Br',
    decimal_digits: 2,
    rounding: 0,
    code: 'ETB',
    name_plural: 'Ethiopian birrs',
  },
  GBP: {
    symbol: '£',
    name: 'British Pound Sterling',
    symbol_native: '£',
    decimal_digits: 2,
    rounding: 0,
    code: 'GBP',
    name_plural: 'British pounds sterling',
  },
  GEL: {
    symbol: 'GEL',
    name: 'Georgian Lari',
    symbol_native: 'GEL',
    decimal_digits: 2,
    rounding: 0,
    code: 'GEL',
    name_plural: 'Georgian laris',
  },
  GHS: {
    symbol: 'GH₵',
    name: 'Ghanaian Cedi',
    symbol_native: 'GH₵',
    decimal_digits: 2,
    rounding: 0,
    code: 'GHS',
    name_plural: 'Ghanaian cedis',
  },
  GNF: {
    symbol: 'FG',
    name: 'Guinean Franc',
    symbol_native: 'FG',
    decimal_digits: 0,
    rounding: 0,
    code: 'GNF',
    name_plural: 'Guinean francs',
  },
  GTQ: {
    symbol: 'GTQ',
    name: 'Guatemalan Quetzal',
    symbol_native: 'Q',
    decimal_digits: 2,
    rounding: 0,
    code: 'GTQ',
    name_plural: 'Guatemalan quetzals',
  },
  HKD: {
    symbol: 'HK$',
    name: 'Hong Kong Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'HKD',
    name_plural: 'Hong Kong dollars',
  },
  HNL: {
    symbol: 'HNL',
    name: 'Honduran Lempira',
    symbol_native: 'L',
    decimal_digits: 2,
    rounding: 0,
    code: 'HNL',
    name_plural: 'Honduran lempiras',
  },
  HRK: {
    symbol: 'kn',
    name: 'Croatian Kuna',
    symbol_native: 'kn',
    decimal_digits: 2,
    rounding: 0,
    code: 'HRK',
    name_plural: 'Croatian kunas',
  },
  HUF: {
    symbol: 'Ft',
    name: 'Hungarian Forint',
    symbol_native: 'Ft',
    decimal_digits: 0,
    rounding: 0,
    code: 'HUF',
    name_plural: 'Hungarian forints',
  },
  IDR: {
    symbol: 'Rp',
    name: 'Indonesian Rupiah',
    symbol_native: 'Rp',
    decimal_digits: 0,
    rounding: 0,
    code: 'IDR',
    name_plural: 'Indonesian rupiahs',
  },
  ILS: {
    symbol: '₪',
    name: 'Israeli New Sheqel',
    symbol_native: '₪',
    decimal_digits: 2,
    rounding: 0,
    code: 'ILS',
    name_plural: 'Israeli new sheqels',
  },
  INR: {
    symbol: '₹',
    name: 'Indian Rupee',
    symbol_native: '₹',
    decimal_digits: 2,
    rounding: 0,
    code: 'INR',
    name_plural: 'Indian rupees',
  },
  IQD: {
    symbol: 'IQD',
    name: 'Iraqi Dinar',
    symbol_native: 'د.ع.‏',
    decimal_digits: 0,
    rounding: 0,
    code: 'IQD',
    name_plural: 'Iraqi dinars',
  },
  IRR: {
    symbol: 'IRR',
    name: 'Iranian Rial',
    symbol_native: '﷼',
    decimal_digits: 0,
    rounding: 0,
    code: 'IRR',
    name_plural: 'Iranian rials',
  },
  ISK: {
    symbol: 'Ikr',
    name: 'Icelandic Króna',
    symbol_native: 'kr',
    decimal_digits: 0,
    rounding: 0,
    code: 'ISK',
    name_plural: 'Icelandic krónur',
  },
  JMD: {
    symbol: 'J$',
    name: 'Jamaican Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'JMD',
    name_plural: 'Jamaican dollars',
  },
  JOD: {
    symbol: 'JD',
    name: 'Jordanian Dinar',
    symbol_native: 'د.أ.‏',
    decimal_digits: 3,
    rounding: 0,
    code: 'JOD',
    name_plural: 'Jordanian dinars',
  },
  JPY: {
    symbol: '¥',
    name: 'Japanese Yen',
    symbol_native: '￥',
    decimal_digits: 0,
    rounding: 0,
    code: 'JPY',
    name_plural: 'Japanese yen',
  },
  KES: {
    symbol: 'Ksh',
    name: 'Kenyan Shilling',
    symbol_native: 'Ksh',
    decimal_digits: 2,
    rounding: 0,
    code: 'KES',
    name_plural: 'Kenyan shillings',
  },
  KHR: {
    symbol: '៛',
    name: 'Cambodian Riel',
    symbol_native: '៛',
    decimal_digits: 2,
    rounding: 0,
    code: 'KHR',
    name_plural: 'Cambodian riels',
  },
  KMF: {
    symbol: 'CF',
    name: 'Comorian Franc',
    symbol_native: 'FC',
    decimal_digits: 0,
    rounding: 0,
    code: 'KMF',
    name_plural: 'Comorian francs',
  },
  KRW: {
    symbol: '₩',
    name: 'South Korean Won',
    symbol_native: '₩',
    decimal_digits: 0,
    rounding: 0,
    code: 'KRW',
    name_plural: 'South Korean won',
  },
  KWD: {
    symbol: 'KD',
    name: 'Kuwaiti Dinar',
    symbol_native: 'د.ك.‏',
    decimal_digits: 3,
    rounding: 0,
    code: 'KWD',
    name_plural: 'Kuwaiti dinars',
  },
  KZT: {
    symbol: '₸',
    name: 'Kazakhstani Tenge',
    symbol_native: '₸',
    decimal_digits: 2,
    rounding: 0,
    code: 'KZT',
    name_plural: 'Kazakhstani tenges',
  },
  LBP: {
    symbol: 'LB£',
    name: 'Lebanese Pound',
    symbol_native: 'ل.ل.‏',
    decimal_digits: 0,
    rounding: 0,
    code: 'LBP',
    name_plural: 'Lebanese pounds',
  },
  LKR: {
    symbol: 'රු',
    name: 'Sri Lankan Rupee',
    symbol_native: 'රු',
    decimal_digits: 2,
    rounding: 0,
    code: 'LKR',
    name_plural: 'Sri Lankan rupees',
  },
  LTL: {
    symbol: 'Lt',
    name: 'Lithuanian Litas',
    symbol_native: 'Lt',
    decimal_digits: 2,
    rounding: 0,
    code: 'LTL',
    name_plural: 'Lithuanian litai',
  },
  LVL: {
    symbol: 'Ls',
    name: 'Latvian Lats',
    symbol_native: 'Ls',
    decimal_digits: 2,
    rounding: 0,
    code: 'LVL',
    name_plural: 'Latvian lati',
  },
  LYD: {
    symbol: 'LD',
    name: 'Libyan Dinar',
    symbol_native: 'د.ل.‏',
    decimal_digits: 3,
    rounding: 0,
    code: 'LYD',
    name_plural: 'Libyan dinars',
  },
  MAD: {
    symbol: 'MAD',
    name: 'Moroccan Dirham',
    symbol_native: 'د.م.‏',
    decimal_digits: 2,
    rounding: 0,
    code: 'MAD',
    name_plural: 'Moroccan dirhams',
  },
  MDL: {
    symbol: 'MDL',
    name: 'Moldovan Leu',
    symbol_native: 'MDL',
    decimal_digits: 2,
    rounding: 0,
    code: 'MDL',
    name_plural: 'Moldovan lei',
  },
  MGA: {
    symbol: 'MGA',
    name: 'Malagasy Ariary',
    symbol_native: 'MGA',
    decimal_digits: 0,
    rounding: 0,
    code: 'MGA',
    name_plural: 'Malagasy Ariaries',
  },
  MKD: {
    symbol: 'MKD',
    name: 'Macedonian Denar',
    symbol_native: 'MKD',
    decimal_digits: 2,
    rounding: 0,
    code: 'MKD',
    name_plural: 'Macedonian denari',
  },
  MMK: {
    symbol: 'MMK',
    name: 'Myanma Kyat',
    symbol_native: 'K',
    decimal_digits: 0,
    rounding: 0,
    code: 'MMK',
    name_plural: 'Myanma kyats',
  },
  MOP: {
    symbol: 'MOP$',
    name: 'Macanese Pataca',
    symbol_native: 'MOP$',
    decimal_digits: 2,
    rounding: 0,
    code: 'MOP',
    name_plural: 'Macanese patacas',
  },
  MUR: {
    symbol: 'MURs',
    name: 'Mauritian Rupee',
    symbol_native: 'MURs',
    decimal_digits: 0,
    rounding: 0,
    code: 'MUR',
    name_plural: 'Mauritian rupees',
  },
  MXN: {
    symbol: 'MX$',
    name: 'Mexican Peso',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'MXN',
    name_plural: 'Mexican pesos',
  },
  MYR: {
    symbol: 'RM',
    name: 'Malaysian Ringgit',
    symbol_native: 'RM',
    decimal_digits: 2,
    rounding: 0,
    code: 'MYR',
    name_plural: 'Malaysian ringgits',
  },
  MZN: {
    symbol: 'MTn',
    name: 'Mozambican Metical',
    symbol_native: 'MTn',
    decimal_digits: 2,
    rounding: 0,
    code: 'MZN',
    name_plural: 'Mozambican meticals',
  },
  NAD: {
    symbol: 'N$',
    name: 'Namibian Dollar',
    symbol_native: 'N$',
    decimal_digits: 2,
    rounding: 0,
    code: 'NAD',
    name_plural: 'Namibian dollars',
  },
  NGN: {
    symbol: '₦',
    name: 'Nigerian Naira',
    symbol_native: '₦',
    decimal_digits: 2,
    rounding: 0,
    code: 'NGN',
    name_plural: 'Nigerian nairas',
  },
  NIO: {
    symbol: 'C$',
    name: 'Nicaraguan Córdoba',
    symbol_native: 'C$',
    decimal_digits: 2,
    rounding: 0,
    code: 'NIO',
    name_plural: 'Nicaraguan córdobas',
  },
  NOK: {
    symbol: 'Nkr',
    name: 'Norwegian Krone',
    symbol_native: 'kr',
    decimal_digits: 2,
    rounding: 0,
    code: 'NOK',
    name_plural: 'Norwegian kroner',
  },
  NPR: {
    symbol: 'रू',
    name: 'Nepalese Rupee',
    symbol_native: 'रू',
    decimal_digits: 2,
    rounding: 0,
    code: 'NPR',
    name_plural: 'Nepalese rupees',
  },
  NZD: {
    symbol: 'NZ$',
    name: 'New Zealand Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'NZD',
    name_plural: 'New Zealand dollars',
  },
  OMR: {
    symbol: 'OMR',
    name: 'Omani Rial',
    symbol_native: 'ر.ع.‏',
    decimal_digits: 3,
    rounding: 0,
    code: 'OMR',
    name_plural: 'Omani rials',
  },
  PAB: {
    symbol: 'B/.',
    name: 'Panamanian Balboa',
    symbol_native: 'B/.',
    decimal_digits: 2,
    rounding: 0,
    code: 'PAB',
    name_plural: 'Panamanian balboas',
  },
  PEN: {
    symbol: 'S/.',
    name: 'Peruvian Nuevo Sol',
    symbol_native: 'S/.',
    decimal_digits: 2,
    rounding: 0,
    code: 'PEN',
    name_plural: 'Peruvian nuevos soles',
  },
  PHP: {
    symbol: '₱',
    name: 'Philippine Peso',
    symbol_native: '₱',
    decimal_digits: 2,
    rounding: 0,
    code: 'PHP',
    name_plural: 'Philippine pesos',
  },
  PKR: {
    symbol: '₨',
    name: 'Pakistani Rupee',
    symbol_native: '₨',
    decimal_digits: 0,
    rounding: 0,
    code: 'PKR',
    name_plural: 'Pakistani rupees',
  },
  PLN: {
    symbol: 'zł',
    name: 'Polish Zloty',
    symbol_native: 'zł',
    decimal_digits: 2,
    rounding: 0,
    code: 'PLN',
    name_plural: 'Polish zlotys',
  },
  PYG: {
    symbol: '₲',
    name: 'Paraguayan Guarani',
    symbol_native: '₲',
    decimal_digits: 0,
    rounding: 0,
    code: 'PYG',
    name_plural: 'Paraguayan guaranis',
  },
  QAR: {
    symbol: 'QR',
    name: 'Qatari Rial',
    symbol_native: 'ر.ق.‏',
    decimal_digits: 2,
    rounding: 0,
    code: 'QAR',
    name_plural: 'Qatari rials',
  },
  RON: {
    symbol: 'RON',
    name: 'Romanian Leu',
    symbol_native: 'RON',
    decimal_digits: 2,
    rounding: 0,
    code: 'RON',
    name_plural: 'Romanian lei',
  },
  RSD: {
    symbol: 'дин',
    name: 'Serbian Dinar',
    symbol_native: 'дин',
    decimal_digits: 0,
    rounding: 0,
    code: 'RSD',
    name_plural: 'Serbian dinars',
  },
  RUB: {
    symbol: '₽',
    name: 'Russian Ruble',
    symbol_native: '₽',
    decimal_digits: 2,
    rounding: 0,
    code: 'RUB',
    name_plural: 'Russian rubles',
  },
  RWF: {
    symbol: 'RWF',
    name: 'Rwandan Franc',
    symbol_native: 'FR',
    decimal_digits: 0,
    rounding: 0,
    code: 'RWF',
    name_plural: 'Rwandan francs',
  },
  SAR: {
    symbol: 'SR',
    name: 'Saudi Riyal',
    symbol_native: 'ر.س.‏',
    decimal_digits: 2,
    rounding: 0,
    code: 'SAR',
    name_plural: 'Saudi riyals',
  },
  SDG: {
    symbol: 'SDG',
    name: 'Sudanese Pound',
    symbol_native: 'SDG',
    decimal_digits: 2,
    rounding: 0,
    code: 'SDG',
    name_plural: 'Sudanese pounds',
  },
  SEK: {
    symbol: 'Skr',
    name: 'Swedish Krona',
    symbol_native: 'kr',
    decimal_digits: 2,
    rounding: 0,
    code: 'SEK',
    name_plural: 'Swedish kronor',
  },
  SGD: {
    symbol: 'S$',
    name: 'Singapore Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'SGD',
    name_plural: 'Singapore dollars',
  },
  SOS: {
    symbol: 'Ssh',
    name: 'Somali Shilling',
    symbol_native: 'Ssh',
    decimal_digits: 0,
    rounding: 0,
    code: 'SOS',
    name_plural: 'Somali shillings',
  },
  SYP: {
    symbol: 'SY£',
    name: 'Syrian Pound',
    symbol_native: 'ل.س.‏',
    decimal_digits: 0,
    rounding: 0,
    code: 'SYP',
    name_plural: 'Syrian pounds',
  },
  THB: {
    symbol: '฿',
    name: 'Thai Baht',
    symbol_native: '฿',
    decimal_digits: 2,
    rounding: 0,
    code: 'THB',
    name_plural: 'Thai baht',
  },
  TND: {
    symbol: 'DT',
    name: 'Tunisian Dinar',
    symbol_native: 'د.ت.‏',
    decimal_digits: 3,
    rounding: 0,
    code: 'TND',
    name_plural: 'Tunisian dinars',
  },
  TOP: {
    symbol: 'T$',
    name: "Tongan Pa'anga",
    symbol_native: 'T$',
    decimal_digits: 2,
    rounding: 0,
    code: 'TOP',
    name_plural: "Tongan pa'anga",
  },
  TRY: {
    symbol: 'TL',
    name: 'Turkish Lira',
    symbol_native: 'TL',
    decimal_digits: 2,
    rounding: 0,
    code: 'TRY',
    name_plural: 'Turkish Lira',
  },
  TTD: {
    symbol: 'TT$',
    name: 'Trinidad and Tobago Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'TTD',
    name_plural: 'Trinidad and Tobago dollars',
  },
  TWD: {
    symbol: 'NT$',
    name: 'New Taiwan Dollar',
    symbol_native: 'NT$',
    decimal_digits: 2,
    rounding: 0,
    code: 'TWD',
    name_plural: 'New Taiwan dollars',
  },
  TZS: {
    symbol: 'TSh',
    name: 'Tanzanian Shilling',
    symbol_native: 'TSh',
    decimal_digits: 0,
    rounding: 0,
    code: 'TZS',
    name_plural: 'Tanzanian shillings',
  },
  UAH: {
    symbol: '₴',
    name: 'Ukrainian Hryvnia',
    symbol_native: '₴',
    decimal_digits: 2,
    rounding: 0,
    code: 'UAH',
    name_plural: 'Ukrainian hryvnias',
  },
  UGX: {
    symbol: 'USh',
    name: 'Ugandan Shilling',
    symbol_native: 'USh',
    decimal_digits: 0,
    rounding: 0,
    code: 'UGX',
    name_plural: 'Ugandan shillings',
  },
  UYU: {
    symbol: '$U',
    name: 'Uruguayan Peso',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'UYU',
    name_plural: 'Uruguayan pesos',
  },
  UZS: {
    symbol: 'UZS',
    name: 'Uzbekistan Som',
    symbol_native: 'UZS',
    decimal_digits: 0,
    rounding: 0,
    code: 'UZS',
    name_plural: 'Uzbekistan som',
  },
  VEF: {
    symbol: 'Bs.F.',
    name: 'Venezuelan Bolívar',
    symbol_native: 'Bs.F.',
    decimal_digits: 2,
    rounding: 0,
    code: 'VEF',
    name_plural: 'Venezuelan bolívars',
  },
  VND: {
    symbol: '₫',
    name: 'Vietnamese Dong',
    symbol_native: '₫',
    decimal_digits: 0,
    rounding: 0,
    code: 'VND',
    name_plural: 'Vietnamese dong',
  },
  XAF: {
    symbol: 'FCFA',
    name: 'CFA Franc BEAC',
    symbol_native: 'FCFA',
    decimal_digits: 0,
    rounding: 0,
    code: 'XAF',
    name_plural: 'CFA francs BEAC',
  },
  XOF: {
    symbol: 'CFA',
    name: 'CFA Franc BCEAO',
    symbol_native: 'CFA',
    decimal_digits: 0,
    rounding: 0,
    code: 'XOF',
    name_plural: 'CFA francs BCEAO',
  },
  YER: {
    symbol: 'YR',
    name: 'Yemeni Rial',
    symbol_native: 'ر.ي.‏',
    decimal_digits: 0,
    rounding: 0,
    code: 'YER',
    name_plural: 'Yemeni rials',
  },
  ZAR: {
    symbol: 'R',
    name: 'South African Rand',
    symbol_native: 'R',
    decimal_digits: 2,
    rounding: 0,
    code: 'ZAR',
    name_plural: 'South African rand',
  },
  ZMK: {
    symbol: 'ZK',
    name: 'Zambian Kwacha',
    symbol_native: 'ZK',
    decimal_digits: 0,
    rounding: 0,
    code: 'ZMK',
    name_plural: 'Zambian kwachas',
  },
  ZWL: {
    symbol: 'ZWL$',
    name: 'Zimbabwean Dollar',
    symbol_native: 'ZWL$',
    decimal_digits: 0,
    rounding: 0,
    code: 'ZWL',
    name_plural: 'Zimbabwean Dollar',
  },
};

export const inconterms = [
  'EXW (Ex Works)',
  'FCA (Free Carrier)',
  'FAS (Free Alongside Ship)',
  'FOB (Free on Board)',
  'CFR (Cost and Freight) ',
  'CIF (Cost, Insurance and Freight)',
  'CPT (Carriage Paid to) ',
  'CIP (Carriage and Insurance Paid To)',
  'DAT (Delivered at Terminal)',
  'DAP (Delivered at Place)',
  'DDP (Delivered Duty Paid)',
];

export const getSelectValue = (optionArray, key, value) => optionArray.find((option) => option[key] === value);

export const convertSelectArray = (array) => {
  if (array && array.length > 0 && Array.isArray(array))
    return array.map((data) => {
      return { value: data, label: data };
    });
  return [];
};

export const findAllByKey = (obj, keyToFind) => {
  if (obj) {
    if (Array.isArray(keyToFind)) {
      let array = [];
      if (Array.isArray(keyToFind) && keyToFind.length > 0) {
        for (const element of keyToFind) {
          const value = Object.entries(obj).reduce((acc, [key, objValue]) => {
            if (key === element) return acc.concat(objValue);
            if (typeof objValue === 'object') return acc.concat(findAllByKey(objValue, keyToFind));
            return acc;
          }, []);
          array = concat(array, value);
        }
      }
      return array;
    }
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (key === keyToFind) return acc.concat(value);
      if (typeof value === 'object') return acc.concat(findAllByKey(value, keyToFind));
      return acc;
    }, []);
  }
};

export const getInitials = (string) => {
  const names = string.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

export const cloneData = (data) => JSON.parse(JSON.stringify(data));

export const createDynamicGroupedArray = async (colourway, size, length, setKeysArray, parentKeys) => {
  let array = [];
  array = await Promise.all(updateArray(colourway, size, length));
  if (setKeysArray) {
    setKeysArray.forEach((obj) => {
      array = array.map((data) => {
        data.expandData = _.map(data.expandData, function (element) {
          return _.extend({}, element, { [obj.key]: obj.value });
        });
        return data;
      });
    });
  }
  if (parentKeys) {
    parentKeys.forEach((obj) => {
      array = array.map((data) => {
        return _.extend({}, data, { [obj.key]: obj.value });
      });
    });
  }
  return array;
};

const updateArray = (colourway, size, length) => {
  if (colourway.length > 0) {
    return colourway.map((col) => {
      return {
        colourway: col?.colourWay || col,
        expandData: getCombinations(size, length, col),
        isExpanded: false,
        colourRef: col?.colourRef,
        sizeRange: size,
        lengthRange: length,
      };
    });
  }
  return size?.map((siz) => {
    return {
      colourway: null,
      expandData: getSizeCombinations(length, siz),
      isExpanded: false,
      colourRef: null,
      sizeRange: [siz],
      lengthRange: length,
    };
  });
};

export function getSizeCombinations(length, size) {
  const pairs = [];
  if (Array.isArray(length) && length.length > 0) {
    for (const valLength of length) {
      const formatData = {
        size: size.name,
        length: valLength,
        colourway: null,
      };
      pairs.push(formatData);
    }
  }
  if (pairs.length === 0) {
    const formatData = {
      size: size.name,
      length: null,
      colourway: null,
    };
    pairs.push(formatData);
  }
  return pairs;
}

export function getCombinations(size, length, colourway) {
  const pairs = [];
  if (Array.isArray(size) && size.length > 0 && Array.isArray(length) && length.length > 0) {
    for (const eleSize of size) {
      for (const eleLen of length) {
        const formatData = {
          size: eleSize.name,
          length: eleLen,
          colourway,
        };
        pairs.push(formatData);
      }
    }
  } else if (Array.isArray(size) && size.length > 0) {
    for (const valSize of size) {
      const formatData = {
        size: valSize.name,
        length: null,
        colourway,
      };
      pairs.push(formatData);
    }
  }
  return pairs;
}

export const convertCommaSeperateString = (array, key) => {
  let commaString = '';
  let stringLength = 0;
  if (Array.isArray(array)) {
    commaString = array.map((data) => data[key]).join(', ');
    stringLength = commaString.split(', ').length;
  }
  return { commaString, stringLength };
};

export const groupByNestedArray = (array, key, subKey) => {
  const groupedObject = {};
  if (Array.isArray(array)) {
    array.forEach((data) => {
      if (Array.isArray(data[key])) {
        data[key].forEach((category) => {
          // create a new category if it does not exist yet
          if (subKey) {
            if (!groupedObject[category[subKey]._id]) {
              groupedObject[category[subKey]._id] = [];
            }
            // add the data to the category[subKey]
            groupedObject[category[subKey]._id].push(data);
          } else {
            if (!groupedObject[category._id]) {
              groupedObject[category._id] = [];
            }
            // add the data to the category
            groupedObject[category._id].push(data);
          }
        });
      }
    });
  }
  return groupedObject;
};

export const checkIsNumber = (str) => {
  str = str?.toString() || '';
  if (str?.includes('-') || str?.includes('--')) return !str?.includes('-');
  return /^-?\d*\.?\d*$/?.test(str);
};

export const supplierChangeKeysArray = [
  'isChangedBySupplierPricePerUom',
  'isChangedBySupplierPricePerUnit',
  'isChangedBySupplierQuantityUom',
  'isChangedBySupplierTotalQuantityPerSizCol',
  'isChangedBySupplierTotalPrice',
  'isChangedBySupllierPricePerUnit',
  'isChangedBySupplierQuantityPerSizCol',
  'isChangedBySupplierColourway',
  'isChangedBySupplierTotalQuantityUom',
  'isChangedBySupplierTotalQuantity',
  'isChangedBySupplierTotalQuantityPerSizColLen',
  'isChangedBySupplierTitle',
  'isNewEntryBySupplier',
];

export const recursiveSearch = async (obj, searchKey, results = []) => {
  const r = results;
  obj &&
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      if (key === searchKey && typeof value !== 'object') {
        r.push(value);
      } else if (typeof value === 'object') {
        recursiveSearch(value, searchKey, r);
      }
    });
  return r;
};

export const checkCurrency = (str, data, onlySymbol) => {
  if (data || data >= 0 || onlySymbol) {
    if (currencySymbols[str]) return currencySymbols[str]?.symbol_native;
    return str;
  }
  return '';
};

export const checkArray = (Arr) => Array.isArray(Arr) && Arr?.length > 0;

export async function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      resolve(reader.result);
    };
    reader.onerror = function (error) {
      toasterMsg(error, 'danger');
      reject();
    };
  });
}

export const roundOff = (value, deciNum, isnan) => {
  const fixedDigit = deciNum || 2;
  if (isnan) {
    return Number.isNaN(value) || value === undefined ? 0 : round(value, fixedDigit);
  }
  return round(value, fixedDigit);
};

export const createOptions = (array, labelKey, valueKey, withoutItem, userList) => {
  let data = [];
  if (array?.length) {
    if (withoutItem) {
      data = array?.map((item) => {
        return {
          [valueKey]: item?.[valueKey] || item,
          [labelKey]: item?.[labelKey] || item,
        };
      });
    } else {
      data = array?.map((item) => {
        let obj = {};
        if (userList) {
          obj = {
            ...item,
            label: `${item?.firstName} ${item?.lastName}`,
            value: `${item?.userId ? item?.userId : item?._id}`,
          };
        } else {
          obj = {
            ...item,
            label: item?.[labelKey] || item,
            value: item?.[valueKey] || item,
          };
        }
        return obj;
      });
    }
  }
  return data;
};

export const checkObjectValues = (obj) => {
  let found = false;
  _.each(obj, (value) => {
    if (Array.isArray(value) && value.length > 0) {
      found = true;
    }
  });
  return found;
};

export const getYears = () => {
  const startYear = 2010;
  const endYear = 2070;
  return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
    const year = startYear + i;
    return { name: year.toString(), _id: year };
  });
};

export const YearsWithId = () => {
  const years = [];
  for (let i = 2010; i <= 2070; i++) {
    years.push({ name: i.toString(), _id: i.toString() });
  }
  return years;
};

export const getBackYears = () => {
  const dt = new Date();
  const years = [];
  for (let i = dt.getFullYear() - 1; i >= 2000; i--) {
    years.push({ name: i.toString(), _id: i });
  }
  return years;
};

export const roundingOff = (value, digitValue = 2) => {
  if (value === undefined || value === null || isNaN(value)) return '0';

  const decimals = digitValue;
  const factor = 10 ** decimals;

  // Proper floating-safe rounding
  const rounded = Math.round((Number(value) + Number.EPSILON) * factor) / factor;

  // Remove trailing decimals if integer
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(decimals);
};

function getFlooredFixed(v, d) {
  return (Math.floor(v * 10 ** d) / 10 ** d).toFixed(d);
}

export const methodShipment = [
  { name: 'Air', value: 'Air', label: 'Air' },
  { name: 'Sea', value: 'Sea', label: 'Sea' },
  { name: 'Truck', value: 'Truck', label: 'Truck' },
  { name: seaAndTruck, value: seaAndTruck, label: seaAndTruck },
];

export const targetControl = (e) => {
  const caret = e.target.selectionStart;
  const elements = e.target;
  window.requestAnimationFrame(() => {
    elements.selectionStart = caret;
    elements.selectionEnd = caret;
  });
};

export const checkDuplicate = (array, objKeys) => {
  array = array.map((data) => _.pick(data, objKeys));
  return _.map(array, function (o, i) {
    o.isDuplicate = false;
    const eq = _.find(array, function (e, ind) {
      if (i > ind) {
        return _.isEqual(e, o);
      }
    });
    if (eq) {
      o.isDuplicate = true;
      return o;
    }
    return o;
  });
};

export const productListLocationsArray = [
  {
    label: 'Product Core Information',
    name: 'Product_Core_Information',
    _id: 11,
  },
  {
    label: productInformation,
    name: 'Product_Information',
    _id: 1,
  },
  {
    label: 'Supplier Information',
    name: 'Supplier_Information',
    _id: 2,
  },
  { label: 'Design/Details', name: 'Design_Details', _id: 13 },
  {
    label: billOfMaterials,
    name: 'Bill_of_Materials',
    _id: 3,
  },
  {
    label: 'Specifications Information',
    name: 'Measurement_Information',
    _id: 4,
  },
  {
    label: 'Testing Information',
    name: 'Testing_Information',
    _id: 5,
  },
  {
    label: 'Care/User Instruction Layouts',
    name: 'Care/User_Info',
    _id: 6,
  },
  {
    label: 'Sample Information',
    name: 'Sample_Information',
    _id: 8,
  },
  {
    label: costingInformation,
    name: 'Costing_Information',
    _id: 9,
  },
  {
    label: 'Custom Modules',
    name: 'Custom_Modules',
    _id: 10,
  },
  {
    label: 'Product Identifier',
    name: 'Product_Identifier',
    _id: 12,
  },
];

export const productListSubLocationsArray = [
  {
    locationId: 1,
    subLocations: [
      {
        label: 'Sizes',
        name: 'size',
        type: 'string',
      },
      {
        label: gradingGroup,
        name: 'gradingGroup',
        type: 'string',
      },
      {
        label: 'Length',
        name: 'length',
        type: 'string',
      },
      {
        label: colourwayName,
        name: 'colourwayName',
        type: 'string',
      },
      {
        label: 'Colour Group',
        name: 'colourGroup',
        type: 'string',
      },
      {
        label: colourRef,
        name: 'colourRef',
        type: 'string',
      },
      {
        label: 'Colour Description',
        name: 'colourDesc',
        type: 'string',
      },
      {
        label: 'Colour Group Description',
        name: 'colourGroupDescription',
        type: 'string',
      },
    ],
  },
  {
    locationId: 2,
    subLocations: [
      {
        label: supplierCode,
        name: 'supplierCode',
        type: 'string',
      },
      {
        label: supplierName,
        name: 'supplierName',
        type: 'string',
      },
      {
        label: 'Currency',
        name: 'currency',
        type: 'string',
      },
      {
        label: factoryCode,
        name: 'factoryCode',
        type: 'string',
      },
      {
        label: factoryName,
        name: 'factoryName',
        type: 'string',
      },
      {
        label: 'Port of Lading',
        name: 'portOfLanding',
        type: 'string',
      },
      {
        label: 'MOQ',
        name: 'MOQ',
        type: 'number',
      },
      {
        label: 'Country of Origin',
        name: 'countryOfOrigin',
        type: 'string',
      },
      {
        label: 'Document Type',
        name: 'documentType',
        type: 'string',
      },
      {
        label: 'Document Name',
        name: 'documentName',
        type: 'string',
      },
      {
        label: 'Date Added',
        name: 'dateAdded',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Expiry Date',
        name: 'expiryDate',
        type: 'string',
      },
      {
        label: 'Assignee',
        name: 'assignee',
        type: 'string',
      },
    ],
  },
  {
    locationId: 3,
    subLocations: [
      {
        label: 'Code',
        name: 'code',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Component Type',
        name: 'componentType',
        type: 'string',
      },
      {
        label: 'Component Supplier',
        name: 'componentSupplier',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Weight',
        name: 'weight',
        type: 'number',
      },
      {
        label: 'Price Per Unit',
        name: 'pricePerUnit',
        type: 'number',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Composition',
        name: 'composition',
        type: 'string',
      },

      {
        label: 'Placement',
        name: 'placement',
        type: 'string',
      },
      {
        label: 'Finish',
        name: 'finish',
        type: 'string',
      },
      {
        label: additionalInformation,
        name: 'additionalInformation',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: colourRef,
        name: 'colourRef',
        type: 'string',
      },
      {
        label: 'Colour Supporting Doc',
        name: 'colourSupportingDoc',
        type: 'number',
      },
      {
        label: 'Colour Status',
        name: 'colourStatus',
        type: 'string',
      },
      {
        label: 'Colour Comments',
        name: 'comments',
        type: 'string',
      },
      {
        label: 'Quality Supporting Doc',
        name: 'qualitySupportingDoc',
        type: 'number',
      },
      {
        label: 'Quality Status',
        name: 'qualityStatus',
        type: 'string',
      },
      {
        label: 'Quality Comments',
        name: 'qualityComments',
        type: 'string',
      },
    ],
  },
  {
    locationId: 4,
    subLocations: [
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Weight',
        name: 'weight',
        type: 'number',
      },
      {
        label: 'Volume',
        name: 'volume',
        type: 'number',
      },
      {
        label: 'E-comm Spec',
        name: 'ecomSpec',
        type: 'number',
      },
      {
        label: 'No. of Supporting Docs',
        name: 'specificationSupportingDocuments',
        type: 'number',
      },
    ],
  },
  {
    locationId: 5,
    subLocations: [
      {
        label: 'Code',
        name: 'code',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Type',
        name: 'type',
        type: 'string',
      },
      {
        label: 'Minimum Pass Rate',
        name: 'minimumPassRate',
        type: 'string',
      },
      {
        label: 'Component',
        name: 'component',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Supplier',
        name: 'supplier',
        type: 'string',
      },
      {
        label: additionalInformation,
        name: 'additionalInformation',
        type: 'string',
      },
      {
        label: 'Result',
        name: 'result',
        type: 'string',
      },
      {
        label: 'Test Price',
        name: 'testPrice',
        type: 'number',
      },
      {
        label: 'Status Value',
        name: 'statusValue',
        type: 'string',
      },
      {
        label: 'Status Date',
        name: 'statusDate',
        type: 'string',
      },
      {
        label: 'Test Report Added Date',
        name: 'testReportAddedDate',
        type: 'string',
      },
      {
        label: 'Test Report Name',
        name: 'testReportName',
        type: 'string',
      },
      {
        label: 'Test Report Expiry Date',
        name: 'testReportExpiryDate',
        type: 'string',
      },
      {
        label: 'Comments',
        name: 'comments',
        type: 'string',
      },
    ],
  },
  {
    locationId: 6,
    subLocations: [
      {
        label: 'Title',
        name: 'title',
        type: 'string',
      },
      {
        label: 'Date Added',
        name: 'dateAdded',
        type: 'string',
      },
      {
        label: 'Status Value',
        name: 'statusValue',
        type: 'string',
      },
      {
        label: 'Status Comment',
        name: 'statusComment',
        type: 'string',
      },
      {
        label: 'Status Date',
        name: 'statusDate',
        type: 'string',
      },
    ],
  },
  {
    locationId: 8,
    subLocations: [
      {
        label: 'Ref',
        name: 'ref',
        type: 'string',
      },
      {
        label: 'Type',
        name: 'type',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Size/Size Range',
        name: 'sizeRange',
        type: 'string',
      },
      {
        label: 'Due Date',
        name: 'dueDate',
        type: 'date',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: markAsComplete,
        name: 'markAsComplete',
        type: 'string',
      },
      {
        label: 'Status Date First',
        name: 'statusDate',
        type: 'string',
      },
      {
        label: 'Status Date Latest',
        name: 'statusDate',
        type: 'string',
      },
      {
        label: 'Comments Added Date First',
        name: 'commentsAddedDateFirst',
        type: 'string',
      },
      {
        label: 'Comments Added Date Latest',
        name: 'commentsAddedDateLatest',
        type: 'string',
      },
    ],
  },
  {
    locationId: 9,
    subLocations: [
      {
        label: 'Reference',
        name: 'reference',
        type: 'string',
      },
      {
        label: 'Costing Type',
        name: 'costingType',
        type: 'string',
      },
      {
        label: 'Costing Template',
        name: 'costingTemplate',
        type: 'string',
      },
      {
        label: 'Sample Ref',
        name: 'sampleRef',
        type: 'string',
      },
      {
        label: 'Supplier',
        name: 'supplier',
        type: 'string',
      },
      {
        label: 'Supplier Currency',
        name: 'supplierCurrency',
        type: 'string',
      },
      {
        label: 'MOQ Needed',
        name: 'moqNeeded',
        type: 'string',
      },
      {
        label: 'Incoterm',
        name: 'incoterm',
        type: 'string',
      },
      {
        label: 'Shipment Method',
        name: 'shipmentMethod',
        type: 'string',
      },
      {
        label: 'Exchange Rate',
        name: 'exchangeRate',
        type: 'number',
      },
      {
        label: 'Landed Currency',
        name: 'landedCurrency',
        type: 'string',
      },
      {
        label: 'Target Cost',
        name: 'targetCost',
        type: 'number',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Price Per Unit (Incoterm)',
        name: 'pricePerUnitInco',
        type: 'number',
      },
      {
        label: 'Total (Incoterm)',
        name: 'totalInco',
        type: 'number',
      },
    ],
  },

  {
    locationId: 10,
    subLocations: [
      {
        label: 'PO Number',
        name: 'poNumber',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Length',
        name: 'length',
        type: 'number',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Creation Date',
        name: 'creationDate',
        type: 'string',
      },
      {
        label: 'Sent Date',
        name: 'sentDate',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Product Approve by Date (Seal by Date)',
        name: 'productApprovebyDate',
        type: 'string',
      },
      {
        label: 'Product Approved Date (Sealed Date)',
        name: 'productApprovedDate',
        type: 'string',
      },
      {
        label: HandoverCargoReadyDate,
        name: 'handoverCargoReadyDate',
        type: 'string',
      },

      {
        label: ActualHandoverCargoReadyDate,
        name: 'actualHandoverCargoReadyDate',
        type: 'string',
      },
      {
        label: ShipmentDate,
        name: 'shipmentDate',
        type: 'string',
      },
      {
        label: actualShipmentDate,
        name: 'actualShipmentDate',
        type: 'string',
      },
      {
        label: deliveryDate,
        name: 'deliveryDate',
        type: 'string',
      },
      {
        label: actualDeliveryDate,
        name: 'actualDeliveryDate',
        type: 'string',
      },
      {
        label: shipmentMethod,
        name: 'shipmentMethod',
        type: 'string',
      },
      {
        label: 'Incoterms',
        name: 'incoterms',
        type: 'string',
      },
      {
        label: 'Shipping & Handling Cost Included?',
        name: 'shippingHandlingCostIncluded',
        type: 'string',
      },
      {
        label: 'Shipping and Handling Cost',
        name: 'shippingandHandlingCost',
        type: 'number',
      },
      {
        label: 'Port of Landing',
        name: 'portofLanding',
        type: 'string',
      },
      {
        label: 'Number of Revisions',
        name: 'numberofRevisions',
        type: 'number',
      },
    ],
  },
  {
    locationId: 11,
    subLocations: [
      {
        label: 'Brand',
        name: 'brand',
        type: 'string',
      },
      {
        label: 'Brand - Code',
        name: 'brandCode',
        type: 'string',
      },
      {
        label: 'Sub Brand',
        name: 'subBrand',
        type: 'string',
      },
      {
        label: 'Sub Brand - Code',
        name: 'subBrandCode',
        type: 'string',
      },
      {
        label: productNumber,
        name: 'number',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Season',
        name: 'season',
        type: 'string',
      },
      {
        label: 'Season - Code',
        name: 'seasonCode',
        type: 'string',
      },
      {
        label: 'Month',
        name: 'month',
        type: 'string',
      },
      {
        label: 'Year',
        name: 'year',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Product Type',
        name: 'productType',
        type: 'string',
      },
      {
        label: 'Product Type - Code',
        name: 'productTypeCode',
        type: 'string',
      },
      {
        label: category1,
        name: 'cat1',
        type: 'string',
      },
      {
        label: category2,
        name: 'cat2',
        type: 'string',
      },
      {
        label: category3,
        name: 'cat3',
        type: 'string',
      },
      {
        label: category4,
        name: 'cat4',
        type: 'string',
      },
      {
        label: 'Category 1 - Code',
        name: 'cat1Code',
        type: 'string',
      },
      {
        label: 'Category 2 - Code',
        name: 'cat2Code',
        type: 'string',
      },
      {
        label: 'Category 3 - Code',
        name: 'cat3Code',
        type: 'string',
      },
      {
        label: 'Category 4 - Code',
        name: 'cat4Code',
        type: 'string',
      },
      {
        label: 'Size Range Name',
        name: 'sizeRangeName',
        type: 'string',
      },
      {
        label: 'Size Range Sizes',
        name: 'sizeRangeSizes',
        type: 'string',
      },
      {
        label: 'Supplier',
        name: 'supplier',
        type: 'string',
      },
      {
        label: colourwayName,
        name: 'colourwayName',
        type: 'string',
      },
      {
        label: 'No. of Colourways',
        name: 'noOfColourways',
        type: 'number',
      },
      {
        label: 'Factory',
        name: 'factory',
        type: 'string',
      },
      {
        label: 'Country Of Origin',
        name: 'productCountryOfOrigin',
        type: 'string',
      },
      {
        label: 'Original Submission',
        name: 'originalSubmission',
        type: 'string',
      },
      {
        label: 'Original Download',
        name: 'originalDownload',
        type: 'string',
      },
      {
        label: 'Most Recent Submission',
        name: 'mostRecentSubmission',
        type: 'string',
      },
      {
        label: 'Most Recent Download',
        name: 'mostRecentDownload',
        type: 'string',
      },
      {
        label: 'Licensed Product?',
        name: 'isLicensedProduct',
        type: 'string',
      },
      {
        label: 'Licensor',
        name: 'licensor',
        type: 'string',
      },
      {
        label: 'Licensor - Code',
        name: 'licensorCode',
        type: 'string',
      },
      {
        label: 'License',
        name: 'license',
        type: 'string',
      },
      {
        label: 'License - Code',
        name: 'licenseCode',
        type: 'string',
      },
      {
        label: 'Properties',
        name: 'likeness',
        type: 'string',
      },
      {
        label: 'Transport',
        name: 'transport',
        type: 'string',
      },
    ],
  },
];

export const calculationSymbols = [
  {
    name: '+',
    _id: '+',
    label: 'Plus',
  },
  {
    name: '-',
    _id: '-',
    label: 'Minus',
  },
  {
    name: '/',
    _id: '/',
    label: 'Division',
  },
  {
    name: '*',
    _id: '*',
    label: 'Multiplication',
  },
  {
    name: '%',
    _id: '%',
    label: 'Percentage',
  },
  {
    name: '&',
    _id: '&',
    label: 'Ampersand',
  },
  {
    name: 'Clear',
    _id: 'Clear',
    label: 'Clear',
  },
  {
    name: 'Undo',
    _id: 'Undo',
    label: 'Undo',
  },
];

export const inputTypes = [
  {
    type: 'Checkbox',
    name: 'Checkbox',
    key: 'Checkbox',
  },
  {
    type: DatePicker,
    name: 'Date_picker',
    key: 'Date_picker',
  },
  {
    type: 'HTML',
    name: 'HTML',
    key: 'HTML',
  },
  {
    type: singleSelectDropdown,
    name: 'Single_select_Dropdown',
    key: 'Dropdown',
  },
  {
    type: longTextArea,
    name: 'Long_Text',
    key: 'Text_area',
  },
  {
    type: multiSelectDropdown,
    name: 'Multi_select_Dropdown',
    key: 'Multi_select_Dropdown',
  },
  {
    type: numberDecimal,
    name: 'Number',
    key: 'Number',
    subType: 'decimal',
  },
  {
    type: numberInteger,
    name: 'Number',
    key: 'Number',
    subType: 'integer',
  },
  {
    type: numberPercentage,
    name: 'Number_percentage',
    key: 'Number_percentage',
  },
  {
    type: radioButton,
    name: 'Radio_button',
    key: 'Radio_button',
  },
  {
    type: 'Status',
    name: 'Status',
    key: 'Status',
  },
  {
    type: shortTextBox,
    name: 'Short_Text',
    key: 'Textbox',
  },
  {
    type: TimePicker,
    name: 'Time_picker',
    key: 'Time_picker',
  },
];

export const MANUAL_STRING_TYPE_KEYS = [
  'Checkbox',
  'Date_picker',
  'HTML',
  'Dropdown',
  'Label',
  'Text_area',
  'Multi_select_Dropdown',
  'Radio_button',
  'Status',
  'Textbox',
  'Time_picker',
];

export const MANUAL_NUMBER_TYPE_KEYS = ['Number', 'Number_percentage'];

export const isManualColumnNumberType = (key) => key != null && MANUAL_NUMBER_TYPE_KEYS.includes(key);

export const currency_symbols = {
  USD: '$', // US Dollar
  EUR: '€', // Euro
  CRC: '₡', // Costa Rican Colón
  GBP: '£', // British Pound Sterling
  ILS: '₪', // Israeli New Sheqel
  INR: '₹', // Indian Rupee
  JPY: '¥', // Japanese Yen
  KRW: '₩', // South Korean Won
  NGN: '₦', // Nigerian Naira
  PHP: '₱', // Philippine Peso
  PLN: 'zł', // Polish Zloty
  PYG: '₲', // Paraguayan Guarani
  THB: '฿', // Thai Baht
  UAH: '₴', // Ukrainian Hryvnia
  VND: '₫', // Vietnamese Dong
};

export const POColumnArray = [
  { label: 'Supplier', value: '1' },
  { label: 'Colourways', value: '2' },
  { label: 'Size', value: '3' },
  { label: 'Total Quantity', value: '4' },
  { label: 'Actual Total Quantity', value: '5' },
  { label: 'Grand Total', value: '6' },
  { label: 'Actual Grand Total', value: '7' },
  { label: 'Incoterms', value: '8' },
  { label: shipmentMethod, value: '9' },
  { label: 'Approve by Date', value: '10' },
  { label: 'Actual Approve by Date', value: '11' },
  { label: HandoverCargoReadyDate, value: '12' },
  { label: ActualHandoverCargoReadyDate, value: '13' },
  { label: ShipmentDate, value: '14' },
  { label: actualShipmentDate, value: '15' },
  { label: deliveryDate, value: '16' },
  { label: actualDeliveryDate, value: '17' },
  { label: 'Price Per Unit', value: '18' },
  { label: 'Discount', value: '19' },
  { label: 'Price After Discount', value: '20' },
];

// check open close bracket of formula
export function checkFormulaOpenCloseBracket(expr) {
  const holder = [];
  const openBrackets = ['(', '{', '['];
  const closedBrackets = [')', '}', ']'];
  for (const letter of expr) {
    // loop trought all letters of expr
    if (openBrackets.includes(letter)) {
      // if its oppening bracket
      holder.push(letter);
    } else if (closedBrackets.includes(letter)) {
      // if its closing
      const openPair = openBrackets[closedBrackets.indexOf(letter)]; // find its pair
      if (holder[holder.length - 1] === openPair) {
        // check if that pair is the last element in the array
        holder.splice(-1, 1); // if so, remove it
      } else {
        // if its not
        holder.push(letter);
        break; // exit loop
      }
    }
  }
  return holder.length === 0; // return true if length is 0, otherwise false
}
/**
 * Shows the floating enlarged preview of the hovered thumbnail.
 *
 * @param {object} e hover event whose target is the thumbnail <img>
 * @param {string} [variantClass] extra class appended to the preview element, letting a
 *   module restyle the preview (size, shadow) without altering the shared `.big-image` look
 */
export const showBigImage = (e, variantClass) => {
  const screenHeight = window.innerHeight;
  const maxClientY = screenHeight - 350;
  const img = document.createElement('img');
  const fullSrc = e.target.dataset?.fullSrc;
  const previewSrc = fullSrc || e.target.src;
  if (previewSrc) {
    img.onerror = () => img.remove();
    img.src = previewSrc;
    const rect = e.target.getBoundingClientRect();

    const topData = rect.top;
    const leftData = rect.left + 60;
    img.className = `big-image fixed-big-image${variantClass ? ` ${variantClass}` : ''}`;

    if (topData > maxClientY) {
      img.style.top = `${maxClientY}px`;
    } else {
      img.style.top = `${topData}px`;
    }
    img.style.left = `${leftData}px`;
    document.getElementById('root').appendChild(img);
  }
};
export const hideImage = () => {
  const eleImage = document.querySelector('.fixed-big-image');
  if (eleImage !== null) {
    // 👇️ this runs
    document.querySelectorAll('.fixed-big-image').forEach((element) => element.remove());
  }
};

// get converted currency value
export const convertedCurrValue = (value, curr1, curr2) => {
  let resValue = 0;
  curr1 = curr1 ? Number(curr1) : 1;
  curr2 = curr2 ? Number(curr2) : 1;
  if (curr1 > curr2 || curr2 > curr1) {
    resValue = value / curr1;
  } else if (curr1 === curr2) {
    resValue = value;
  }
  return resValue;
  // else if (curr2 > curr1) {
  //   resValue = value * curr1;
  // }
};

export const checValidMongoId = (id) => id?.length === 24 && !Number.isNaN(Number(`0x${id}`));

export const staticCostingColumnArray = () => {
  const topColumnArr = [
    // {
    //   title: "Colourway",
    //   isActive: true,
    //   showIcon: false,
    // },
    // {
    //   title: "Size",
    //   isActive: true,
    //   showIcon: false,
    // },
    // {
    //   title: "Length",
    //   isActive: true,
    //   showIcon: false,
    // },
    // {
    //   title: "Price Per Unit (Incoterm)",
    //   isActive: true,
    //   showIcon: false,
    // },
    {
      title: 'Price Per Unit (Converted)',
      isActive: true,
      showIcon: true,
      aliasName: 'pricePerUnitIncoConverted',
    },
    // {
    //   title: "Total (Incoterm)",
    //   isActive: true,
    //   showIcon: false,
    // },
    {
      title: 'Total (Converted)',
      isActive: true,
      showIcon: true,
      aliasName: 'totalIncoConverted',
    },
    // {
    //   title: "Brand License Royalties Fee",
    //   isActive: true,
    //   showIcon: true,
    // },
  ];

  const bottomColumnArr = [
    {
      title: 'Price Per Unit (Landed)',
      isActive: true,
      showIcon: true,
      aliasName: 'pricePerUnitLanded',
    },
    {
      title: 'Total (Landed)',
      isActive: true,
      showIcon: true,
      aliasName: 'totalLanded',
    },
    {
      title: 'Selling Price (Gross)',
      isActive: true,
      showIcon: true,
      isEditable: true,
      aliasName: 'sellingPriceGross',
    },
    {
      title: 'VAT',
      isActive: true,
      showIcon: true,
      isEditable: true,
      aliasName: 'vat',
    },
    {
      title: 'Customer Discount %',
      isActive: true,
      showIcon: true,
      isEditable: true,
      aliasName: 'customerDiscount',
    },
    {
      title: 'Selling Price (Net)',
      isActive: true,
      showIcon: true,
      aliasName: 'sellingPriceNet',
    },
    {
      title: 'Gross Profit Per Unit',
      isActive: true,
      showIcon: true,
      aliasName: 'grossProfitPerUnit',
    },

    {
      title: 'Markup Per Unit',
      isActive: true,
      showIcon: true,
      aliasName: 'markupPerUnit',
    },
    {
      title: 'Margin % Per Unit',
      isActive: true,
      showIcon: true,
      isEditable: true,
      aliasName: 'marginPerUnit',
    },

    {
      title: 'Estimated Retail Value',
      isActive: true,
      showIcon: true,
      isEditable: true,
      aliasName: 'estimatedRetailValue',
    },
    {
      title: 'Estimated Profit Per Unit',
      isActive: true,
      showIcon: true,
      aliasName: 'estimatedProfitPerUnit',
    },
    {
      title: 'Retail Margin % Per Unit',
      isActive: true,
      showIcon: true,
      isEditable: true,
      aliasName: 'retailMarginPerUnit',
    },
  ];
  return { topColumnArr, bottomColumnArr };
};

export const treadFile = async (file, isHeaderArray, skipSheets = []) => {
  const f = file;
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = (evt) => {
      // evt = on_file_select event
      /* Parse data */
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      /* Get worksheet - skip specified sheets */
      let wsname;
      for (const sheetName of wb.SheetNames) {
        // Convert both to lowercase for case-insensitive comparison
        const shouldSkip = skipSheets?.some((skipSheet) => skipSheet.toLowerCase() === sheetName.toLowerCase());
        if (!shouldSkip) {
          wsname = sheetName;
          break;
        }
      }

      // Fallback to first sheet if no valid sheet found
      if (!wsname) {
        wsname = wb.SheetNames[0];
      }

      const ws = wb.Sheets[wsname];
      /* Convert array of arrays */
      const data = XLSX.utils.sheet_to_csv(ws, { header: 1 });
      /* Update state */
      const result = convertToJson(data, isHeaderArray);
      resolve(result); // shows data in json format
    };
    reader.onerror = reject;
    reader.readAsBinaryString(f);
  });
};

const convertToJson = async (csv, isHeaderArray) => {
  const lines = csv.split('\n');
  const result = [];
  const headers = parseRow(lines[0]);

  for (let i = 1; i < lines.length; i++) {
    const obj = {};
    const currentline = parseRow(lines[i]);
    if (currentline) {
      for (let j = 0; j < headers.length; j++) {
        obj[headers[j]] = currentline[j] ? currentline[j] : '--';
      }
      result.push(obj);
    }
  }

  const keys = {
    Brand: 'brand',
    'Sub Brand': 'subBrand',
    Sizes: 'size',
    Length: 'length',
    Suffix: 'suffix',
    colourwayName: 'colourWay',
    'Product Id': 'productId',
    'Section Id': 'sectionId',
  };

  let res = JSON.parse(JSON.stringify(result)); // JSON
  res = res?.map(function (o) {
    return _.mapKeys(o, function (v, k) {
      return k in keys ? keys[k] : k;
    });
  });
  const headerObj = headers.reduce((a, v) => ({ ...a, [v]: v }), {});
  const stringArray = [];
  if (isHeaderArray) {
    for (const key in headerObj) {
      stringArray.push(key);
    }
  }
  return {
    data: res,
    headerKeys: isHeaderArray ? stringArray : headerObj,
  };
};
export const calculationBtnArrays = (
  checkFormulaHasStringType,
  counterState,
  checkFormulaHasPercentageAtlast,
  checkRootAtLast,
  disableNumbers,
  disableSpecialCharacter,
  squareAtLast,
  piAtLast,
  closingBreak,
) => [
  {
    name: '+',
    label: '+',
    type: 'symbol',
    className: 'calculation-btn',
    disabled: checkFormulaHasStringType || counterState || checkRootAtLast,
  },
  {
    name: '-',
    label: '-',
    type: 'symbol',
    className: 'calculation-btn',
    disabled: checkFormulaHasStringType || counterState || checkRootAtLast,
  },
  {
    name: '*',
    label: '*',
    type: 'symbol',
    className: 'calculation-btn',
    disabled: checkFormulaHasStringType || counterState || checkRootAtLast,
  },
  {
    name: '/',
    label: '/',
    type: 'symbol',
    className: 'calculation-btn',
    disabled: checkFormulaHasStringType || counterState || checkRootAtLast,
  },
  {
    name: '%',
    label: '%',
    type: 'symbol',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || counterState || checkRootAtLast || disableNumbers,
  },
  {
    name: '(',
    label: '(',
    type: 'symbol',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableSpecialCharacter || squareAtLast || piAtLast,
  },
  {
    name: ')',
    label: ')',
    type: 'symbol',
    className: 'calculation-btn-16',
    disabled: closingBreak,
  },
  {
    name: '&',
    label: '&',
    type: 'symbol',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || counterState || !checkFormulaHasStringType || checkRootAtLast || disableNumbers,
  },
  {
    name: '1',
    label: '1',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '2',
    label: '2',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '3',
    label: '3',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '4',
    label: '4',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '5',
    label: '5',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '6',
    label: '6',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '7',
    label: '7',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '8',
    label: '8',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '9',
    label: '9',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '0',
    label: '0',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: '.',
    label: '.',
    type: 'Number',
    className: 'calculation-btn',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
  },
  {
    name: 'π',
    label: 'π',
    type: 'symbol',
    className: 'calculation-btn-16',
    specialCondition: true,
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableSpecialCharacter || disableNumbers,
  },
  {
    name: 'X²',
    label: '²',
    type: 'symbol',
    className: 'calculation-btn-16',
    specialCondition: true,
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || counterState || checkRootAtLast || squareAtLast,
  },
  {
    name: '√',
    label: '√',
    type: 'symbol',
    className: 'calculation-btn-16',
    specialCondition: true,
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers || disableSpecialCharacter,
  },
];

export const getBottomUpKeyName = () => {
  return {
    planSelling: {
      colourway: '',
      jan: false,
      feb: false,
      march: false,
      april: false,
      may: false,
      june: false,
      july: false,
      aug: false,
      sept: false,
      oct: false,
      nov: false,
      dec: false,
      totalPWS: 0,
    },
    fpRos: {
      colourway: '',
      value: 0,
    },
    stores: {
      colourway: '',
      value: 0,
    },
    fpSlsMix: {
      colourway: '',
      value: 0,
    },
    markDown: {
      colourway: '',
      value: 0,
    },
    terminal: {
      colourway: '',
      value: 0,
    },
    unitMarketPlace: {
      colourway: '',
      ratio: 0,
      value: 0,
    },
    unitOutlate: {
      colourway: '',
      ratio: 0,
      value: 0,
    },
    sizeRatio: {
      colourway: '',
      size: '',
      value: 0,
    },
    lengthRatio: {
      colourway: '',
      size: '',
      length: '',
      value: 0,
    },
  };
};

export const checkItsValidNodejsId = (str) => {
  const checkForHexRegExp = /^[0-9a-fA-F]{24}$/;
  return checkForHexRegExp.test(str);
};

export const checkRangePlanFileUploadcol = () => {
  return {
    productId: 'productId',
    brand: 'brand',
    productNumber,
    Description: 'Description',
    Season: 'Season',
    Month: 'Month',
    Year: 'Year',
    Status: 'status',
    'Product Type': 'Product Type',
    category1,
    category2,
    category3,
    category4,
    suffix: 'suffix',
    gradingGroup,
    size: 'size',
    length: 'length',
    'Colour Name': 'Colour Name',
    'Colour Group': 'Colour Group',
    SKU: 'SKU',
    EAN: 'EAN',
  };
};

export const hasAllMatch = (obj1, obj2) => {
  if (!isEmpty(obj1) && !isEmpty(obj2)) {
    return Object.keys(obj1).every((key) => {
      return Object.prototype.hasOwnProperty.call(obj2, key);
    });
  }
  return false;
};

function parseRow(row) {
  let isInQuotes = false;
  const values = [];
  let val = '';
  for (let i = 0; i < row.length; i++) {
    switch (row[i]) {
      case ',':
        if (isInQuotes) {
          val += row[i];
        } else {
          values.push(val);
          val = '';
        }
        break;

      case '"':
        if (isInQuotes && i + 1 < row.length && row[i + 1] === '"') {
          val += '"';
          i++;
        } else {
          isInQuotes = !isInQuotes;
        }
        break;

      default:
        val += row[i];
        break;
    }
  }

  values.push(val);

  return values;
}

function matches(str) {
  try {
    new Function(str);
    return true;
  } catch (e) {
    return !(e instanceof SyntaxError);
  }
}

export const checkEvaluteOperatorVlaidation = (formulaArrys) => {
  const foundString = formulaArrys?.filter((val) => val?.subLocation?.type === 'string');
  const checkFormulaHasStringType = foundString?.length > 0;
  if (!checkFormulaHasStringType) {
    const str = createFormulaForCalculationmodal(formulaArrys);
    if (str) {
      return { status: matches(str), str };
    }
  } else {
    return { status: true, str: '' };
  }
};

export function createArrayofUniqueKeys(array, propertyName, subName) {
  if (subName) {
    return array
      .filter((e, i) => array.findIndex((a) => a[propertyName] && e[propertyName] && a[propertyName][subName] === e[propertyName][subName]) === i)
      .map((val) => val[propertyName]);
  }
  return array
    .filter((e, i) => array.findIndex((a) => a[propertyName] && e[propertyName] && a[propertyName] === e[propertyName]) === i)
    .map((val) => val[propertyName]);
}

export function setDeepNestedProperty(obj, path, value) {
  const pList = path.split('.');
  const key = pList.pop();
  const pointer = pList.reduce((accumulator, currentValue) => {
    if (accumulator[currentValue] === undefined) accumulator[currentValue] = {};
    return accumulator[currentValue];
  }, obj);
  pointer[key] = value;
  return obj;
}
export const checkIs4DigitDecimal = (value) => {
  const len = value?.toString()?.split('.')[1]?.length || 0;
  return len <= 4;
};
export const check4DigitDecimal = (value) => {
  if (value == null) return false;
  const strValue = value.toString();
  const [integerPart] = strValue.split('.');
  return integerPart.length <= 4;
};

export const checkIs3DigitDecimal = (value) => {
  const len = value?.toString()?.split('.')[1]?.length || 0;
  return len <= 3;
};

export const columTypeList = [
  { name: 'Source', _id: 'source' },
  { name: 'Manual', _id: 'manual' },
];

export const AttributeList = [
  { name: firstItem, _id: 'first_item' },
  { name: 'Last Item', _id: 'last_item' },
  { name: 'All Items', _id: 'all_item' },
  { name: 'Marked as Complete', _id: 'mark_as_complete' },
];

export const outComeList = [
  { name: dateAdded, _id: 'date_added' },
  { name: 'Percentage of Date Added', _id: 'percentage_of_date_added' },
  { name: percentageOfApproval, _id: 'percentage_of_approval' },
  { name: 'Percentage of Delivered', _id: 'percentage_of_delivered' },
  { name: 'Percentage of Completion', _id: 'completion_percentage' },
];

export const manualTypeList = [
  {
    type: 'Checkbox',
    name: 'Checkbox',
    key: 'Checkbox',
  },
  {
    type: DatePicker,
    name: 'Date_picker',
    key: 'Date_picker',
  },
  {
    type: 'HTML',
    name: 'HTML',
    key: 'HTML',
  },
  {
    type: singleSelectDropdown,
    name: 'Single_select_Dropdown',
    key: 'Dropdown',
  },
  {
    type: 'Label',
    name: 'Label',
    key: 'Label',
  },
  {
    type: longTextArea,
    name: 'Long_Text',
    key: 'Text_area',
  },
  {
    type: multiSelectDropdown,
    name: 'Multi_select_Dropdown',
    key: 'Multi_select_Dropdown',
  },
  {
    type: numberDecimal,
    name: 'Number',
    key: '  ',
    subType: 'decimal',
  },
  {
    type: numberInteger,
    name: 'Number',
    key: 'Number_Integer',
    subType: 'integer',
  },
  {
    type: numberPercentage,
    name: 'Number_percentage',
    key: 'Number_percentage',
  },
  {
    type: radioButton,
    name: 'Radio_button',
    key: 'Radio_button',
  },
  {
    type: 'Status',
    name: 'Status',
    key: 'Status',
  },
  {
    type: shortTextBox,
    name: 'Short_Text',
    key: 'Textbox',
  },
  {
    type: TimePicker,
    name: 'Time_picker',
    key: 'Time_picker',
  },
];

export const LocationList = [
  { name: productInformation, _id: 'product_information' },
  { name: 'Colour', _id: 'colourWay_artworks' },
  { name: billOfMaterials, _id: 'bill_of_materials' },
  { name: 'Testing', _id: 'testing' },
  {
    name: 'Care/User Instruction Layouts',
    _id: 'care_user_instruction_layouts',
  },
  { name: 'Samples', _id: 'samples' },
  // { name: "Purchase Orders", _id: "purchase_orders" },
  { name: 'Submit', _id: 'submit' },
  { name: 'Download', _id: 'download' },
  { name: 'Completion Indicators', _id: 'completion_indicators' },
  { name: 'Product Listings', _id: 'product_listings' },
  // { name: costingInformation, _id: "coasting_information" },
];

export const subLocationList = [
  { name: 'Sustainability', _id: 'sustainability' },
  { name: 'New/Continuity Type', _id: 'new_continuity_type' },
  { name: 'Partner Exclusive', _id: 'partner_exclusive' },
  { name: 'Print Family', _id: 'print_family' },
  { name: 'Price Brand', _id: 'price_brand' },
  { name: 'Number Options', _id: 'number_options' },
  { name: 'Target RRP', _id: 'target_rrp' },
];

export const typeList = [
  { name: 'Proto Sample', _id: 'proto_sample' },
  { name: 'Fit Sample', _id: 'fit_sample' },
  { name: 'Size Set Sample', _id: 'size_set_sample' },
  { name: 'Pre-Production Sample', _id: 'pre_production_sample' },
  { name: 'Salesman Sample', _id: 'salesman_sample' },
];

export const poTypeList = [
  { name: 'Approve By Date', _id: 'approve_by_date' },
  { name: 'Actual Approve By Date', _id: 'actual_approve_by_date' },
  { name: 'HandOver/Cargo Date', _id: 'handOver_cargo_date' }, // temp commented due to incomplete QRY
  { name: 'Actual HandOver/Cargo Date', _id: 'actual_handOver_cargo_date' }, // temp commented due to incomplete QRY
  { name: ShipmentDate, _id: 'shipment_date' },
  { name: actualShipmentDate, _id: 'actual_shipment_date' },
  { name: deliveryDate, _id: 'delivery_date' },
  { name: actualDeliveryDate, _id: 'actual_delivery_date' },
];

export const sectionList = [
  { name: 'Duty', _id: 'duty' },
  { name: 'Freight', _id: 'freight' },
  { name: 'International', _id: 'international' },
];

export const poAttributeList = [
  { name: 'EU Commodity Code', _id: 'eu_commodity_code' },
  { name: 'EU Duty Rate', _id: 'eu_duty_rate' },
  { name: 'China Duty Rate', _id: 'china_duty_rate' },
  { name: 'Japan Duty Rate', _id: 'japan_duty_rate' },
  { name: 'Japan HS Code', _id: 'japan_hs_code' },
  { name: 'Query', _id: 'query' },
  { name: 'Answer', _id: 'answer' },
];

export const complitionTypeList = [
  { name: 'Design Details', _id: 'design_details' },
  { name: 'BOM', _id: 'bill_of_materials' },
  { name: 'Measurment', _id: 'measurment' },
  { name: 'Testing', _id: 'testing' },
  { name: 'Care Labels', _id: 'care_user_instruction_layouts' },
];
export const customSectionPlacementArry = [
  { label: firstItem, name: 'earliest' },
  { label: 'Most Recent', name: 'mostRecent' },
];

export const ColumnValues = [
  { name: 'Greater than', _id: 'greater_than' },
  { name: 'Between', _id: 'between' },
  { name: 'Less than', _id: 'less_than' },
  { name: 'Equal', _id: 'equal' },
];

// match exact string in all length string
const escapeRegExpMatch = function (s) {
  return s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
};
export const isExactMatch = (str, match) => {
  return new RegExp(`\\b${escapeRegExpMatch(match)}\\b`).test(str);
};
// to remove dulicate items from array
export const removeDuplicateFromArray = (array) => {
  return [...new Set(array)];
};
// to convert array of array into single array
export const flatArray = (array) => {
  return array.flatMap((arrElement) => arrElement);
};
// truncate the number to fix decimal point ( it would round up the last decimal value)
export const truncateNumber = (number, decimalValues) => {
  // Check if both arguments are valid numbers
  if (typeof number !== 'number' || Number.isNaN(number) || typeof decimalValues !== 'number' || Number.isNaN(decimalValues)) {
    throw new Error('Both arguments should be valid numbers.');
  }
  const factor = 10 ** decimalValues; // Use the exponentiation operator
  return Math.round(number * factor) / factor; // Scale, round, and then scale back
};

// to clear all the cookies
export const clearCookies = () => {
  return document.cookie.split(';').forEach((c) => (document.cookie = c.replace(/^ +/, '').replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`)));
};

export function isIdValid(id) {
  // Regular expression that checks for hex value
  const checkForHexRegExp = /^[0-9a-fA-F]{24}$/;
  if (typeof id === 'string') {
    return id.length === 12 || (id.length === 24 && checkForHexRegExp.test(id));
  }
}

export function sortTable(list, field, currentField, order) {
  if (field === currentField) {
    order = !order;
  } else {
    currentField = field;
    order = false;
  }
  if (order) {
    list.sort(sortAsc(currentField));
  } else {
    list.sort(sortDesc(currentField));
  }
  return {
    list,
    currentField,
    order,
  };
}

function sortAsc(property) {
  return function (a, b) {
    if (a[property] < b[property]) {
      return -1;
    }
    if (a[property] > b[property]) {
      return 1;
    }
    return 0;
  };
}

function sortDesc(property) {
  return function (a, b) {
    if (a[property] > b[property]) {
      return -1;
    }
    if (a[property] < b[property]) {
      return 1;
    }
    return 0;
  };
}

export const toShowArrayToString = (item, title, shortLength, propertyName) => {
  if (item?.[title] && !propertyName) {
    const names = item[title]
      .map((e) => e?.name)
      .filter((ele) => ele)
      .toString(', ');
    return shortenStringWithTooltip(names, shortLength);
  }

  if (propertyName) {
    const properties = item[title]
      .map((e) => e)
      .filter((ele) => ele)
      .toString(', ');
    return shortenStringWithTooltip(properties, shortLength);
  }

  return ' - ';
};

export const createFormulaForCalculationmodal = (formulaArrys) => {
  let str = '';
  const foundString = formulaArrys?.filter((val) => val?.subLocation?.type === 'string');
  const checkFormulaHasStringType = foundString?.length > 0;
  if (!checkFormulaHasStringType) {
    formulaArrys.forEach((vl, index) => {
      if (vl?.subLocation?.type === 'symbol') {
        str = str.concat(vl?.subLocation?.name, '');
      } else if (vl?.subLocation?.type === 'number' && !checkIsNumber(vl?.subLocation?.name)) {
        str = str.concat(index + 1, '');
      } else if (vl?.subLocation?.type === 'Number') {
        str = str.concat(vl?.subLocation?.name, '');
      } else if (vl?.subLocation?.type === 'manualNumber') {
        str = str.concat(index + 1, '');
      }
    });
    return matches(str);
  }
  return true;
};

export const complitionPercentage = [
  { name: '0-25', _id: '0-25' },
  { name: '25-50', _id: '25-50' },
  { name: '50-75', _id: '50-75' },
  { name: '75-100', _id: '75-100' },
];

export const SetSequenceForHistoryModule = {
  Products: {
    'Product Number': 1,
    Description: 2,
    Brand: 3,
    Season: 4,
    Month: 5,
    Year: 6,
    'Category 1': 7,
    'Category 2': 8,
    'Category 3': 9,
    'Category 4': 10,
    'Grading Groups': 11,
    Length: 12,
    legLengths: 12,
    Size: 13,
    Colourway: 14,
    'Variable 2': 15,
    additionalVariables: 15,
    'Product Type': 16,
    Suppliers: 17,
    'Packing Method': 18,
    Status: 19,
    followers: 20,
    'Product Indentifier': 21,
    'Dynamic Column': 22,
  },
  'Design/Details': {
    name: 1,
    Image: 2,
    annotatedImageurl: 3,
  },
  BOM: {
    'Bom Type': 1,
    'Bom SubType': 2,
    'Bom Name': 3,
    'Bom Description': 4,
    'Bom Supplier': 5,
    'New Placment': 6,
    Size: 7,
    Weight: 8,
    Brand: 9,
    category1: 10,
    category2: 11,
    category3: 12,
    category4: 13,
    Finished: 14,
    Quantity: 15,
    Composition: 16,
    additionalInformation: 17,
    'Quantity Auto Approve': 18,
    'Colour Auto Approve': 19,
  },
  Specifications: {
    'Sample Size': 1,
    'CAD File': 2,
    'Weight/Volume': 3,
    'Pom Libraries': 4,
    'Spec Pom Libraries': 5,
    'CAD File, Point of Measure': 6,
    'Other, Point of Measure': 7,
    'Unit Type': 8,
  },
  Testing: {
    Code: 1,
    Description: 2,
    Type: 3,
    'Min Pass Rate': 4,
    'Test Report': 5,
    Supplier: 6,
    'Bom Component': 7,
    Colour: 8,
    additionalInformation: 9,
    Status: 10,
  },
  'User Instructions': {
    image: 1,
    Code: 2,
    Description: 3,
    'Care Type': 4,
    'Test Report': 5,
  },
  Samples: {
    'Sample Type': 1,
    'Sample Number': 2,
    'Due Date': 3,
    Colourways: 4,
    'Tracking Dates': 5,
    'Sample Status': 6,
  },
  'Product Costing': {
    Currency: 1,
    Incoterms: 2,
    shipmentMethod: 3,
    Status: 4,
    colourLibraries: 5,
    Length: 6,
    Size: 7,
  },
  'Purchase Orders': {
    'PO Number': 1,
    'Approve By Date': 2,
    colourLibraries: 3,
    Currency: 4,
    Incoterms: 5,
    'Ship Method': 6,
    'Actual Approve By Date': 7,
    ShipmentDate: 8,
    deliveryDate: 9,
    actualDeliveryDate: 10,
    Status: 11,
  },
  'Product Listing': {
    importedMarketPlace: 1,
    importedProducts: 2,
    colourWay: 3,
    length: 4,
    size: 5,
  },
};

export const PCPLocation = [
  {
    label: 'Product Core Information',
    name: 'Product_Core_Information',
    _id: 11,
  },
  {
    label: productInformation,
    name: 'Product_Information',
    _id: 1,
  },
  {
    label: 'Supplier Information',
    name: 'Supplier_Information',
    _id: 2,
  },
  {
    label: 'Design/Details',
    name: 'Design_Details',
    _id: 13,
  },
  {
    // tempCommented
    label: billOfMaterials,
    name: 'Bill_of_Materials',
    _id: 3,
  },
  {
    label: 'Specifications',
    name: 'Specifications',
    _id: 4,
  },
  {
    label: 'Testing',
    name: 'Testing',
    _id: 5,
  },
  {
    label: 'Care/User Document Information',
    name: 'Care/User_Document_Information',
    _id: 6,
  },
  {
    label: 'Samples',
    name: 'Samples',
    _id: 8,
  },
  // {
  //   label: costingInformation,
  //   name: "Costing_Information",
  //   _id: 9,
  // },
  // {
  //   // tempCommented
  //   label: "Purchase Order",
  //   name: "Purchase_Order",
  //   _id: 10,
  // },
  {
    label: 'Completion Indicator',
    name: 'Completion_Indicator',
    _id: 14,
  },
  {
    label: 'Custom Module',
    name: 'custom_module',
    _id: 7,
  },
];

export const SupplierLocation = [
  {
    label: 'Supplier Information',
    name: 'Supplier_Information',
    _id: 2,
  },
  {
    label: 'Supplier Templates',
    name: 'supplier_templates',
    _id: 7,
  },
];

/**
 * PCPSublocationList represents a structured configuration of various locations
 * and their respective sub-locations. Each sub-location contains metadata such as
 * label, name, and type, which describe the properties associated with that sub-location.
 */
export const PCPSublocationList = [
  {
    locationId: 1,
    subLocations: [
      {
        label: 'Sizes',
        name: 'size',
        type: 'string',
      },
      {
        label: gradingGroup,
        name: 'gradingGroup',
        type: 'string',
      },
      {
        label: 'Length',
        name: 'length',
        type: 'string',
      },
      {
        label: colourwayName,
        name: 'colourwayName',
        type: 'string',
      },
      {
        label: 'Colour Description',
        name: 'colourDesc',
        type: 'string',
      },
      {
        label: 'Colour Ref',
        name: 'colourwayRef',
        type: 'string',
      },
      {
        label: 'Colour Group',
        name: 'colourGroup',
        type: 'string',
      },
      {
        label: colourRef,
        name: 'colourRef',
        type: 'string',
      },
    ],
  },
  {
    locationId: 2,
    subLocations: [
      {
        label: supplierCode,
        name: 'supplierCode',
        type: 'string',
      },
      {
        label: supplierName,
        name: 'supplierName',
        type: 'string',
      },
      {
        label: 'Supplier Address',
        name: 'supplierAddress',
        type: 'string',
      },
      {
        label: factoryCode,
        name: 'factoryCode',
        type: 'string',
      },
      {
        label: factoryName,
        name: 'factoryName',
        type: 'string',
      },
      {
        label: 'Factory Address',
        name: 'factoryAddress',
        type: 'string',
      },
      {
        label: 'Port of Lading',
        name: 'portOfLanding',
        type: 'string',
      },
      {
        label: 'MOQ',
        name: 'MOQ',
        type: 'number',
      },
      {
        label: 'MCQ',
        name: 'MCQ',
        type: 'number',
      },
      {
        label: 'Country Code',
        name: 'countryCode',
        type: 'string',
      },
      {
        label: 'Country of Origin',
        name: 'countryOfOrigin',
        type: 'string',
      },
      {
        label: 'Document Type',
        name: 'documentType',
        type: 'string',
      },
      {
        label: 'Document Name',
        name: 'documentName',
        type: 'string',
      },
      {
        label: 'Date Added',
        name: 'dateAdded',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Expiry Date',
        name: 'expiryDate',
        type: 'string',
      },
      {
        label: 'Assignee',
        name: 'assignee',
        type: 'string',
      },
    ],
  },
  {
    locationId: 3,
    subLocations: [
      {
        label: 'Code',
        name: 'code',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Component Type',
        name: 'componentType',
        type: 'string',
      },
      {
        label: 'Component Supplier',
        name: 'componentSupplier',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Weight',
        name: 'weight',
        type: 'number',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Composition',
        name: 'composition',
        type: 'string',
      },

      {
        label: 'Placement',
        name: 'placement',
        type: 'string',
      },
      {
        label: 'Finish',
        name: 'finish',
        type: 'string',
      },
      {
        label: additionalInformation,
        name: 'additionalInformation',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: colourRef,
        name: 'colourRef',
        type: 'string',
      },
      {
        label: 'No. of Colour Supporting Doc',
        name: 'colourSupportingDoc',
        type: 'number',
      },
      {
        label: 'Colour Status',
        name: 'colourStatus',
        type: 'string',
      },
      {
        label: 'Colour Comments',
        name: 'comments',
        type: 'string',
      },
      {
        label: 'No. of Quality Supporting Doc',
        name: 'qualitySupportingDoc',
        type: 'number',
      },
      {
        label: 'Quality Status',
        name: 'qualityStatus',
        type: 'string',
      },
      {
        label: 'Quality Comments',
        name: 'qualityComments',
        type: 'string',
      },
      {
        label: 'Quality Supporting Doc Name',
        name: 'qualitySupportingDocName',
        type: 'string',
      },
      {
        label: 'Quality Supporting Doc Date Added',
        name: 'qualitySupportingDocDateAdded',
        type: 'string',
      },
      {
        label: 'Colour Supporting Doc Name',
        name: 'colourSupportingDocName',
        type: 'string',
      },
      {
        label: 'Colour Supporting Doc Date Added',
        name: 'colourSupportingDocDateAdded',
        type: 'string',
      },
    ],
  },
  {
    locationId: 4,
    subLocations: [
      {
        label: 'Specification Supporting Documents',
        name: 'specificationSupportingDocuments',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Weight',
        name: 'weight',
        type: 'number',
      },
      {
        label: 'Volume',
        name: 'volume',
        type: 'number',
      },
      {
        label: 'E-comm Spec',
        name: 'ecomSpec',
        type: 'number',
      },
    ],
  },
  {
    locationId: 8,
    subLocations: [
      {
        label: 'Ref',
        name: 'ref',
        type: 'string',
      },
      {
        label: 'Type',
        name: 'type',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Size/Size Range',
        name: 'sizeRange',
        type: 'string',
      },
      {
        label: 'Due Date',
        name: 'dueDate',
        type: 'date',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: markAsComplete,
        name: 'markAsComplete',
        type: 'string',
      },
      {
        label: 'Status Date First',
        name: 'statusDateFirst',
        type: 'date',
      },
      {
        label: 'Status Date Latest',
        name: 'statusDateLatest',
        type: 'date',
      },
      {
        label: 'Comments Added Date First',
        name: 'commentsAddedDateFirst',
        type: 'date',
      },
      {
        label: 'Comments Added Date Latest',
        name: 'commentsAddedDateLatest',
        type: 'date',
      },
    ],
  },
  {
    locationId: 9,
    subLocations: [
      {
        label: 'Reference',
        name: 'reference',
        type: 'string',
      },
      {
        label: 'Template',
        name: 'template',
        type: 'string',
      },
      {
        label: 'Costing Type',
        name: 'costingType',
        type: 'string',
      },
      {
        label: 'Cost Status',
        name: 'costStatus',
        type: 'string',
      },
      {
        label: 'Mode',
        name: 'mode',
        type: 'string',
      },

      {
        label: 'Landed Currency',
        name: 'landedCurrency',
        type: 'string',
      },
      {
        label: 'Incoterm',
        name: 'incoterm',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Price Per Unit Incoterm',
        name: 'pricePerUnitIncoterm',
        type: 'number',
      },
      {
        label: 'Exchange Rate',
        name: 'exchangeRate',
        type: 'number',
      },
      {
        label: 'Total (Incoterm)',
        name: 'incotermTotal',
        type: 'number',
      },
      {
        label: 'Price Per Unit (Landed)',
        name: 'pricePerUnitLanded',
        type: 'number',
      },
      {
        label: 'Total (Landed)',
        name: 'totalLanded',
        type: 'number',
      },
    ],
  },
  {
    locationId: 10,
    subLocations: [
      {
        label: 'PO Number',
        name: 'poNumber',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'poColourway',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Creation Date',
        name: 'creationDate',
        type: 'date',
      },
      {
        label: 'Sent Date',
        name: 'sentDate',
        type: 'date',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Product Approve by Date (Seal by Date)',
        name: 'productApprovebyDate',
        type: 'date',
      },
      {
        label: 'Product Approved Date (Sealed Date)',
        name: 'productApprovedDate',
        type: 'date',
      },
      {
        label: HandoverCargoReadyDate,
        name: 'handoverCargoReadyDate',
        type: 'date',
      },

      {
        label: ActualHandoverCargoReadyDate,
        name: 'actualHandoverCargoReadyDate',
        type: 'date',
      },
      {
        label: ShipmentDate,
        name: 'shipmentDate',
        type: 'date',
      },
      {
        label: actualShipmentDate,
        name: 'actualShipmentDate',
        type: 'date',
      },
      {
        label: deliveryDate,
        name: 'deliveryDate',
        type: 'date',
      },
      {
        label: actualDeliveryDate,
        name: 'actualDeliveryDate',
        type: 'date',
      },
      {
        label: shipmentMethod,
        name: 'shipmentMethod',
        type: 'string',
      },
      {
        label: 'Incoterms',
        name: 'incoterms',
        type: 'string',
      },
      {
        label: 'Shipping & Handling Cost Included?',
        name: 'shippingHandlingCostIncluded',
        type: 'string',
      },
      {
        label: 'Shipping and Handling Cost',
        name: 'shippingandHandlingCost',
        type: 'number',
      },
      {
        label: 'Port of Landing',
        name: 'portofLanding',
        type: 'string',
      },
      {
        label: 'Number of Revisions',
        name: 'numberofRevisions',
        type: 'number',
      },
    ],
  },
  {
    locationId: 11,
    subLocations: [
      {
        label: 'Brand',
        name: 'brand',
        type: 'string',
      },
      {
        label: 'Sub Brand',
        name: 'subBrand',
        type: 'string',
      },
      {
        label: productNumber,
        name: 'number',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Season',
        name: 'season',
        type: 'string',
      },
      {
        label: 'Month',
        name: 'month',
        type: 'string',
      },
      {
        label: 'Year',
        name: 'year',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Product Type',
        name: 'productType',
        type: 'string',
      },
      {
        label: 'Category 1',
        name: 'cat1',
        type: 'string',
      },
      {
        label: 'Category 2',
        name: 'cat2',
        type: 'string',
      },
      {
        label: 'Category 3',
        name: 'cat3',
        type: 'string',
      },
      {
        label: 'Category 4',
        name: 'cat4',
        type: 'string',
      },
      {
        label: 'Size Range',
        name: 'sizeRange',
        type: 'string',
      },
      {
        label: 'Supplier',
        name: 'supplier',
        type: 'string',
      },

      {
        label: colourwayName,
        name: 'colourwayName',
        type: 'string',
      },
      {
        label: 'No. of Colourways',
        name: 'noOfColourways',
        type: 'number',
      },
      {
        label: 'Factory',
        name: 'factory',
        type: 'string',
      },
      {
        label: 'Country Of Origin',
        name: 'productCountryOfOrigin',
        type: 'string',
      },
      {
        label: 'Original Supplier Submission',
        name: 'originalSubmission',
        type: 'date',
      },
      {
        label: 'Original Supplier Download',
        name: 'originalDownload',
        type: 'date',
      },
      {
        label: 'Most Recent Supplier Submission',
        name: 'mostRecentSubmission',
        type: 'date',
      },
      {
        label: 'Most Recent Supplier Download',
        name: 'mostRecentDownload',
        type: 'date',
      },
      {
        label: 'Licensor',
        name: 'licensor',
        type: 'string',
      },
      {
        label: 'Licensor - Code',
        name: 'licensorCode',
        type: 'string',
      },
      {
        label: 'License',
        name: 'license',
        type: 'string',
      },
      {
        label: 'Properties',
        name: 'likeness',
        type: 'string',
      },
      {
        label: 'Transport',
        name: 'transport',
        type: 'string',
      },
    ],
  },
  {
    locationId: 14,
    subLocations: [
      {
        label: 'Design/Details',
        name: 'Design_Details',
        type: '',
      },
      {
        label: billOfMaterials,
        name: 'Bill_of_Materials',
        type: '',
      },
      {
        label: 'Specifications',
        name: 'Specifications',
        type: '',
      },
      {
        label: 'Testing',
        name: 'Testing',
        type: '',
      },
      {
        label: 'Care/User Document Information',
        name: 'Care/User_Document_Information',
        type: '',
      },
      {
        label: 'Samples',
        name: 'Samples',
        type: '',
      },
    ],
  },
];

export const PIMSublocationList = [
  {
    locationId: 1,
    subLocations: [
      {
        label: 'Sizes',
        name: 'size',
        type: 'string',
      },
      {
        label: gradingGroup,
        name: 'gradingGroup',
        type: 'string',
      },
      {
        label: 'Length',
        name: 'length',
        type: 'string',
      },
      {
        label: colourwayName,
        name: 'colourwayName',
        type: 'string',
      },
      {
        label: 'Colour Description',
        name: 'colourDesc',
        type: 'string',
      },
      {
        label: 'Colour Ref',
        name: 'colourwayRef',
        type: 'string',
      },
      {
        label: 'Colour Group',
        name: 'colourGroup',
        type: 'string',
      },
      {
        label: 'colourRef',
        name: 'colourRef',
        type: 'string',
      },
    ],
  },
  {
    locationId: 2,
    subLocations: [
      {
        label: supplierCode,
        name: 'supplierCode',
        type: 'string',
      },
      {
        label: supplierName,
        name: 'supplierName',
        type: 'string',
      },
      {
        label: 'Supplier Address',
        name: 'supplierAddress',
        type: 'string',
      },
      {
        label: factoryCode,
        name: 'factoryCode',
        type: 'string',
      },
      {
        label: factoryName,
        name: 'factoryName',
        type: 'string',
      },
      {
        label: 'Factory Address',
        name: 'factoryAddress',
        type: 'string',
      },
      {
        label: 'Port of Lading',
        name: 'portOfLanding',
        type: 'string',
      },
      {
        label: 'MOQ',
        name: 'MOQ',
        type: 'number',
      },
      {
        label: 'MCQ',
        name: 'MCQ',
        type: 'number',
      },
      {
        label: 'Country Code',
        name: 'countryCode',
        type: 'string',
      },
      {
        label: 'Country of Origin',
        name: 'countryOfOrigin',
        type: 'string',
      },
      {
        label: 'Document Type',
        name: 'documentType',
        type: 'string',
      },
      {
        label: 'Document Name',
        name: 'documentName',
        type: 'string',
      },
      {
        label: 'Date Added',
        name: 'dateAdded',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Expiry Date',
        name: 'expiryDate',
        type: 'string',
      },
      {
        label: 'Assignee',
        name: 'assignee',
        type: 'string',
      },
    ],
  },
  {
    locationId: 3,
    subLocations: [
      {
        label: 'Code',
        name: 'code',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Component Type',
        name: 'componentType',
        type: 'string',
      },
      {
        label: 'Component Supplier',
        name: 'componentSupplier',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Weight',
        name: 'weight',
        type: 'number',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Composition',
        name: 'composition',
        type: 'string',
      },

      {
        label: 'Placement',
        name: 'placement',
        type: 'string',
      },
      {
        label: 'Finish',
        name: 'finish',
        type: 'string',
      },
      {
        label: additionalInformation,
        name: 'additionalInformation',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'colourRef',
        name: 'colourRef',
        type: 'string',
      },
      {
        label: 'No. of Colour Supporting Doc',
        name: 'colourSupportingDoc',
        type: 'number',
      },
      {
        label: 'Colour Status',
        name: 'colourStatus',
        type: 'string',
      },
      {
        label: 'Colour Comments',
        name: 'comments',
        type: 'string',
      },
      {
        label: 'No. of Quality Supporting Doc',
        name: 'qualitySupportingDoc',
        type: 'number',
      },
      {
        label: 'Quality Status',
        name: 'qualityStatus',
        type: 'string',
      },
      {
        label: 'Quality Comments',
        name: 'qualityComments',
        type: 'string',
      },
      {
        label: 'Quality Supporting Doc Name',
        name: 'qualitySupportingDocName',
        type: 'string',
      },
      {
        label: 'Quality Supporting Doc Date Added',
        name: 'qualitySupportingDocDateAdded',
        type: 'string',
      },
      {
        label: 'Colour Supporting Doc Name',
        name: 'colourSupportingDocName',
        type: 'string',
      },
      {
        label: 'Colour Supporting Doc Date Added',
        name: 'colourSupportingDocDateAdded',
        type: 'string',
      },
    ],
  },
  {
    locationId: 4,
    subLocations: [
      {
        label: 'Specification Supporting Documents',
        name: 'specificationSupportingDocuments',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Weight',
        name: 'weight',
        type: 'number',
      },
      {
        label: 'Volume',
        name: 'volume',
        type: 'number',
      },
      {
        label: 'E-comm Spec',
        name: 'ecomSpec',
      },
    ],
  },
  {
    locationId: 8,
    subLocations: [
      {
        label: 'Ref',
        name: 'ref',
        type: 'string',
      },
      {
        label: 'Type',
        name: 'type',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Size/Size Range',
        name: 'sizeRange',
        type: 'string',
      },
      {
        label: 'Due Date',
        name: 'dueDate',
        type: 'date',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Mark As Complete',
        name: 'markAsComplete',
        type: 'string',
      },
      {
        label: 'Status Date First',
        name: 'statusDateFirst',
        type: 'date',
      },
      {
        label: 'Status Date Latest',
        name: 'statusDateLatest',
        type: 'date',
      },
      {
        label: 'Comments Added Date First',
        name: 'commentsAddedDateFirst',
        type: 'date',
      },
      {
        label: 'Comments Added Date Latest',
        name: 'commentsAddedDateLatest',
        type: 'date',
      },
    ],
  },
  {
    locationId: 9,
    subLocations: [
      {
        label: 'Reference',
        name: 'reference',
        type: 'string',
      },
      {
        label: 'Template',
        name: 'template',
        type: 'string',
      },
      {
        label: 'Costing Type',
        name: 'costingType',
        type: 'string',
      },
      {
        label: 'Cost Status',
        name: 'costStatus',
        type: 'string',
      },
      {
        label: 'Mode',
        name: 'mode',
        type: 'string',
      },

      {
        label: 'Landed Currency',
        name: 'landedCurrency',
        type: 'string',
      },
      {
        label: 'Incoterm',
        name: 'incoterm',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Price Per Unit Incoterm',
        name: 'pricePerUnitIncoterm',
        type: 'number',
      },
      {
        label: 'Exchange Rate',
        name: 'exchangeRate',
        type: 'number',
      },
      {
        label: 'Total (Incoterm)',
        name: 'incotermTotal',
        type: 'number',
      },
      {
        label: 'Price Per Unit (Landed)',
        name: 'pricePerUnitLanded',
        type: 'number',
      },
      {
        label: 'Total (Landed)',
        name: 'totalLanded',
        type: 'number',
      },
    ],
  },
  {
    locationId: 10,
    subLocations: [
      {
        label: 'PO Number',
        name: 'poNumber',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'poColourway',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Creation Date',
        name: 'creationDate',
        type: 'date',
      },
      {
        label: 'Sent Date',
        name: 'sentDate',
        type: 'date',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Product Approve by Date (Seal by Date)',
        name: 'productApprovebyDate',
        type: 'date',
      },
      {
        label: 'Product Approved Date (Sealed Date)',
        name: 'productApprovedDate',
        type: 'date',
      },
      {
        label: HandoverCargoReadyDate,
        name: 'handoverCargoReadyDate',
        type: 'date',
      },

      {
        label: ActualHandoverCargoReadyDate,
        name: 'actualHandoverCargoReadyDate',
        type: 'date',
      },
      {
        label: ShipmentDate,
        name: 'shipmentDate',
        type: 'date',
      },
      {
        label: actualShipmentDate,
        name: 'actualShipmentDate',
        type: 'date',
      },
      {
        label: deliveryDate,
        name: 'deliveryDate',
        type: 'date',
      },
      {
        label: actualDeliveryDate,
        name: 'actualDeliveryDate',
        type: 'date',
      },
      {
        label: shipmentMethod,
        name: 'shipmentMethod',
        type: 'string',
      },
      {
        label: 'Incoterms',
        name: 'incoterms',
        type: 'string',
      },
      {
        label: 'Shipping & Handling Cost Included?',
        name: 'shippingHandlingCostIncluded',
        type: 'string',
      },
      {
        label: 'Shipping and Handling Cost',
        name: 'shippingandHandlingCost',
        type: 'number',
      },
      {
        label: 'Port of Landing',
        name: 'portofLanding',
        type: 'string',
      },
      {
        label: 'Number of Revisions',
        name: 'numberofRevisions',
        type: 'number',
      },
    ],
  },
  {
    locationId: 11,
    subLocations: [
      {
        label: 'Brand',
        name: 'brand',
        type: 'string',
      },
      {
        label: 'Product Number',
        name: 'number',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Season',
        name: 'season',
        type: 'string',
      },
      {
        label: 'Season - Code',
        name: 'seasonCode',
        type: 'string',
      },
      {
        label: 'Month',
        name: 'month',
        type: 'string',
      },
      {
        label: 'Year',
        name: 'year',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Product Type',
        name: 'productType',
        type: 'string',
      },
      {
        label: 'Product Type - Code',
        name: 'productTypeCode',
        type: 'string',
      },
      {
        label: category1,
        name: 'cat1',
        type: 'string',
      },
      {
        label: category2,
        name: 'cat2',
        type: 'string',
      },
      {
        label: category3,
        name: 'cat3',
        type: 'string',
      },
      {
        label: category4,
        name: 'cat4',
        type: 'string',
      },
      {
        label: 'Category 1 - Code',
        name: 'cat1Code',
        type: 'string',
      },
      {
        label: 'Category 2 - Code',
        name: 'cat2Code',
        type: 'string',
      },
      {
        label: 'Category 3 - Code',
        name: 'cat3Code',
        type: 'string',
      },
      {
        label: 'Category 4 - Code',
        name: 'cat4Code',
        type: 'string',
      },
      {
        label: 'Size Range',
        name: 'sizeRange',
        type: 'string',
      },
      {
        label: 'Supplier',
        name: 'supplier',
        type: 'string',
      },
      {
        label: 'Factory',
        name: 'factory',
        type: 'string',
      },
      {
        label: 'Country Of Origin',
        name: 'productCountryOfOrigin',
        type: 'string',
      },
      {
        label: colourwayName,
        name: 'colourwayName',
        type: 'string',
      },
      {
        label: 'No. of Colourways',
        name: 'noOfColourways',
        type: 'number',
      },
      {
        label: 'Original Submission',
        name: 'originalSubmission',
        type: 'string',
      },
      {
        label: 'Original Download',
        name: 'originalDownload',
        type: 'string',
      },
      {
        label: 'Most Recent Submission',
        name: 'mostRecentSubmission',
        type: 'string',
      },
      {
        label: 'Most Recent Download',
        name: 'mostRecentDownload',
        type: 'string',
      },
      {
        label: 'Licensor',
        name: 'licensor',
        type: 'string',
      },
      {
        label: 'Licensor - Code',
        name: 'licensorCode',
        type: 'string',
      },
      {
        label: 'License',
        name: 'license',
        type: 'string',
      },
      {
        label: 'Properties',
        name: 'likeness',
        type: 'string',
      },
      {
        label: 'Transport',
        name: 'transport',
        type: 'string',
      },
    ],
  },
  {
    locationId: 14,
    subLocations: [
      {
        label: 'Design/Details',
        name: 'Design_Details',
        type: '',
      },
      {
        label: billOfMaterials,
        name: 'Bill_of_Materials',
        type: '',
      },
      {
        label: 'Specifications',
        name: 'Specifications',
        type: '',
      },
      {
        label: 'Testing',
        name: 'Testing',
        type: '',
      },
      {
        label: 'Care/User Document Information',
        name: 'Care/User_Document_Information',
        type: '',
      },
      {
        label: 'Samples',
        name: 'Samples',
        type: '',
      },
      {
        label: costingInformation,
        name: 'Costing_Information',
        type: '',
      },
      {
        label: 'Purchase Order',
        name: 'Purchase_Order',
        type: '',
      },
    ],
  },
];

export const PIMDisplayFormat = [
  {
    label: 'Average Size Master',
    name: 'avergae_size_master',
  },
  {
    label: 'Sample Size Master',
    name: 'sample_size_master',
  },
  {
    label: 'Actual Size Master',
    name: 'actual_size_master',
  },
];

export const filterCustomAttributesDropdownAndTextType = (allTypeData) => {
  const allData = cloneDeep(allTypeData);
  let filteredData = [];
  let filteredDropdownData = allData?.filter((ele) => ele?.type === 'dropdown' && ele?.isLable === true);
  const filteredTextData = allData?.filter((ele) => ele?.type === 'text' && ele?.isLable === false);
  let filteredRadioData = allData?.filter((ele) => ele?.type === 'radio-button' && ele?.isLable === true);
  const filteredDateData = allData?.filter((ele) => ele?.type === 'date-picker');
  if (!isEmpty(filteredDropdownData)) {
    filteredDropdownData = filteredDropdownData?.map((item) => {
      const valuesForLabel = allData?.filter((ele) => item?._id === ele?.lableId);
      item.options = valuesForLabel;
      return item;
    });
  }
  if (!isEmpty(filteredRadioData)) {
    filteredRadioData = filteredRadioData?.map((item) => {
      const valuesForLabel = allData?.filter((ele) => item?._id === ele?.lableId);
      item.options = valuesForLabel;
      return item;
    });
  }
  if (isArray(filteredDropdownData) && isArray(filteredTextData)) {
    filteredData = filteredDropdownData.concat(filteredTextData);
  }
  if (isArray(filteredRadioData)) filteredData = filteredData.concat(filteredRadioData);
  if (isArray(filteredDateData)) filteredData = filteredData.concat(filteredDateData);
  return filteredData;
};

export const productCpAttributeList = [
  { label: firstItem, name: 'first_item' },
  { label: 'Last Item', name: 'last_item' },
  { label: 'All Items', name: 'all_item' },
  { label: 'Value', name: 'valueType' },
];

export const prouctCustmSectionAttributeList = [
  { label: 'First Input', name: 'first_item' },
  { label: 'Most Recent Input', name: 'last_item' },
];

export const prouctBomOutComeList = [
  { label: dateAdded, name: 'date_added' },
  { label: 'Value', name: 'valueType' },
  { label: percentageOfApproval, name: 'percentage_of_approval' },
];
export const prouctCustmSectionOutComeList = [
  { label: dateAdded, name: 'date_added' },
  { label: 'Value', name: 'valueType' },
];

export const productCpoutComeList = [
  { label: dateAdded, name: 'date_added' },
  { label: 'Percentage of Date Added', name: 'percentage_of_date_added' },
  { label: percentageOfApproval, name: 'percentage_of_approval' },
  { label: 'Percentage of Completion', name: 'completion_percentage' },
];

export const poProductoutComeList = [{ label: 'Percentage of Confirmed', name: 'completion_percentage' }];

export const productListDesignDetailOutComeList = [
  { label: 'Date Added', name: 'date_added' },
  { label: 'Status Date', name: 'status_date' },
  { label: 'Amended Date', name: 'amended_date' },
  { label: 'Colour', name: 'colour' },
  { label: 'Status', name: 'status' },
];

export const productListDesignDetailAttributeList = [
  { label: 'First Item', name: 'first_item' },
  { label: 'Last Item', name: 'last_item' },
];

export const productCpmanualTypeList = [
  {
    type: 'Checkbox',
    name: 'Checkbox',
    key: 'Checkbox',
  },
  {
    type: DatePicker,
    name: 'Date_picker',
    key: 'Date_picker',
  },
  {
    type: 'HTML',
    name: 'HTML',
    key: 'HTML',
  },
  {
    type: singleSelectDropdown,
    name: 'Single_select_Dropdown',
    key: 'Dropdown',
  },
  {
    type: 'Label',
    name: 'Label',
    key: 'Label',
  },
  {
    type: longTextArea,
    name: 'Long_Text',
    key: 'Text_area',
  },
  {
    type: multiSelectDropdown,
    name: 'Multi_select_Dropdown',
    key: 'Multi_select_Dropdown',
  },
  {
    type: numberDecimal,
    name: 'Number',
    key: 'Number_Decimal',
    subType: 'decimal',
  },
  {
    type: numberInteger,
    name: 'Number',
    key: 'Number_Integer',
    subType: 'integer',
  },
  {
    type: numberPercentage,
    name: 'Number_percentage',
    key: 'Number_percentage',
  },
  {
    type: radioButton,
    name: 'Radio_button',
    key: 'Radio_button',
  },
  {
    type: 'Status',
    name: 'Status',
    key: 'Status',
  },
  {
    type: shortTextBox,
    name: 'Short_Text',
    key: 'Textbox',
  },
  {
    type: TimePicker,
    name: 'Time_picker',
    key: 'Time_picker',
  },
];

export const bomSelection = [{ label: 'All Item', name: 'all_item' }];

export const PCPpoTypeList = [
  { label: 'Approve By Date', name: 'approve_by_date' },
  { label: 'Actual Approve By Date', name: 'actual_approve_by_date' },
  { label: ShipmentDate, name: 'shipment_date' },
  { label: actualShipmentDate, name: 'actual_shipment_date' },
  { label: deliveryDate, name: 'delivery_date' },
  { label: actualDeliveryDate, name: 'actual_delivery_date' },
];
export const checkIsNanAndNumber = (value) => {
  const currValue = Number(value);
  return Number.isNaN(currValue) ? 0 : currValue;
};

/**
 * Resolve the display string for a product dynamic (custom-attribute) column cell.
 * Mirrors the backend resolveDynamicDisplayValue: option-backed types (dropdown/radio)
 * show the selected value keyName, date-picker formats the stored valueText, and every
 * other type falls back to valueText.
 * @param {object} col - Dynamic column cell ({ colId, valueId, valueText }).
 * @returns {string} Display string, or '--' when no value is present.
 */
export const resolveDynamicColumnDisplay = (col) => {
  if (!col) return '--';
  if (col?.valueId) {
    return col?.valueId?.keyName || '--';
  }
  if (col?.colId?.type === 'date-picker' && col?.valueText) {
    const dateFormat = col?.colId?.datePickerConfig?.dateFormat || 'DD-MMM-YYYY';
    const parsedDate = moment(col?.valueText, 'YYYY-MM-DD', true);
    return parsedDate.isValid() ? parsedDate.format(dateFormat) : col?.valueText;
  }
  return col?.valueText || '--';
};

export const converDropdownData = (allTypeData) => {
  const allData = allTypeData;
  let filteredData = [];
  let filteredDropdownData = allData?.filter((ele) => ele.type === 'dropdown' && ele.isLable === true);
  const filteredTextData = allData?.filter((ele) => ele.type === 'text' && ele.isLable === false);
  let filteredRadioData = allData?.filter((ele) => ele.type === 'radio-button' && ele.isLable === true);
  const filteredDateData = allData?.filter((ele) => ele.type === 'date-picker');
  if (!isEmpty(filteredDropdownData)) {
    filteredDropdownData = filteredDropdownData.map((item) => {
      const valuesForLabel = allData.filter((ele) => item._id === ele.lableId);
      item.values = valuesForLabel;
      return item;
    });
  }
  if (!isEmpty(filteredRadioData)) {
    filteredRadioData = filteredRadioData.map((item) => {
      const valuesForLabel = allData.filter((ele) => item._id === ele.lableId);
      item.values = valuesForLabel;
      return item;
    });
  }
  if (isArray(filteredDropdownData) && isArray(filteredTextData)) {
    filteredData = filteredDropdownData.concat(filteredTextData);
  }
  if (isArray(filteredRadioData)) filteredData = filteredData.concat(filteredRadioData);
  if (isArray(filteredDateData)) filteredData = filteredData.concat(filteredDateData);
  return filteredData;
};
export const converDropdownDataFilterData = (allTypeData) => {
  const allData = allTypeData;
  let filteredDropdownData = allData?.filter((ele) => (ele.type === 'dropdown' || ele.type === 'radio-button') && ele.isLable === true);
  if (!isEmpty(filteredDropdownData)) {
    filteredDropdownData = filteredDropdownData.map((item) => {
      const valuesForLabel = allData.filter((ele) => item._id === ele.lableId);
      item.values = valuesForLabel;
      return item;
    });
  }
  return filteredDropdownData || [];
};

/**
 * Ids of the dropdown / radio custom attributes that System Masters configures for BOTH the
 * given Brand and Product Type selection. Both selections are required: when either is empty no
 * attribute qualifies, so the caller offers no custom attribute filters at all.
 */
export const getMatchingCustomAttributeIds = (fields, brandIds, productTypeIds) => {
  const brands = (brandIds || []).filter(Boolean);
  const productTypes = (productTypeIds || []).filter(Boolean);
  if (!brands.length || !productTypes.length) return [];
  return (fields || [])
    .filter((attribute) => {
      const matchesBrand = attribute?.brand?.length ? attribute.brand.some((brandId) => brands.includes(brandId)) : false;
      const matchesProductType = attribute?.productType?.length ? attribute.productType.some((productTypeId) => productTypes.includes(productTypeId)) : false;
      return matchesBrand && matchesProductType;
    })
    .map((attribute) => attribute._id);
};

/**
 * Active multi dropdown custom attributes matching the given Product Type selection. This master
 * is configured by product type only, so the brand selection does not apply; an empty product
 * type selection yields nothing.
 */
export const getMatchingMultiCustomAttributes = (fields, productTypeIds) => {
  const productTypes = (productTypeIds || []).filter(Boolean);
  if (!productTypes.length) return [];
  return (fields || []).filter(
    (attribute) => attribute?.isActive !== false && attribute?.productType?.some((productTypeId) => productTypes.includes(productTypeId)),
  );
};

export const manualLabelColours = [
  {
    colourName: 'Red',
    colourHexCode: '#f65158',
  },
  {
    colourName: 'Orange',
    colourHexCode: '#fc6a03',
  },
  {
    colourName: 'Yellow',
    colourHexCode: '#fbb755',
  },
  {
    colourName: 'Green',
    colourHexCode: '#159e79',
  },
  {
    colourName: 'Turquoise',
    colourHexCode: '#33dfff',
  },
  {
    colourName: 'Blue',
    colourHexCode: '#1e90ff',
  },
  {
    colourName: 'Purple',
    colourHexCode: '#b05bdc',
  },
  {
    colourName: 'Pink',
    colourHexCode: '#dc5bc2',
  },
  {
    colourName: 'Mid Grey',
    colourHexCode: '#848484',
  },
  {
    colourName: 'Light Grey',
    colourHexCode: '#cecece',
  },
];

export const manualStatusColours = [
  {
    colourName: 'Yellow',
    colourHexCode: '#fbb755',
  },
  {
    colourName: 'Orange',
    colourHexCode: '#dc6d5b',
  },

  {
    colourName: 'Red',
    colourHexCode: '#f65158',
  },
  {
    colourName: 'Pink',
    colourHexCode: '#dc5bc2',
  },
  {
    colourName: 'Teal',
    colourHexCode: '#2c7d6d',
  },
];

export const compareObjectWithKey = (obj1, obj2, keyName) => {
  let isEqual = null;

  if ((!isEmpty(obj1) && obj1[keyName]) === (!isEmpty(obj2) && obj2[keyName])) {
    isEqual = true;
  } else {
    isEqual = false;
  }
  return isEqual;
};

export const removeImageOrientation = async (imageArray) => {
  const newArray = [];

  const loadImage = (element) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = element.image;
      img.onload = () => resolve(img);
      img.onerror = (error) => reject(error);
    });
  };

  const processImage = (img, element) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const base64WithoutOrientation = canvas.toDataURL('image/jpeg');

      if (base64WithoutOrientation) {
        newArray.push({ ...element, image: base64WithoutOrientation });
      }
      resolve();
    });
  };

  for (const element of imageArray) {
    try {
      const img = await loadImage(element);
      await processImage(img, element);
    } catch (error) {
      console.error('Error loading or processing image:', error);
    }
  }

  return newArray;
};

export const getTimeZone = () => window?.Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;

export const settingModuleArray = [
  {
    title: productInformation,
    moduleName: 'productInformation',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Add', isChecked: true, keyName: 'add' },
      { name: 'Update', isChecked: true, keyName: 'update' },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
      {
        name: archiveUnarchive,
        isChecked: true,
        keyName: 'archiveUnarchive',
      },
    ],
  },
  {
    title: 'Design Detail',
    moduleName: 'designDetail',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      {
        name: 'Add/Annotate/Replace',
        isChecked: true,
        keyName: 'addAnnotateReplace',
      },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
      { name: approveReject, isChecked: true, keyName: 'approveReject' },
      { name: markAsComplete, isChecked: true, keyName: 'markAsComplete' },
      {
        name: archiveUnarchive,
        isChecked: true,
        keyName: 'archiveUnarchive',
      },
    ],
  },
  {
    title: billOfMaterials,
    moduleName: 'bom',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Add', isChecked: true, keyName: 'add' },
      { name: 'Update', isChecked: true, keyName: 'update' },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
      { name: approveReject, isChecked: true, keyName: 'approveReject' },
      { name: markAsComplete, isChecked: true, keyName: 'markAsComplete' },
      {
        name: supplierSuggestions,
        isChecked: true,
        keyName: 'supplierSuggestions',
      },
    ],
  },
  {
    title: 'Specifications',
    moduleName: 'specification',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Add', isChecked: true, keyName: 'add' },
      { name: 'Update', isChecked: true, keyName: 'update' },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
      { name: approveReject, isChecked: true, keyName: 'approveReject' },
      { name: markAsComplete, isChecked: true, keyName: 'markAsComplete' },
      {
        name: supplierSuggestions,
        isChecked: true,
        keyName: 'supplierSuggestions',
      },
    ],
  },
  {
    title: 'Testing',
    moduleName: 'testing',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Add', isChecked: true, keyName: 'add' },
      { name: 'Update', isChecked: true, keyName: 'update' },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
      { name: approveReject, isChecked: true, keyName: 'approveReject' },
      { name: markAsComplete, isChecked: true, keyName: 'markAsComplete' },
      {
        name: supplierSuggestions,
        isChecked: true,
        keyName: 'supplierSuggestions',
      },
    ],
  },
  {
    title: 'User Instructions',
    moduleName: 'userIntruction',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Add', isChecked: true, keyName: 'add' },
      { name: 'Update', isChecked: true, keyName: 'update' },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
      { name: approveReject, isChecked: true, keyName: 'approveReject' },
      { name: markAsComplete, isChecked: true, keyName: 'markAsComplete' },
      {
        name: supplierSuggestions,
        isChecked: true,
        keyName: 'supplierSuggestions',
      },
    ],
  },
  {
    title: 'Samples',
    moduleName: 'sample',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Add', isChecked: true, keyName: 'add' },
      { name: 'Update', isChecked: true, keyName: 'update' },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
      { name: statusChange, isChecked: true, keyName: 'statusChange' },
      { name: markAsComplete, isChecked: true, keyName: 'markAsComplete' },
      {
        name: 'Supplier Selection Updates',
        isChecked: true,
        keyName: 'supplierSuggestions',
      },
    ],
  },
  {
    title: 'Costings',
    moduleName: 'productCosting',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Add', isChecked: true, keyName: 'add' },
      { name: 'Update', isChecked: true, keyName: 'update' },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
      { name: statusChange, isChecked: true, keyName: 'statusChange' },
      { name: markAsComplete, isChecked: true, keyName: 'markAsComplete' },
    ],
  },
  {
    title: 'Multi Costings',
    moduleName: 'multiProductCosting',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Submit Multiple', isChecked: true, keyName: 'submitMultiple' },
      { name: 'Download Multiple', isChecked: true, keyName: 'downloadMultiple' },
      { name: 'Supplier Download All', isChecked: true, keyName: 'supplierDownloadAll' },
      { name: 'Supplier Download Some', isChecked: true, keyName: 'supplierDownloadSome' },
    ],
  },
  {
    title: 'Purchase Orders',
    moduleName: 'purchaseOrder',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Add', isChecked: true, keyName: 'add' },
      { name: 'Update', isChecked: true, keyName: 'update' },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
      { name: statusChange, isChecked: true, keyName: 'statusChange' },
      { name: markAsComplete, isChecked: true, keyName: 'markAsComplete' },
    ],
  },
  {
    title: 'Submit and Downloads',
    moduleName: 'submitAndDownload',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Submit', isChecked: true, keyName: 'submit' },
      { name: 'Download', isChecked: true, keyName: 'download' },
      { name: 'Email Addresses Download and submit', isChecked: true, keyName: 'emailAddDownloadSubmit' },
    ],
  },
  {
    title: 'Multi Submit and Downloads',
    moduleName: 'multiSubmitAndDownload',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Submit Multiple', isChecked: true, keyName: 'submitMultiple' },
      { name: 'Download Multiple', isChecked: true, keyName: 'downloadMultiple' },
      { name: 'Supplier Download Some', isChecked: true, keyName: 'supplierDownloadSome' },
    ],
  },
  {
    title: supplierSuggestions,
    moduleName: 'supplierSuggestion',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'New', isChecked: true, keyName: 'new' },
      { name: 'Amended', isChecked: true, keyName: 'amended' },
      { name: 'Approved New', isChecked: true, keyName: 'approvedNew' },
      { name: 'Approved Amended', isChecked: true, keyName: 'approvedAmended' },
      { name: 'Rejected New', isChecked: true, keyName: 'rejectedNew' },
      { name: 'Rejected Amended', isChecked: true, keyName: 'rejectedAmended' },
    ],
  },
  {
    title: 'Documents',
    moduleName: 'document',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      { name: 'Add/Renew', isChecked: true, keyName: 'addRenew' },
      { name: 'Delete', isChecked: true, keyName: 'delete' },
    ],
  },
  {
    title: 'Operational Critical Paths',
    moduleName: 'OCP',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      {
        name: 'Add Row Name (Assigned to you)',
        isChecked: true,
        keyName: 'addRowNameAssigned',
      },
      {
        name: 'Add Row Name (not Assigned to you)',
        isChecked: true,
        keyName: 'addRowNameNotAssigned',
      },
      {
        name: 'Update Row Name (Assigned to you)',
        isChecked: true,
        keyName: 'updateRowNameAssigned',
      },
      {
        name: 'Update Row Name (not Assigned to you)',
        isChecked: true,
        keyName: 'updateRowNameNotAssigned',
      },
      {
        name: 'Delete Row Name (Assigned to you)',
        isChecked: true,
        keyName: 'deleteRowNameAssigned',
      },
      {
        name: 'Delete Row Name (not Assigned to you)',
        isChecked: true,
        keyName: 'deleteRowNameNotAssigned',
      },
      {
        name: addColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'addColumnNameAssigned',
      },
      {
        name: addColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'addColumnNameNotAssigned',
      },
      {
        name: updateColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'updateColumnNameAssigned',
      },
      {
        name: updateColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'updateColumnNameNotAssigned',
      },
      {
        name: deleteColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'deleteColumnAssigned',
      },
      {
        name: deleteColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'deleteColumnNotAssigned',
      },
      {
        name: addCellDataAssignedToYou,
        isChecked: true,
        keyName: 'addCellDataAssigned',
      },
      {
        name: addCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'addCellDataNotAssigned',
      },
      {
        name: updateCellDataAssignedToYou,
        isChecked: true,
        keyName: 'updateCellDataAssigned',
      },
      {
        name: updateCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'updateCellDataNotAssigned',
      },
      {
        name: deleteCellDataAssignedToYou,
        isChecked: true,
        keyName: 'deleteCellDataAssigned',
      },
      {
        name: deleteCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'deleteCellDataNotAssigned',
      },
      {
        name: assignmentRemovalOfYourGroup,
        isChecked: true,
        keyName: 'assignmentRemovalOfyou',
      },
      {
        name: assignmentRemovalOfOthers,
        isChecked: true,
        keyName: 'assignmentRemovalOfother',
      },
      {
        name: preparationInProgress,
        isChecked: true,
        keyName: 'preparationInProgress',
      },
      {
        name: preparedAndReadyForDownload,
        isChecked: true,
        keyName: 'preparedAndReadyForDownload',
      },
      {
        name: bulkUploadInProgress,
        isChecked: true,
        keyName: 'bulkUploadInProgress',
      },
      {
        name: bulkUploadCompleted,
        isChecked: true,
        keyName: 'bulkUploadCompleted',
      },
      {
        name: archiveUnarchive,
        isChecked: true,
        keyName: 'archiveUnarchive',
      },
    ],
  },
  {
    title: 'Product Critical Paths',
    moduleName: 'PCP',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      {
        name: addColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'addColumnNameAssigned',
      },
      {
        name: addColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'addColumnNameNotAssigned',
      },
      {
        name: updateColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'updateColumnNameAssigned',
      },
      {
        name: updateColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'updateColumnNameNotAssigned',
      },
      {
        name: deleteColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'deleteColumnAssigned',
      },
      {
        name: deleteColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'deleteColumnNotAssigned',
      },
      {
        name: addCellDataAssignedToYou,
        isChecked: true,
        keyName: 'addCellDataAssigned',
      },
      {
        name: addCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'addCellDataNotAssigned',
      },
      {
        name: updateCellDataAssignedToYou,
        isChecked: true,
        keyName: 'updateCellDataAssigned',
      },
      {
        name: updateCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'updateCellDataNotAssigned',
      },
      {
        name: deleteCellDataAssignedToYou,
        isChecked: true,
        keyName: 'deleteCellDataAssigned',
      },
      {
        name: deleteCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'deleteCellDataNotAssigned',
      },
      {
        name: assignmentRemovalOfYourGroup,
        isChecked: true,
        keyName: 'assignmentRemovalOfyou',
      },
      {
        name: assignmentRemovalOfOthers,
        isChecked: true,
        keyName: 'assignmentRemovalOfother',
      },
      {
        name: preparationInProgress,
        isChecked: true,
        keyName: 'preparationInProgress',
      },
      {
        name: preparedAndReadyForDownload,
        isChecked: true,
        keyName: 'preparedAndReadyForDownload',
      },
      {
        name: bulkUploadInProgress,
        isChecked: true,
        keyName: 'bulkUploadInProgress',
      },
      {
        name: bulkUploadCompleted,
        isChecked: true,
        keyName: 'bulkUploadCompleted',
      },
      {
        name: archiveUnarchive,
        isChecked: true,
        keyName: 'archiveUnarchive',
      },
    ],
  },
  {
    title: 'Range Plan - Custom Sections',
    moduleName: 'rangePlan',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      {
        name: addColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'addColumnNameAssigned',
      },
      {
        name: addColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'addColumnNameNotAssigned',
      },
      {
        name: updateColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'updateColumnNameAssigned',
      },
      {
        name: updateColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'updateColumnNameNotAssigned',
      },
      {
        name: deleteColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'deleteColumnAssigned',
      },
      {
        name: deleteColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'deleteColumnNotAssigned',
      },
      {
        name: addCellDataAssignedToYou,
        isChecked: true,
        keyName: 'addCellDataAssigned',
      },
      {
        name: addCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'addCellDataNotAssigned',
      },
      {
        name: updateCellDataAssignedToYou,
        isChecked: true,
        keyName: 'updateCellDataAssigned',
      },
      {
        name: updateCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'updateCellDataNotAssigned',
      },
      {
        name: deleteCellDataAssignedToYou,
        isChecked: true,
        keyName: 'deleteCellDataAssigned',
      },
      {
        name: deleteCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'deleteCellDataNotAssigned',
      },
      {
        name: assignmentRemovalOfYourGroup,
        isChecked: true,
        keyName: 'assignmentRemovalOfyou',
      },
      {
        name: assignmentRemovalOfOthers,
        isChecked: true,
        keyName: 'assignmentRemovalOfother',
      },
      {
        name: preparationInProgress,
        isChecked: true,
        keyName: 'preparationInProgress',
      },
      {
        name: preparedAndReadyForDownload,
        isChecked: true,
        keyName: 'preparedAndReadyForDownload',
      },
      {
        name: bulkUploadInProgress,
        isChecked: true,
        keyName: 'bulkUploadInProgress',
      },
      {
        name: bulkUploadCompleted,
        isChecked: true,
        keyName: 'bulkUploadCompleted',
      },
      {
        name: archiveUnarchive,
        isChecked: true,
        keyName: 'archiveUnarchive',
      },
    ],
  },
  {
    title: 'PIM',
    moduleName: 'PIM',
    isParentChecked: true,
    childNodes: [
      { name: selectAll, isChecked: true, keyName: 'selectAll' },
      {
        name: addColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'addColumnNameAssigned',
      },
      {
        name: addColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'addColumnNameNotAssigned',
      },
      {
        name: updateColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'updateColumnNameAssigned',
      },
      {
        name: updateColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'updateColumnNameNotAssigned',
      },
      {
        name: deleteColumnNameAssignedToYou,
        isChecked: true,
        keyName: 'deleteColumnAssigned',
      },
      {
        name: deleteColumnNameNotAssignedToYou,
        isChecked: true,
        keyName: 'deleteColumnNotAssigned',
      },
      {
        name: addCellDataAssignedToYou,
        isChecked: true,
        keyName: 'addCellDataAssigned',
      },
      {
        name: addCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'addCellDataNotAssigned',
      },
      {
        name: updateCellDataAssignedToYou,
        isChecked: true,
        keyName: 'updateCellDataAssigned',
      },
      {
        name: updateCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'updateCellDataNotAssigned',
      },
      {
        name: deleteCellDataAssignedToYou,
        isChecked: true,
        keyName: 'deleteCellDataAssigned',
      },
      {
        name: deleteCellDataNotAssignedToYou,
        isChecked: true,
        keyName: 'deleteCellDataNotAssigned',
      },
      {
        name: assignmentRemovalOfYourGroup,
        isChecked: true,
        keyName: 'assignmentRemovalOfyou',
      },
      {
        name: assignmentRemovalOfOthers,
        isChecked: true,
        keyName: 'assignmentRemovalOfother',
      },
      {
        name: preparationInProgress,
        isChecked: true,
        keyName: 'preparationInProgress',
      },
      {
        name: preparedAndReadyForDownload,
        isChecked: true,
        keyName: 'preparedAndReadyForDownload',
      },
      {
        name: bulkUploadInProgress,
        isChecked: true,
        keyName: 'bulkUploadInProgress',
      },
      {
        name: bulkUploadCompleted,
        isChecked: true,
        keyName: 'bulkUploadCompleted',
      },
      {
        name: archiveUnarchive,
        isChecked: true,
        keyName: 'archiveUnarchive',
      },
    ],
  },
];

export const ERROR_MESSAGE = 'API connection failed. Please screenshot error and contact your account manager.';

/**
 * Extract the backend's business-error i18n key from a rejected API call. Validation failures
 * come back as a 4xx carrying either `{ data: { errMsg } }` or, from the api-response helper,
 * `{ message }`; genuine transport failures carry nothing, so callers fall back to ERROR_MESSAGE.
 * @param {unknown} error the rejected axios error
 * @returns {string} the i18n key, or an empty string when the failure is not a business error
 */
export const getApiErrorKey = (error) => error?.response?.data?.data?.errMsg || error?.response?.data?.message || '';

/**
 * Translate a backend error key, falling back to the generic message when it has no translation.
 *
 * i18next returns the key itself when a translation is missing, so an unmapped backend key would
 * otherwise be shown to the user verbatim — e.g.
 * "BACKEND_SERVER.DEVELOPMENT_ORDER.DEVELOPMENT_ORDER_SERVICE.PRODUCT_UPDATE_FAILED".
 * Backend internals must never reach the UI, so anything untranslated becomes the generic message.
 * @param {Function} translate the `t` function for the active namespace
 * @param {string} key the error key returned by the backend
 * @returns {string} a message that is safe to show the user
 */
export const backendErrorMsg = (translate, key) => {
  if (!key || typeof key !== 'string') return ERROR_MESSAGE;
  const translated = typeof translate === 'function' ? translate(key) : '';
  return !translated || translated === key ? ERROR_MESSAGE : translated;
};
export const SUBMIT_DOWNLOAD = {
  FILE_NOT_EXISTS: 'The required file no longer exists.',
  GRAD_GROUPS_SWITCH_OFF: 'Switch is off for all the grading groups in Specifications.',
  SWITCH_OFF: 'Either switch is off or no entry found.',
  FILE_NOT_AVAILABLE: 'File not available for download.Please contact admin.',
  FILE_MISSING: 'This file was archived and is no longer available for download.',
  DESIGN_NOT_FOUND: 'Design not found',
};
const componenetSchema = {
  Company_Details: [
    [
      { type: 'item', id: '1', key: 'Dropdown', model: 'contactPersonName', label: 'Contact Person Name', enable: true, required: true, options: [] },
      { type: 'item', id: '3', key: 'Text', model: 'createdByName', label: 'Created By Name', enable: true, required: true },
      { type: 'item', id: '5', key: 'Text_Area', label: 'Billing Address', enable: true, required: true },
    ],
    [
      { type: 'item', id: '2', key: 'Text', model: 'contactPersonEmail', label: 'Contact Person Email', enable: true, required: true },
      { type: 'item', id: '4', key: 'Text', model: 'createdByEmail', label: 'Created By Email', enable: true, required: true },
      { type: 'item', id: '6', key: 'Text_Area', label: 'Shipping Address', enable: true, required: true },
    ],
  ],

  Shipping_Details: [
    [
      {
        type: 'item',
        id: '5',
        key: 'Dropdown',
        label: 'Packing Type',
        enable: true,
        required: true,
        options: [{ name: 'Flat' }, { name: 'Hanging' }, { name: 'Rolled' }, { name: 'Other' }],
      },
      {
        type: 'item',
        id: '5',
        key: 'Dropdown',
        label: 'Mode of Shipment',
        enable: true,
        required: true,
        options: [{ name: 'Air' }, { name: 'Sea' }, { name: 'Truck' }, { name: seaAndTruck }],
      },
    ],
    [
      {
        type: 'item',
        id: '4',
        key: 'Dropdown',
        label: 'Incoterms',
        enable: true,
        required: true,
        options: [
          { name: 'EXW (Ex Works)' },
          { name: 'FCA (Free Carrier)' },
          { name: 'FAS (Free Alongside Ship)' },
          { name: 'FOB (Free on Board)' },
          { name: 'CFR (Cost and Freight) ' },
          { name: 'CIF (Cost, Insurance and Freight)' },
          { name: 'CPT (Carriage Paid to) ' },
          { name: 'CIP (Carriage and Insurance Paid To)' },
          { name: 'DAT (Delivered at Terminal)' },
          { name: 'DAP (Delivered at Place)' },
          { name: 'DDP (Delivered Duty Paid)' },
        ],
      },
    ],
  ],
  Supplier_factory: [
    [
      {
        type: 'item',
        id: '1',
        fixed: true,
        field: 'supplierName',
        model: 'supplierName',
        key: 'Text',
        label: supplierName,
        enable: true,
        required: true,
      },
      {
        type: 'item',
        id: '2',
        fixed: true,
        field: 'supplierAddress',
        model: 'supplierAddress',
        key: 'Text_Area',
        label: 'Supplier Address',
        enable: true,
        required: true,
      },
      {
        type: 'item',
        id: '3',
        fixed: true,
        field: 'supplierCode',
        key: 'Text',
        model: 'supplierCode',
        label: supplierCode,
        enable: true,
        required: true,
      },
      {
        type: 'item',
        id: '4',
        fixed: true,
        field: 'contactPersonsDetails',
        model: 'contactPersonsDetails',
        key: 'Dropdown',
        label: 'Supplier Contact Name',
        enable: true,
        required: true,
        options: [],
      },
      {
        type: 'item',
        id: '5',
        fixed: true,
        field: 'supplierContactEmail',
        model: 'supplierContactEmail',
        key: 'Text',
        label: 'Supplier Contact Email',
        enable: true,
        required: true,
      },
    ],
    [
      {
        type: 'item',
        id: '6',
        fixed: true,
        field: 'factoryName',
        onSelect: 'bindFactoryDetails',
        model: 'factoryName',
        key: 'Dropdown',
        label: factoryName,
        enable: true,
        required: true,
        options: [],
      },
      {
        type: 'item',
        id: '7',
        fixed: true,
        field: 'factoryAddress',
        model: 'factoryAddress',
        key: 'Text_Area',
        label: 'Factory Address',
        enable: true,
        required: true,
      },
      {
        type: 'item',
        id: '8',
        fixed: true,
        field: 'factoryCode',
        model: 'factoryCode',
        key: 'Text',
        label: factoryCode,
        enable: true,
        required: true,
      },
      {
        type: 'item',
        id: '10',
        fixed: true,
        field: 'factContactPersonsDetails',
        model: 'factContactPersonsDetails',
        key: 'Dropdown',
        label: 'Factory Contact Name',
        enable: true,
        required: true,
        options: [],
      },
      {
        type: 'item',
        id: '11',
        fixed: true,
        field: 'factoryContactEmail',
        model: 'factoryContactEmail',
        key: 'Text',
        label: 'Factory Contact Email',
        enable: true,
        required: true,
      },
    ],
  ],
};

export const poTemplatemodel = [
  {
    templates: [
      { type: 'container', key: 'Shipping_Details', id: 2, label: 'Shipping Detail', columns: componenetSchema.Shipping_Details },
      { type: 'container', key: 'Company_Details', id: 3, label: 'Company Detail', columns: componenetSchema.Company_Details },
      { type: 'container', key: 'Supplier_factory', id: 4, label: 'Supplier/Factory Details', columns: componenetSchema.Supplier_factory },
    ],
    components: [
      { type: 'item', id: '5', key: 'Text_Area', label: 'Text Area', placeholder: 'Enter Text', enable: true, required: true },
      { type: 'item', id: '6', key: 'Text', label: 'Textbox', placeholder: 'Enter Text', enable: true, required: true },
      {
        type: 'item',
        id: '7',
        key: 'Dropdown',
        label: 'Dropdown',
        placeholder: 'Select Option',
        enable: true,
        required: true,
        options: [{ name: 'Option 1' }],
      },
      { type: 'item', id: '8', key: 'Check_Boxes', label: 'Checkbox', placeholder: '', enable: true, required: true, options: [{ name: 'Option 1' }] },
      {
        type: 'item',
        id: '9',
        key: 'Ratio_Button',
        label: radioButton,
        placeholder: '',
        enable: true,
        required: true,
        options: [{ name: 'Option 1' }, { name: 'Option 2' }],
      },
      {
        type: 'container',
        key: 'Section',
        id: 4,
        label: 'Section',
        columns: [
          [
            {
              type: 'item',
              key: 'Div',
            },
          ],
          [
            {
              type: 'item',
              key: 'Div',
            },
          ],
        ],
      },
      { type: 'item', id: '10', key: 'Date_Picker', label: DatePicker, placeholder: '', enable: true, required: true },
      { type: 'item', id: '11', key: 'Time_Picker', label: TimePicker, placeholder: '', enable: true, required: true },
      {
        type: 'item',
        id: '12',
        key: 'Ratio_Button',
        label: 'Question Label',
        placeholder: '',
        enable: true,
        required: true,
        options: [{ name: 'Yes' }, { name: 'No' }],
      },
      { type: 'item', id: '13', key: 'Text_Area', label: 'Paragraph', placeholder: '', enable: true, required: true },
    ],
  },
];

export const dateFormate = 'DD-MMMM-YYYY, h:mm a';

export const dateFormateDateOnly = 'DD-MMM-YYYY';

/**
 * Formats a stored date for display in the viewer's local timezone.
 * Values that were never set or were cleared arrive as null, '' or undefined,
 * and moment renders those as "Invalid date" or silently as today, so they are
 * shown as a dash instead.
 * @param {string|Date|null|undefined} value Raw date from the API.
 * @param {string} dateFormat Moment format string to render with.
 * @returns {string} Formatted date, or '-' when there is no usable date.
 */
export const formatDateOrDash = (value, dateFormat) => {
  if (!value) {
    return '-';
  }
  const parsedDate = moment(value);
  return parsedDate.isValid() ? parsedDate.format(dateFormat) : '-';
};

/**
 * Formats a date-only value without converting to the viewer's timezone.
 * Use for calendar dates stored at UTC midnight (e.g. document expiry), so every
 * user sees the same day rather than one shifted by their local offset.
 * @param {*} value Date, ISO string or timestamp stored at UTC midnight.
 * @param {string} dateFormat Moment format string.
 * @returns {string} Formatted date, or '-' when absent/invalid.
 */
export const formatUtcDateOrDash = (value, dateFormat) => {
  if (!value) {
    return '-';
  }
  const parsedDate = moment.utc(value);
  return parsedDate.isValid() ? parsedDate.format(dateFormat) : '-';
};

export const selectedOption = (array, value) => {
  return array?.find((e) => e?._id === (typeof value === 'string' ? value : value?._id)) || null;
};

export const customStylesWithBorderRight = {
  ...customStyles,
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: borderBottom,
    borderRight: borderBottom,
    height: '35px',
    minWidth: '150px',
    padding: '5px 0 5px 5px',
    fontSize: '14px',
    background: '#FFF',
  }),
};

export const setArrayByCase = (array, payload) => {
  if (!isEmpty(get(payload, 'searchText')) && get(payload, 'isScrolled')) {
    array = [...array, ...payload.data];
    return array;
  }
  if (!isEmpty(get(payload, 'searchText'))) {
    array = payload.data;
    return array;
  }
  if (get(payload, 'isScrolled')) {
    array = concat(array, payload.data);
    return array;
  }
  return payload.data;
};
export const bomInfoModuleSearchOption = [
  {
    value: 'bomlibrary',
    label: (
      <CustomTooltip placement="right" tooltipText="BOM Library">
        <i className="icon-Library-1 f-24" />
      </CustomTooltip>
    ),
  },
  {
    value: 'tags',
    label: (
      <CustomTooltip placement="right" tooltipText="Tag">
        <i className="icon-Tags f-24" />
      </CustomTooltip>
    ),
  },
];
export const productInfoModuleSearchOption = [
  {
    value: 'productNumber',
    label: (
      <CustomTooltip tooltipText="Product Number">
        <i className="icon-Product f-24" />
      </CustomTooltip>
    ),
  },
  {
    value: 'tags',
    label: (
      <CustomTooltip tooltipText="Tags">
        <i className="icon-Tags f-24" />
      </CustomTooltip>
    ),
  },
];

export const ProductInfoSortOption = [
  {
    value: { sortBy: 'Brand (Ascending)', sortOrder: 'Asc' },
    label: 'Brand (Ascending)',
  },
  {
    value: { sortBy: 'Brand (Descending)', sortOrder: 'Desc' },
    label: 'Brand (Descending)',
  },
  {
    value: { sortBy: 'Season (Ascending)', sortOrder: 'Asc' },
    label: 'Season (Ascending)',
  },
  {
    value: { sortBy: 'Season (Descending)', sortOrder: 'Desc' },
    label: 'Season (Descending)',
  },
  {
    value: { sortBy: 'Created (Ascending)', sortOrder: 'Asc' },
    label: 'Created (Ascending)',
  },
  {
    value: { sortBy: 'Created (Descending)', sortOrder: 'Desc' },
    label: 'Created (Descending)',
  },
  {
    value: { sortBy: 'Modified (Ascending)', sortOrder: 'Asc' },
    label: 'Modified (Ascending)',
  },
  {
    value: { sortBy: 'Modified (Descending)', sortOrder: 'Desc' },
    label: 'Modified (Descending)',
  },
];

export const combinationData = (data) => {
  const sizeArr = data?.sizeRanges;
  const lengthArr = data?.legLengths;
  const colourArr = data?.colourLibraries;
  const combinedArr = [];

  if (isArray(colourArr) && colourArr?.length && isArray(lengthArr) && lengthArr?.length && isArray(sizeArr) && sizeArr?.length) {
    for (const colour of colourArr) {
      for (const size of sizeArr) {
        for (const length of lengthArr) {
          const obj = {
            colourWay: colour,
            size,
            length,
          };
          combinedArr.push(obj);
        }
      }
    }
  } else if (isArray(colourArr) && colourArr?.length && isArray(lengthArr) && lengthArr?.length) {
    for (const colour of colourArr) {
      for (const length of lengthArr) {
        const obj = {
          colourWay: colour,
          length,
          size: '-',
        };
        combinedArr.push(obj);
      }
    }
  } else if (isArray(colourArr) && colourArr?.length && isArray(sizeArr) && sizeArr?.length) {
    for (const colour of colourArr) {
      for (const size of sizeArr) {
        const obj = {
          colourWay: colour,
          length: null,
          size,
        };
        combinedArr.push(obj);
      }
    }
  } else if (isArray(lengthArr) && lengthArr?.length && isArray(sizeArr) && sizeArr?.length) {
    for (const length of lengthArr) {
      for (const size of sizeArr) {
        const obj = {
          colourWay: null,
          length,
          size,
        };
        combinedArr.push(obj);
      }
    }
  } else if (isArray(lengthArr) && lengthArr?.length) {
    for (const length of lengthArr) {
      const obj = {
        colourWay: null,
        length,
        size: '-',
      };
      combinedArr.push(obj);
    }
  } else if (isArray(sizeArr) && sizeArr?.length) {
    for (const size of sizeArr) {
      const obj = {
        colourWay: null,
        length: null,
        size,
      };
      combinedArr.push(obj);
    }
  } else if (isArray(colourArr) && colourArr?.length) {
    for (const colour of colourArr) {
      const obj = {
        colourWay: colour,
        length: null,
        size: '-',
      };
      combinedArr.push(obj);
    }
  } else {
    combinedArr.push({
      colourWay: null,
      length: null,
      size: '-',
    });
  }
  return combinedArr;
};

export const matchRule = (rule, valueFloat) => {
  const start = parseFloat(rule?.startPercentage);
  const end = rule?.endPercentage ? parseFloat(rule.endPercentage) : null;
  const value = parseFloat(valueFloat);

  return (
    (rule.colourValue === 'equal' && value === start) ||
    (rule.colourValue === 'greater_than' && value >= start) ||
    (rule.colourValue === 'between' && ((value >= start && (end === null || value <= end)) || (value <= start && (end === null || value >= end)))) ||
    (rule.colourValue === 'less_than' && value <= start)
  );
};

export const fetchColorArray = (oldArray) => {
  const colorData = oldArray;
  const isEqualIndex = colorData?.findIndex((ele) => ele.colourValue === 'equal');
  let newArray = colorData;
  if (isEqualIndex !== -1) {
    // Create a new array with the "equal" object at the beginning
    newArray = [colorData[isEqualIndex], ...colorData.slice(0, isEqualIndex), ...colorData.slice(isEqualIndex + 1)];
  }
  return newArray;
};

// company rights for = user setting page access
export const UserAccessRights = () => {
  const rights = RightsAccess();

  return {
    products: true,
    libraries: true,
    rangePlan: rights?.rangePlanRight,
    criticalPath: rights?.productCriticalPathRight || rights?.operationalCriticalPathRight,
    planning: rights?.planningBottomUpRight || rights?.planningTopsDownRight,
    placeholder: rights?.placeholderRight,
    supplierOverview: true,
    pim: rights?.PIMRight,
    dam: true,
    dpp: rights?.DPPRight,
    manuals: rights?.manualDocsRight,
    masters: true,
    addProduct: true,
    design: true,
    bom: true,
    measurement: true,
    testing: rights?.TestingRight,
    care: true,
    costing: rights?.productCostingRight,
    purchaseOrder: rights?.purchaseOrderRight,
    tradeRef: false,
    documents: rights?.productDocRight,
    colours: true,
    pomGrading: true,
    spec: true,
    cad: true,
    poTemplate: rights?.purchaseOrderRight,
    costingTemplate: rights?.productCostingRight,
    default: true,
    custom: true,
    product: rights?.productCriticalPathRight,
    operational: rights?.operationalCriticalPathRight,
    bottomUp: rights?.planningBottomUpRight,
    topDown: rights?.planningTopsDownRight,
    brand: true,
    season: true,
    category: true,
    poSize: rights?.purchaseOrderRight,
    length: true,
    units: true,
    prodCompTypes: true,
    prodStatus: true,
    bomSubTypes: true,
    placement: true,
    finishes: true,
    composition: true,
    specs: true,
    samples: true,
    exchangeRates: true,
    customAttribute: true,
    customModule: rights?.customModuleRight,
    productGroups: true,
    productIdentifier: true,
    documentType: true,
    coastingStage: true,
    testType: true,
    DAM: true,
    incoterms: true,
    licensor: true,
    licence: true,
    likeness: true,
    subBrand: true,
    productTypeProcesses: true,
    bomComponentTypeProcess: true,
    compositionProcess: true,
    retailIdentifier: true,
  };
};

export const PCPDUrationUnit = [
  {
    label: 'Day(s)',
    name: 'days',
  },
  {
    label: 'Week(s)',
    name: 'weeks',
  },
  {
    label: 'Month(s)',
    name: 'months',
  },
  {
    label: 'Year(s)',
    name: 'years',
  },
];
export const productInfoHeader = ['Reference', 'Supplier', 'Currency', 'Target cost', 'Incoterms', 'Shipment Method'];
export const averageCostingHeader = ['Colourway', 'Size', 'Length', 'Quantity', 'Price Per Unit', 'Total Price'];
export const grandTotalHeader = ['Colourway', 'Size', 'Length', 'Quantity', 'Price Per Unit', 'Total Price'];
export const landedIndoHeader = [
  'Colourway',
  'Size Range',
  'Length Range',
  'Price Per Unit (Incoterm)',
  'Price Per Unit (Converted)',
  'Total (Incoterm)',
  'Brand Fee %',
  'Supplier Fee %',
  'Price Per Unit (Landed)',
  'Total (Landed)',
  'Selling Price (Gross)',
  'VAT',
  'Selling Price (Net)',
  'Gross Profit Per Unit',
  'Markup Per Unit',
  'Margin % Per Unit',
];
export const quantitiesHeader = ['Colourway', 'Size', 'Length', 'Quantity'];
export const testingHeader = [
  'Colourway',
  'Testing Code',
  'Testing Description',
  'Testing Type',
  'BOM Component',
  'Colour Ref',
  'Test Price',
  'Price Per Unit',
  'Exclude',
];

export const additionalHeader = ['Colourway', 'Item', 'Quantity', 'UoM', 'Total Quantity', 'Price Per Unit', 'Total Price'];
export const detailedCostingHeader = [
  'BOM Type',
  'Component Code',
  'Component Description',
  'Component Type',
  'Placement',
  'Colourway',
  'Colour Ref',
  'Size Range',
  'Length Range',
  'Consumption',
  'UoM',
  'Price Per Unit',
  'Total Price',
];

const allCurrencySymbols = [
  '$',
  '$',
  '€',
  'د.إ.',
  '؋',
  'Lek',
  'դր.',
  '$',
  '$',
  'ман.',
  'KM',
  '৳',
  'лв.',
  'د.ب.',
  'FBu',
  '$',
  'Bs',
  'R$',
  'P',
  'руб.',
  '$',
  'FrCD',
  'CHF',
  '$',
  'CN¥',
  '$',
  '₡',
  'CV$',
  'Kč',
  'Fdj',
  'kr',
  'RD$',
  'د.ج.',
  'kr',
  'ج.م.',
  'Nfk',
  'Br',
  '£',
  'GEL',
  'GH₵',
  'FG',
  'Q',
  '$',
  'L',
  'kn',
  'Ft',
  'Rp',
  '₪',
  '₹',
  'د.ع.',
  '﷼',
  'kr',
  '$',
  'د.أ.',
  '￥',
  'Ksh',
  '៛',
  'FC',
  '₩',
  'د.ك.',
  'تңг.',
  'ل.ل.',
  'SL Re',
  'Lt',
  'Ls',
  'د.ل.',
  'د.م.',
  'MDL',
  'MGA',
  'MKD',
  'K',
  'MOP$',
  'MURs',
  '$',
  'RM',
  'MTn',
  'N$',
  '₦',
  'C$',
  'kr',
  'नेरू',
  '$',
  'ر.ع.',
  'B/.',
  'S/.',
  '₱',
  '₨',
  'zł',
  '₲',
  'ر.ق.',
  'RON',
  'дин.',
  '₽.',
  'FR',
  'ر.س.',
  'SDG',
  'kr',
  '$',
  'Ssh',
  'ل.س.',
  '฿',
  'د.ت.',
  'T$',
  'TL',
  '$',
  'NT$',
  'TSh',
  '₴',
  'USh',
  '$',
  'UZS',
  'Bs.F.',
  '₫',
  'FCFA',
  'CFA',
  'ر.ي.',
  'R',
  'ZK',
  'ZWL$',
];

export function extractCurrencyValue(strs) {
  let str = strs?.toString();
  if (typeof str !== 'string') {
    console.error('Input is not a string');
    return null;
  }
  for (const symbol of allCurrencySymbols) {
    if (str?.startsWith(symbol)) {
      str = str?.slice(symbol.length).trim();
      break;
    }
  }
  let numericValue = '';
  for (const char of str) {
    if (/\d|,/?.test(char)) {
      numericValue += char;
    } else {
      break;
    }
  }
  numericValue = parseFloat(numericValue.replace(/,/g, ''));
  return numericValue || null;
}

export function sumWithoutCurrency(array) {
  const numericValues = array
    .filter((value) => value !== null && value !== undefined)
    .map((value) => {
      // Remove currency symbols and convert to a numeric value
      const stringValue = value.toString();
      const numericPart = Number(extractCurrencyValue(stringValue));
      return Number.isNaN(numericPart) ? 0 : numericPart;
    });
  return numericValues.reduce((acc, currentValue) => acc + currentValue, 0);
}

// covert file into proper size
export const formatFileSize = (sizeInBytes) => {
  const KB = 1024;
  const MB = KB * KB;

  if (sizeInBytes < KB) {
    return `${sizeInBytes} B`;
  }
  if (sizeInBytes < MB) {
    return `${(sizeInBytes / KB).toFixed(2)} KB`;
  }
  return `${(sizeInBytes / MB).toFixed(2)} MB`;
};

// download s3 URL
export const downloadFile = async (url, fileName, fileExtension) => {
  let downloadUrl;
  if (!url.includes('s3.eu-west-2')) {
    downloadUrl = encodeURI(`https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${url}`);
  } else {
    downloadUrl = url;
  }
  const response = await fetch(downloadUrl);
  const blob = await response.blob();
  const extensionToAppend = fileExtension ? `.${fileExtension}` : '';
  const downloadFileName = `${fileName || 'downloadedFile'}${extensionToAppend}`;

  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = downloadFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Reports whether a value is an absolute http(s) URL, as opposed to a relative storage key.
 * @param {string} str Value to test.
 * @returns {boolean} True for an absolute http or https URL.
 */
export const isAbsoluteHttpUrl = (str) => {
  return typeof str === 'string' && /^https?:\/\//i.test(str.trim());
};

/**
 * Fetches a URL and triggers a browser download of the response body.
 * A non-OK response (expired presigned link, missing S3 key, API error page) resolves to false
 * instead of being saved, since fetch only rejects on network failure and would otherwise write
 * the error body to disk under the expected file name.
 * @param {string} url Absolute URL to fetch.
 * @param {string} fileName Name to save the file as.
 * @returns {Promise<boolean>} True when the download was triggered, false when the URL was unusable.
 */
export const downloadFileFromUrl = async (url, fileName) => {
  if (!isAbsoluteHttpUrl(url)) {
    return false;
  }

  const response = await fetch(url);
  if (!response.ok) {
    return false;
  }

  const blob = await response.blob();
  const fileObjectURL = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = fileObjectURL;
  link.download = fileName || 'downloadedFile';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(fileObjectURL);
  return true;
};

/**
 * Reports whether a file path is a pre-S3 legacy local disk path that can no longer be served.
 * Logs created before the move to S3 stored paths like './static/submit-download/...'. The migration
 * could only rewrite the ones whose file still existed on disk, so the rest kept a path that points at
 * a file which no longer exists anywhere. Valid S3 keys (e.g. 'tops/development-order/...') and
 * absolute http(s) URLs are not legacy and must be allowed through.
 * @param {string} filePath File path from a download log entry.
 * @returns {boolean} True when the path is a legacy local disk path.
 */
const isLegacyLocalPath = (filePath) => {
  if (typeof filePath !== 'string') {
    return false;
  }
  const trimmed = filePath.trim();
  if (!trimmed) {
    return false;
  }
  return trimmed.startsWith('./') || trimmed.startsWith('/') || /^static\//i.test(trimmed);
};

/**
 * Reports whether a download log holds only legacy local file paths.
 * Valid S3-key or presigned-URL logs are allowed through so the backend can build the zip and presign
 * it on download; only logs whose every file is a dead legacy local path are rejected up front.
 * Accepts either a single log or the array of logs the multi-download screens pass.
 * @param {object|Array<object>} log Download log entry, or an array of them.
 * @returns {boolean} True when files are present and every one of them is a legacy path.
 */
export const hasOnlyLegacyFilePaths = (log) => {
  const logs = isArray(log) ? log : [log];
  const paths = logs.flatMap((entry) => (isArray(entry?.files) ? entry.files : [])).map((file) => file?.path);

  if (!paths.length) {
    return false;
  }
  return paths.every((path) => isLegacyLocalPath(path));
};

/**
 * Fetches multiple file URLs, bundles them into a single zip and triggers a browser download.
 * Used when a download log holds files but no server-built zip, so the bundle is assembled client-side.
 * Entries without a usable http(s) path and entries whose fetch returns a non-OK response are skipped
 * rather than failing the whole bundle, so one expired presigned link cannot block the rest.
 * Duplicate file names are suffixed to stop JSZip silently overwriting an earlier entry.
 * @param {Array<{filename: string, path: string}>} files File entries to bundle.
 * @param {string} zipName Name to save the zip as, with or without the .zip extension.
 * @returns {Promise<{downloaded: boolean, zippedCount: number, skippedCount: number}>} Outcome of the bundle.
 */
export const downloadUrlsAsZip = async (files, zipName) => {
  const fileList = isArray(files) ? files : [];
  const usableFiles = fileList.filter((file) => isAbsoluteHttpUrl(file?.path));
  const skippedFromPath = fileList.length - usableFiles.length;

  const fetchedFiles = await Promise.all(
    usableFiles.map(async (file, index) => {
      try {
        const response = await fetch(file.path);
        if (!response.ok) {
          return null;
        }
        return { filename: file.filename || `file-${index + 1}`, blob: await response.blob() };
      } catch (error) {
        console.error('Zip entry download error:', error);
        return null;
      }
    }),
  );

  const availableFiles = fetchedFiles.filter(Boolean);
  const skippedCount = skippedFromPath + (usableFiles.length - availableFiles.length);
  if (!availableFiles.length) {
    return { downloaded: false, zippedCount: 0, skippedCount };
  }

  const zip = new JSZip();
  const usedNames = new Map();
  availableFiles.forEach(({ filename, blob }) => {
    const duplicateCount = usedNames.get(filename) || 0;
    usedNames.set(filename, duplicateCount + 1);
    const extensionIndex = filename.lastIndexOf('.');
    const hasExtension = extensionIndex > 0;
    const baseName = hasExtension ? filename.slice(0, extensionIndex) : filename;
    const extension = hasExtension ? filename.slice(extensionIndex) : '';
    zip.file(duplicateCount ? `${baseName} (${duplicateCount})${extension}` : filename, blob);
  });

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const zipObjectURL = window.URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = zipObjectURL;
  link.download = /\.zip$/i.test(zipName || '') ? zipName : `${zipName || 'Document Pack'}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(zipObjectURL);

  return { downloaded: true, zippedCount: availableFiles.length, skippedCount };
};

export const getControlStyle = (otherConfiguration, alignmentName, colorName, boldName) => {
  let borderStyle;
  if (otherConfiguration?.border) {
    if (otherConfiguration.border_style === 'dotted' || otherConfiguration.border_style === 'dashed') {
      borderStyle = otherConfiguration.border_style;
    } else {
      borderStyle = 'solid';
    }
  } else {
    borderStyle = 'none';
  }

  const borderWidthStyle = otherConfiguration?.border_style === '3px' || otherConfiguration?.border_style === '5px' ? otherConfiguration?.border_style : '1px';
  const borderRightWidth = otherConfiguration?.borderRight ? 0 : borderWidthStyle;
  const borderBottomWidth = otherConfiguration?.borderBottom ? 0 : borderWidthStyle;

  return {
    textAlign: otherConfiguration?.alignment || otherConfiguration?.[alignmentName] || 'left',
    fontWeight: otherConfiguration?.bold || otherConfiguration?.[boldName] ? 'bold' : 'normal',
    fontFamily: otherConfiguration?.bold || otherConfiguration?.[boldName] ? 'Aktiv Grotesk Medium' : 'inherit',
    fontSize: otherConfiguration?.fontSize ? otherConfiguration?.fontSize : 'inherit',
    background: otherConfiguration?.background || 'transparent',
    color: otherConfiguration?.color || otherConfiguration?.[colorName] || 'inherit',
    borderStyle,
    borderColor: otherConfiguration?.border_color || '#848484',
    borderWidth: otherConfiguration?.borderThickness || `${borderWidthStyle} ${borderRightWidth} ${borderBottomWidth} ${borderWidthStyle}`,
    padding: otherConfiguration?.border && '2px',
    textDecoration: otherConfiguration?.underline ? 'underline' : 'none',
  };
};
// check samples colourways has unique object and each object key with filled value
export function areObjectsUniqueAndValid(arr) {
  const seen = new Set();
  if (arr?.length === 1) {
    const obj = arr[0];
    // Check if all keys are empty, null, or undefined
    const allKeysEmptyOrNullUndefined = Object.values(obj).every((value) => value === null || value === undefined || value === '');
    if (allKeysEmptyOrNullUndefined) {
      return allKeysEmptyOrNullUndefined;
    }
    const objKeys = { gradingGroup: 'gradingGroup', size: 'size' };
    for (const key in objKeys) {
      if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
        return false;
      }
    }
  }

  for (const obj of arr) {
    const objKeys = { gradingGroup: 'gradingGroup', size: 'size' };
    for (const key in objKeys) {
      if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
        return false;
      }
    }
    const uniqueKey = `${obj?.size?.name || obj?.size?.value || obj?.size}-${obj?.len?.value || obj?.len?._id}-${obj?.uom?.value || obj?.uom?._id}-${
      obj?.gradingGroup?.value || obj?.gradingGroup?._id
    }-${obj?.quantity}`;
    if (seen.has(uniqueKey)) {
      return false;
    }
    seen.add(uniqueKey);
  }
  return true;
}

export const specNumberRegex = /^[0-9NAna\/\-\+.]+$/;
export const specNumRegex = /^[\+\-]?\d{1,4}(\.\d{1,3})?$/;

const checkQuality = (element) => {
  if (element.bomApprovals.length > 0) {
    const qualityRejctStatus = element.bomApprovals.filter((val) => val.status === 0);
    const approvedStatus = element.bomApprovals.filter((val) => val.status === 1);

    if (approvedStatus.length === element.bomApprovals.length) {
      return true;
    }
    if (qualityRejctStatus.length === element.bomApprovals.length) {
      return false;
    }
    return 'pending';
  }
  return '--';
};

const calculateColourStatus = (element) => {
  let totalColourLength = 0;
  let approvedStatus = 0;
  let rejectStatus = 0;

  element.colourWayRefsData.forEach((item) => {
    if (item.colourWayId && item.colourRefsDataAsPerSuppliers) {
      totalColourLength += item.colourRefsDataAsPerSuppliers.length;
      item.colourRefsDataAsPerSuppliers.forEach((ele) => {
        if (ele.status && ele.status === 1) {
          approvedStatus += 1;
        }
        if (ele.hasOwnProperty('status') && ele.status === 0) {
          rejectStatus += 1;
        }
      });
    }
  });

  return { totalColourLength, approvedStatus, rejectStatus };
};

const determineColourStatus = (totalColourLength, approvedStatus, rejectStatus) => {
  if (approvedStatus === totalColourLength) {
    return true;
  }
  if (rejectStatus === totalColourLength) {
    return false;
  }
  return 'pending';
};

const checkColour = (element) => {
  const { totalColourLength, approvedStatus, rejectStatus } = calculateColourStatus(element);

  if (totalColourLength) {
    return determineColourStatus(totalColourLength, approvedStatus, rejectStatus);
  }
  return '--';
};

export const checkIndividualStatus = (data) => {
  const element = data
    ? {
        ...data,
        colourWayRefsData: data.colourWayRefsData || [],
        bomApprovals: data.bomApprovals || [],
      }
    : {};

  if (!element || !Object.keys(element).length || !element.colourWayRefsData || !element.bomApprovals) {
    return { quality: '--', colour: '--' };
  }

  if (element.colourWayRefsData.length === 0 && element.bomApprovals.length === 0) {
    return { quality: '--', colour: '--' };
  }

  return { quality: checkQuality(element), colour: checkColour(element) };
};
export function isBase64Image(str) {
  return /^data:image\/[a-zA-Z]*;base64,/.test(str);
}

export function renderColumnType(relation) {
  const getLabel = (key) => relation?.[key]?.label || '';
  const getPart = (key, prefix = ' > ') => {
    const label = getLabel(key);
    return label ? `${prefix}${label}` : '';
  };

  const locationLabel = getLabel('location');
  const subLocationLabel = getLabel('subLocation');
  const controlPart = getPart('control');
  const pagesPart = getPart('pages');
  const displayFormatPart = subLocationLabel && getPart('displayFormat');
  const pomPart = subLocationLabel && getPart('pom');
  const locationPlacementPart = getPart('locationPlacement', '>');
  const locationTypePart = getPart('locationType', '>');
  const attributePart = getPart('attribute', '>');
  const subLocationPart = getPart('subLocation', '>');
  const outputPart = getPart('output', '>');
  if (relation?.location?.name === 'custom_module' || relation?.location?.name === 'supplier_templates') {
    let result = '';

    if (pagesPart) {
      const parts = pagesPart.split('>');
      result += parts[1] || '';
    }

    result += controlPart;

    if (relation?.tableColumn) {
      result += ` > ${relation.tableColumn.fieldName}`;
    }

    return result;
  }
  return `${locationLabel} ${locationTypePart} ${locationPlacementPart} ${attributePart} ${
    subLocationPart || outputPart
  } ${pomPart}${displayFormatPart}${pagesPart}${controlPart}`;
}

export function renderSupplierColumnType(relation, values) {
  const getLabel = (key) => relation?.[key]?.key || '';
  const getValue = (key) => relation?.[key]?.value || '';
  const getPart = (key) => {
    const label = getLabel(key);
    const value = getValue(key);

    if (label === 'Time_Picker') {
      return moment(value).format('LLL');
    }
    if (label === 'Status') {
      return value?.colourName;
    }
    if (label === 'Dropdown') {
      return value?.name;
    }
    if (label === 'Multi_Dropdown') {
      return value.map((val) => val.name).join(', ');
    }
    if (values) {
      return values;
    }

    return value ? `${value}` : '';
  };
  const controlPart = getPart('control');

  return `${controlPart}`;
}

export function cellFillColours(colour) {
  const obj = {
    red: 'cell-red',
    amber: 'cell-amber',
    green: 'cell-green',
  };
  return obj[colour] || '';
}

export function splitAndFilterString(string) {
  const pattern = /([+\-/*()%&π²√])/;
  const splitArray = string?.split(pattern);
  return splitArray.filter((str) => str.trim().length > 0);
}

export function handleSubLocation(ele) {
  let subLocationValue = null;
  productListSubLocationsArray?.forEach((subArrEle) => {
    subArrEle.subLocations?.forEach((subEle) => {
      if (subEle.label === ele) {
        const locationValue = productListLocationsArray?.find((val) => val?._id === subArrEle.locationId);
        subLocationValue = { location: locationValue, subLocation: subEle };
      }
    });
  });
  return subLocationValue;
}
export const sortData = (data, key, direction) => {
  if (direction === 'default') return data;

  return data.sort((a, b) => {
    if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
    if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

export const getNewSortDirection = (currentSortConfig, key) => {
  if (currentSortConfig.key === key) {
    if (currentSortConfig.direction === 'asc') return 'desc';
    if (currentSortConfig.direction === 'desc') return 'default';
  }
  return 'asc';
};

export const getSortIcon = (sortConfig, key) => {
  if (sortConfig.key === key) {
    if (sortConfig.direction === 'asc') return <i className="icon-Table-Down font-weight-heavy text-black f-14 ml-1" />;
    if (sortConfig.direction === 'desc') return <i className="icon-Table-Up font-weight-heavy text-black f-14 ml-1" />;
  }
  return <i className="icon-Table-Up font-weight-heavy text-black f-14 ml-1" />;
};

export const supplierLocationsArray = [
  {
    label: 'Supplier Information',
    name: 'Supplier_Information',
    _id: 2,
  },
  {
    label: 'Supplier Template',
    name: 'Supplier_Template',
    _id: 1,
  },
];

export const supplierListSubLocationsArray = [
  {
    locationId: 2,
    subLocations: [
      {
        label: supplierCode,
        name: 'supplierCode',
        type: 'string',
      },
      {
        label: supplierName,
        name: 'supplierName',
        type: 'string',
      },
      {
        label: factoryCode,
        name: 'factoryCode',
        type: 'string',
      },
      {
        label: factoryName,
        name: 'factoryName',
        type: 'string',
      },
      {
        label: 'Port of Lading',
        name: 'portOfLanding',
        type: 'string',
      },
      {
        label: 'MOQ',
        name: 'MOQ',
        type: 'number',
      },
      {
        label: 'MCQ',
        name: 'MCQ',
        type: 'number',
      },
      {
        label: 'Country of Origin',
        name: 'countryOfOrigin',
        type: 'string',
      },
    ],
  },
];
export const getImageProperties = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const blob = await response.blob();

  // Get file type and size
  const fileType = blob.type;
  const fileSize = (blob.size / 1024 / 1024).toFixed(2);
  const fileName = url.substring(url.lastIndexOf('/') + 1, url.indexOf('?') !== -1 ? url.indexOf('?') : url.length);

  // Create an Image object to get the dimensions
  const img = new Image();
  img.src = url;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  // Get dimensions
  const { width } = img;
  const { height } = img;
  return {
    fileType,
    fileSize,
    width,
    height,
    src: url,
    fileName,
  };
};

export const imageUrl = window.location.origin.includes('8015')
  ? `${window.location.origin.replace('8015', '8014')}/assets`
  : `${window.location.origin}/api/assets`;

// Helper function to safely decode URL-encoded strings
export const safeDecodeURIComponent = (str) => {
  if (!str || typeof str !== 'string') {
    return str;
  }

  try {
    // Check if the string contains URL-encoded characters
    if (str.includes('%')) {
      return decodeURIComponent(str);
    }
    return str;
  } catch (error) {
    console.warn('Error decoding URI component:', error.message);
    // Return original string if decoding fails
    return str;
  }
};
// Helper function to check if a string is a base64 image (utility version)
export const isBase64ImageString = (str) => {
  return str && typeof str === 'string' && str.startsWith('data:image/');
};

// Helper function to check if a string needs URL decoding (has % but is not base64)
export const needsUrlDecoding = (str) => {
  return str && typeof str === 'string' && str.includes('%') && !isBase64ImageString(str);
};

/**
 * Resolves an image value from the API into a src usable by an img tag.
 * Absolute URLs are returned untouched: a presigned S3 URL carries percent-encoded characters in its
 * signature (X-Amz-Credential, ResponseContentType), and decoding them turns %2F back into / and makes
 * S3 reject the request as SignatureDoesNotMatch. Only relative storage keys are decoded, which is what
 * the decoding was added for.
 * @param {string} image Absolute URL, data URI, or relative storage key.
 * @returns {string} Image src, falling back to the placeholder when there is no value.
 */
export const getImageFullUrlBasedonPath = (image) => {
  const words = ['/dam/', '/dam_folder/'];

  if (!image || typeof image !== 'string' || !image.trim()) {
    return noImage;
  }
  if (isAbsoluteHttpUrl(image)) {
    return image;
  }

  const decodedImage = needsUrlDecoding(image) ? safeDecodeURIComponent(image) : image;

  if (includes(decodedImage, 'data:image/')) {
    return decodedImage;
  }
  if (isAbsoluteHttpUrl(decodedImage)) {
    return decodedImage;
  }
  if (some(words, (word) => includes(decodedImage, word))) {
    return `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${decodedImage}`;
  }
  return `${imageUrl}/${decodedImage}`;
};

export const formatDuration = (durationInSeconds) => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = Math.floor(durationInSeconds % 60);
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const getVideoProperties = async (url) => {
  // Fetch the video to get the file type and size
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const blob = await response.blob();

  // Get file type and size
  const fileType = blob.type;
  const fileSize = (blob.size / 1024 / 1024).toFixed(2);

  // Create a video element to get the duration and thumbnail
  const video = document.createElement('video');
  video.src = URL.createObjectURL(blob);

  // Create a promise to handle the loaded metadata and thumbnail extraction
  return await new Promise((resolve, reject) => {
    video.onloadedmetadata = () => {
      const { duration } = video;
      // Seek to the beginning of the video to capture the thumbnail
      video.currentTime = Math.min(5, duration * 0.05); // Seek to 5% into the video or 5 seconds, whichever is smaller
      video.onseeked = () => {
        // Create a canvas to draw the video frame
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnailDataUrl = canvas.toDataURL('image/jpeg');
        // Resolve the promise with video properties and thumbnail
        resolve({
          fileType,
          fileSize,
          duration,
          width: video.videoWidth,
          height: video.videoHeight,
          thumbnail: thumbnailDataUrl,
          src: url,
        });
      };
      // Handle seeking errors
      video.onerror = reject;
    };

    // Handle metadata loading errors
    video.onerror = reject;
  });
};

const YOUTUBE_REGEX = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:\S+\/|(?:v|embed)\/|\S*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
const VIMEO_REGEX = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/i;

export const getProgressedVideoProperties = async (url, onProgress) => {
  try {
    const MAX_CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB
    if (YOUTUBE_REGEX.test(url)) {
      const videoId = url.match(YOUTUBE_REGEX)[1];
      return await getYouTubeVideoProperties(videoId, url);
    }
    if (VIMEO_REGEX.test(url)) {
      const vimeoId = url.match(VIMEO_REGEX)[1];
      return await getVimeoVideoProperties(vimeoId, url);
    }
    // First, make a HEAD request to get the full size of the video
    const headResponse = await fetch(url, {
      method: 'HEAD',
    });

    if (!headResponse.ok) {
      throw new Error('Network response was not ok');
    }

    const contentLength = headResponse.headers.get('content-length');

    if (!contentLength) {
      throw new Error('Content-Length response header is missing');
    }

    const totalSize = parseInt(contentLength, 10);
    // Now make a GET request to fetch the first 2 MB
    const response = await fetch(url, {
      headers: {
        Range: `bytes=0-${MAX_CHUNK_SIZE - 1}`,
      },
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    let loaded = 0;

    const reader = response.body.getReader();

    const stream = new ReadableStream({
      start(controller) {
        function push() {
          reader
            .read()
            .then(({ done, value }) => {
              if (done) {
                controller.close();
                return;
              }
              loaded += value.byteLength;
              onProgress((loaded / totalSize) * 100);
              controller.enqueue(value);
              push();
            })
            .catch((error) => {
              console.error(error);
              controller.error(error);
            });
        }
        push();
      },
    });

    const blob = await new Response(stream).blob();
    const fileType = blob.type;
    const fileSize = (totalSize / 1024 / 1024).toFixed(2); // Real size of the video

    const video = document.createElement('video');
    video.src = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
      video.onloadedmetadata = () => {
        const { duration } = video;
        video.currentTime = Math.min(5, duration * 0.05);
        video.onseeked = () => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const context = canvas.getContext('2d');
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailDataUrl = canvas.toDataURL('image/jpeg');
          resolve({
            fileType,
            fileSize,
            duration,
            width: video.videoWidth,
            height: video.videoHeight,
            thumbnail: thumbnailDataUrl,
            src: url,
          });
        };
        video.onerror = reject;
      };
      video.onerror = reject;
    });
  } catch (err) {
    console.log('error', err);
    throw new Error(err);
  }
};

export const filesPropertiesMapping = async (acceptedFiles) => {
  const processFile = (file) => {
    return new Promise((resolve) => {
      const fileSize = (file.size / 1024 / 1024).toFixed(2); // size in MB
      const fileType = file.type;
      const fileName = file.name;

      const reader = new FileReader();

      reader.onload = () => {
        if (fileType.startsWith('image/')) {
          const img = new Image();
          img.src = reader.result;
          img.onload = () => {
            const dimensions = `${img.width}x${img.height}`;
            resolve({
              size: fileSize,
              dimensions,
              fileType,
              base64Url: reader.result,
              fileName,
            });
          };
        } else if (fileType.startsWith('video/')) {
          const videoUrl = URL.createObjectURL(file);
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.src = videoUrl;
          video.onloadedmetadata = async () => {
            const { duration } = video;
            const thumbnail = await extractThumbnail(video);
            resolve({
              size: fileSize,
              duration,
              fileType,
              thumbnail,
              base64Url: reader.result,
              fileName,
            });
          };
        }
      };

      reader.readAsDataURL(file);
    });
  };

  const extractThumbnail = (video) => {
    return new Promise((resolve) => {
      // Otherwise, seek to 0.5% of the video duration
      video.currentTime = 5;
      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnail = canvas.toDataURL('image/jpeg');
        resolve(thumbnail);
      };
    });
  };
  return Promise.all(acceptedFiles.map(processFile));
};

export const getYouTubeVideoProperties = async (videoId, url) => {
  try {
    if (!videoId) {
      throw new Error('Invalid YouTube URL');
    }
    const response = await fetchYoutubeMetadata(videoId);
    if (response.code === 200) {
      const data = await response.data;
      const videoDetails = data.items[0];
      if (!videoDetails) {
        throw new Error('Link is not valid');
      }
      const { snippet, contentDetails } = videoDetails;
      const duration = parseISO8601Duration(contentDetails.duration);
      const thumbnail = snippet.thumbnails.high.url;
      return {
        fileType: 'video/mp4', // YouTube videos are usually in MP4 format
        fileSize: 'N/A', // Cannot be determined from the API
        duration,
        width: snippet.thumbnails.high.width,
        height: snippet.thumbnails.high.height,
        thumbnail,
        src: url,
        fileName: snippet.title,
      };
    }
    if (response.code === 500) {
      throw new Error('Link is not valid');
    }
  } catch (err) {
    console.log('getYouTubeVideoProperties', err);
    throw new Error('Link is not valid');
  }
};

const parseISO8601Duration = (duration) => {
  const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
  if (!match) return 0; // Default to 0 if the string is invalid
  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);
  return hours * 3600 + minutes * 60 + seconds;
};

export const extractS3KeyName = (url, moduleName, devOrderId) => {
  const start = url?.lastIndexOf('/') + 1;
  const end = url?.indexOf('?'); // Exclude query parameters
  const keyName = url?.substring(start, end);
  return `${s3Config({ devOrderId })[moduleName]}${keyName}`;
};
export const getSlugName = () => window.location.hostname?.split('.')?.[0];

export const fileTypeOptions = [
  {
    label: 'JPEG',
    value: 'image/jpeg',
  },
  {
    label: 'PNG',
    value: 'image/png',
  },
  {
    label: 'WEBP',
    value: 'image/webp',
  },
  {
    label: 'MP4',
    value: 'video/mp4',
  },
  {
    label: 'WEBM',
    value: 'video/webm',
  },
  {
    label: 'MOV',
    value: 'video/mov',
  },
];

export const videoLinkTypeOptions = [
  { label: 'Vimeo', value: 'vimeo' },
  { label: 'Youtube', value: 'youtube' },
  { label: 'Self hosted', value: 'selfHosted' },
];

export const getVimeoVideoProperties = async (videoId, url) => {
  try {
    if (!videoId) {
      throw new Error('Invalid Vimeo URL');
    }
    const response = await fetchVimeoMetaData(videoId);
    if (response.code === 200) {
      const data = await response.data;
      // Extract necessary metadata
      const { name: videoTitle, width, height } = data;
      const thumbnail = data.pictures.sizes.find((size) => size.width === 640)?.link || '';
      return {
        fileType: 'video/mp4', // Vimeo videos are typically in MP4 format
        fileSize: 'N/A', // Cannot be determined from the API
        duration: data.duration,
        width,
        height,
        thumbnail,
        src: url,
        fileName: videoTitle,
      };
    }
    if (response.code === 500) {
      throw new Error('Link is not valid');
    }
  } catch (error) {
    console.error('Error fetching Vimeo video data:', error);
    throw new Error('Link is not valid');
  }
};
export const calculationProductListSubLocationsArray = [
  {
    locationId: 1,
    subLocations: [
      {
        label: gradingGroup,
        name: 'gradingGroup',
        type: 'string',
      },
      {
        label: 'Colour Group',
        name: 'colourGroup',
        type: 'string',
      },
      {
        label: colourRef,
        name: 'colourRef',
        type: 'string',
      },
      {
        label: 'Colour Description',
        name: 'colourDesc',
        type: 'string',
      },
    ],
  },
  {
    locationId: 2,
    subLocations: [
      {
        label: supplierCode,
        name: 'supplierCode',
        type: 'string',
      },
      {
        label: supplierName,
        name: 'supplierName',
        type: 'string',
      },
      {
        label: factoryCode,
        name: 'factoryCode',
        type: 'string',
      },
      {
        label: factoryName,
        name: 'factoryName',
        type: 'string',
      },
      {
        label: 'Port of Lading',
        name: 'portOfLanding',
        type: 'string',
      },
      {
        label: 'MOQ',
        name: 'MOQ',
        type: 'number',
      },
      {
        label: 'MCQ',
        name: 'MCQ',
        type: 'number',
      },
      {
        label: 'Country of Origin',
        name: 'countryOfOrigin',
        type: 'string',
      },
      {
        label: 'Document Type',
        name: 'documentType',
        type: 'string',
      },
      {
        label: 'Document Name',
        name: 'documentName',
        type: 'string',
      },
      {
        label: 'Date Added',
        name: 'dateAdded',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Expiry Date',
        name: 'expiryDate',
        type: 'string',
      },
      {
        label: 'Assignee',
        name: 'assignee',
        type: 'string',
      },
    ],
  },
  {
    locationId: 3,
    subLocations: [
      {
        label: 'BOM Type',
        name: 'bomType',
        type: 'string',
      },
      {
        label: 'Code',
        name: 'code',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Component Supplier',
        name: 'componentSupplier',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Weight',
        name: 'weight',
        type: 'number',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Composition',
        name: 'composition',
        type: 'string',
      },

      {
        label: 'Placement',
        name: 'placement',
        type: 'string',
      },
      {
        label: 'Finish',
        name: 'finish',
        type: 'string',
      },
      {
        label: additionalInformation,
        name: 'additionalInformation',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: colourRef,
        name: 'colourRef',
        type: 'string',
      },
      {
        label: 'Colour Supporting Doc',
        name: 'colourSupportingDoc',
        type: 'number',
      },
      {
        label: 'Colour Status',
        name: 'colourStatus',
        type: 'string',
      },
      {
        label: 'Colour Comments',
        name: 'comments',
        type: 'string',
      },
      {
        label: 'Quality Supporting Doc',
        name: 'qualitySupportingDoc',
        type: 'number',
      },
      {
        label: 'Quality Status',
        name: 'qualityStatus',
        type: 'string',
      },
      {
        label: 'Quality Comments',
        name: 'qualityComments',
        type: 'string',
      },
    ],
  },
  {
    locationId: 4,
    subLocations: [
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Weight',
        name: 'weight',
        type: 'number',
      },
      {
        label: 'Volume',
        name: 'volume',
        type: 'number',
      },
      {
        label: 'No. of Supporting Docs',
        name: 'specificationSupportingDocuments',
        type: 'number',
      },
    ],
  },
  {
    locationId: 5,
    subLocations: [
      {
        label: 'Code',
        name: 'code',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Type',
        name: 'type',
        type: 'string',
      },
      {
        label: 'Minimum Pass Rate',
        name: 'minimumPassRate',
        type: 'string',
      },
      {
        label: 'Component',
        name: 'component',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Supplier',
        name: 'supplier',
        type: 'string',
      },
      {
        label: additionalInformation,
        name: 'additionalInformation',
        type: 'string',
      },
      {
        label: 'Result',
        name: 'result',
        type: 'string',
      },
      {
        label: 'Test Price',
        name: 'testPrice',
        type: 'number',
      },
      {
        label: 'Status Value',
        name: 'statusValue',
        type: 'string',
      },
      {
        label: 'Status Date',
        name: 'statusDate',
        type: 'string',
      },
      {
        label: 'Test Report Added Date',
        name: 'testReportAddedDate',
        type: 'string',
      },
      {
        label: 'Test Report Name',
        name: 'testReportName',
        type: 'string',
      },
      {
        label: 'Test Report Expiry Date',
        name: 'testReportExpiryDate',
        type: 'string',
      },
      {
        label: 'Comments',
        name: 'comments',
        type: 'string',
      },
    ],
  },
  {
    locationId: 6,
    subLocations: [
      {
        label: 'Code',
        name: 'code',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Care Type',
        name: 'careType',
        type: 'string',
      },
    ],
  },
  {
    locationId: 8,
    subLocations: [
      {
        label: 'Ref',
        name: 'ref',
        type: 'string',
      },
      {
        label: 'Type',
        name: 'type',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Size/Size Range',
        name: 'sizeRange',
        type: 'string',
      },
      {
        label: 'Due Date',
        name: 'dueDate',
        type: 'date',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: markAsComplete,
        name: 'markAsComplete',
        type: 'string',
      },
    ],
  },
  {
    locationId: 9,
    subLocations: [
      {
        label: 'Reference',
        name: 'reference',
        type: 'string',
      },
      {
        label: 'Template',
        name: 'template',
        type: 'string',
      },
      {
        label: 'Costing Type',
        name: 'costingType',
        type: 'string',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Landed Currency',
        name: 'landedCurrency',
        type: 'string',
      },
      {
        label: 'Incoterm',
        name: 'incoterm',
        type: 'string',
      },
      {
        label: 'Price Per Unit (Incoterm)',
        name: 'pricePerUnitIncoterm',
        type: 'number',
      },
      {
        label: 'Exchange Rate',
        name: 'exchangeRate',
        type: 'number',
      },
      {
        label: 'Price Per Unit (converted)',
        name: 'pricePerUnitIncotermConverted',
        type: 'number',
      },
      {
        label: 'Incoterm Total',
        name: 'incotermTotal',
        type: 'number',
      },
      {
        label: 'Target Cost',
        name: 'targetCost',
        type: 'number',
      },
      {
        label: 'Sample Ref',
        name: 'sampleRef',
        type: 'string',
      },
      {
        label: 'Shipment Method',
        name: 'shipmentMethod',
        type: 'string',
      },
    ],
  },

  {
    locationId: 10,
    subLocations: [
      {
        label: 'PO Number',
        name: 'poNumber',
        type: 'string',
      },
      {
        label: 'Colourway',
        name: 'colourway',
        type: 'string',
      },
      {
        label: 'Size',
        name: 'size',
        type: 'string',
      },
      {
        label: 'Length',
        name: 'length',
        type: 'number',
      },
      {
        label: 'Quantity',
        name: 'quantity',
        type: 'number',
      },
      {
        label: 'Creation Date',
        name: 'creationDate',
        type: 'string',
      },
      {
        label: 'Sent Date',
        name: 'sentDate',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'Product Approve by Date (Seal by Date)',
        name: 'productApprovebyDate',
        type: 'string',
      },
      {
        label: 'Product Approved Date (Sealed Date)',
        name: 'productApprovedDate',
        type: 'string',
      },
      {
        label: HandoverCargoReadyDate,
        name: 'handoverCargoReadyDate',
        type: 'string',
      },

      {
        label: ActualHandoverCargoReadyDate,
        name: 'actualHandoverCargoReadyDate',
        type: 'string',
      },
      {
        label: ShipmentDate,
        name: 'shipmentDate',
        type: 'string',
      },
      {
        label: actualShipmentDate,
        name: 'actualShipmentDate',
        type: 'string',
      },
      {
        label: deliveryDate,
        name: 'deliveryDate',
        type: 'string',
      },
      {
        label: actualDeliveryDate,
        name: 'actualDeliveryDate',
        type: 'string',
      },
      {
        label: shipmentMethod,
        name: 'shipmentMethod',
        type: 'string',
      },
      {
        label: 'Incoterms',
        name: 'incoterms',
        type: 'string',
      },
      {
        label: 'Shipping & Handling Cost Included?',
        name: 'shippingHandlingCostIncluded',
        type: 'string',
      },
      {
        label: 'Shipping and Handling Cost',
        name: 'shippingandHandlingCost',
        type: 'number',
      },
      {
        label: 'Port of Landing',
        name: 'portofLanding',
        type: 'string',
      },
      {
        label: 'Number of Revisions',
        name: 'numberofRevisions',
        type: 'number',
      },
    ],
  },
  {
    locationId: 11,
    subLocations: [
      {
        label: 'Brand',
        name: 'brand',
        type: 'string',
      },
      {
        label: 'Brand - Code',
        name: 'brandCode',
        type: 'string',
      },
      {
        label: 'Sub Brand',
        name: 'subBrand',
        type: 'string',
      },
      {
        label: 'Sub Brand - Code',
        name: 'subBrandCode',
        type: 'string',
      },
      {
        label: productNumber,
        name: 'number',
        type: 'string',
      },
      {
        label: 'Description',
        name: 'description',
        type: 'string',
      },
      {
        label: 'Season',
        name: 'season',
        type: 'string',
      },
      {
        label: 'Season - Code',
        name: 'seasonCode',
        type: 'string',
      },
      {
        label: 'Month',
        name: 'month',
        type: 'string',
      },
      {
        label: 'Year',
        name: 'year',
        type: 'string',
      },
      {
        label: 'Status',
        name: 'status',
        type: 'string',
      },
      {
        label: 'ProductType',
        name: 'productType',
        type: 'string',
      },
      {
        label: 'Product Type - Code',
        name: 'productTypeCode',
        type: 'string',
      },
      {
        label: category1,
        name: 'cat1',
        type: 'string',
      },
      {
        label: category2,
        name: 'cat2',
        type: 'string',
      },
      {
        label: category3,
        name: 'cat3',
        type: 'string',
      },
      {
        label: category4,
        name: 'cat4',
        type: 'string',
      },
      {
        label: 'Category 1 - Code',
        name: 'cat1Code',
        type: 'string',
      },
      {
        label: 'Category 2 - Code',
        name: 'cat2Code',
        type: 'string',
      },
      {
        label: 'Category 3 - Code',
        name: 'cat3Code',
        type: 'string',
      },
      {
        label: 'Category 4 - Code',
        name: 'cat4Code',
        type: 'string',
      },
      {
        label: 'Size Range Name',
        name: 'sizeRangeName',
        type: 'string',
      },
      {
        label: 'Size Range Sizes',
        name: 'sizeRangeSizes',
        type: 'string',
      },
      {
        label: 'Supplier',
        name: 'supplier',
        type: 'string',
      },

      {
        label: colourwayName,
        name: 'colourwayName',
        type: 'string',
      },
      {
        label: 'No. of Colourways',
        name: 'noOfColourways',
        type: 'number',
      },
      {
        label: 'Original Submission',
        name: 'originalSubmission',
        type: 'string',
      },
      {
        label: 'Original Download',
        name: 'originalDownload',
        type: 'string',
      },
      {
        label: 'Most Recent Submission',
        name: 'mostRecentSubmission',
        type: 'string',
      },
      {
        label: 'Most Recent Download',
        name: 'mostRecentDownload',
        type: 'string',
      },
      {
        label: 'Licensor',
        name: 'licensor',
        type: 'string',
      },
      {
        label: 'Licensor - Code',
        name: 'licensorCode',
        type: 'string',
      },
      {
        label: 'License',
        name: 'license',
        type: 'string',
      },
      {
        label: 'License - Code',
        name: 'licenseCode',
        type: 'string',
      },
      {
        label: 'Properties',
        name: 'likeness',
        type: 'string',
      },
      {
        label: 'Transport',
        name: 'transport',
        type: 'string',
      },
    ],
  },
];

export const assetType = [
  { value: 'image', label: 'Image' },
  { value: 'imageLink', label: 'Image Link' },
  { value: 'video', label: 'Video' },
  { value: 'videoLink', label: 'Video Link' },
];

export const damSortOpions = [
  { label: 'Size (Ascending)', sortBy: 'size', sortOrder: 1 },
  { label: 'Size (Descending)', sortBy: 'size', sortOrder: -1 },

  { label: 'Asset Title (Ascending)', sortBy: 'assetTitle', sortOrder: 1 },
  { label: 'Asset Title (Descending)', sortBy: 'assetTitle', sortOrder: -1 },

  { label: 'CreatedAt (Ascending)', sortBy: 'createdAt', sortOrder: 1 },
  { label: 'CreatedAt (Descending)', sortBy: 'createdAt', sortOrder: -1 },

  { label: 'ModifiedAt (Ascending)', sortBy: 'updatedAt', sortOrder: 1 },
  { label: 'ModifiedAt (Descending)', sortBy: 'updatedAt', sortOrder: -1 },
];

export const optionsEnergy = [
  { label: 'National Grid Mix', value: 'National_Grid_Mix', type: 'id' },
  { label: 'Renewable Energy', value: 'Renewable_Energy', type: 'id' },
];
export function extractAndSortHeadings(pages) {
  const headings = [];

  // Iterate through each page
  pages.forEach((elePage) => {
    // Filter the page data to find entries with key "Heading"
    const pageHeadings = elePage?.grid?.filter((item) => item?.control?.key === 'Heading' && item?.control?.supplierVisible === true);

    if (pageHeadings?.length > 0) {
      // Sort the headings based on the 'y' property
      pageHeadings.sort((a, b) => a.y - b.y);
      // Push the headings to the headings array
      headings.push(...pageHeadings);
    }
  });

  // Sort the headings based on headingStyle

  return headings;
}
const platforms = ['ytimg.com', 'vimeocdn.com'];

export function includesAnyVideoUrlLink(url) {
  return platforms?.some((substring) => url?.includes(substring));
}

export const isS3PresignedUrl = (url) => {
  const s3UrlPattern = /^https:\/\/[\w\-]+\.s3\.[\w\-]+\.amazonaws\.com\/.+/;
  return s3UrlPattern.test(url);
};
export const urlToBase64 = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export function areArraysSimilar(arrays) {
  // Filter out empty arrays
  const nonEmptyArrays = arrays.filter((array) => array.length > 0);

  // If there are no non-empty arrays, return true
  if (nonEmptyArrays.length === 0) {
    return true;
  }

  // Create a set from the first non-empty array as the reference
  const referenceSet = new Set(nonEmptyArrays[0]);

  // Check if there is at least one common element in all non-empty arrays
  return nonEmptyArrays.every((array) => array.some((value) => referenceSet.has(value)));
}

export const filePreview = (element) => {
  return element ? encodeURI(`https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${element}`) : '';
};
export const transportArray = [
  {
    label: 'Aircraft - Average haul',
    name: 'aircraft_average_haul',
    _id: 1,
  },
  {
    label: 'Aircraft - Long haul (>4000 km)',
    name: 'aircraft_long_haul_4000_km)',
    _id: 2,
  },
  {
    label: 'Aircraft - Medium haul (1500-4000 km)',
    name: 'aircraft_medium_haul_1500_4000_km',
    _id: 3,
  },
  {
    label: 'Aircraft - Short haul (800-1500 km)',
    name: 'aircraft_short_haul_800_1500_km',
    _id: 4,
  },
  {
    label: 'Ship - Bulk Carrier',
    name: 'ship_bulk_carrier',
    _id: 5,
  },
  {
    label: 'Ship - Container Ship',
    name: 'ship_container_ship',
    _id: 6,
  },
  {
    label: 'Ship - Large',
    name: 'ship_large',
    _id: 7,
  },
  {
    label: 'Ship - Ocean',
    name: 'ship_ocean',
    _id: 8,
  },
  {
    label: 'Ship - Regional',
    name: 'ship_regional',
    _id: 9,
  },
  {
    label: 'Train - Medium Diesel',
    name: 'train_medium_diesel',
    _id: 10,
  },
  {
    label: 'Train - Medium Electric',
    name: 'train_medium_electric',
    _id: 11,
  },
  {
    label: 'Train - Average',
    name: 'train_average',
    _id: 12,
  },
  {
    label: 'Rigid Truck - 16-32 t gross vehicle weight',
    name: 'rigid_truck_16_32_t_gross_vehicle_weight',
    _id: 13,
  },
  {
    label: 'Rigid Truck - 7.5-16 t gross vehicle weight',
    name: 'rigid_truck_7.5_16_t_gross_vehicle_weight',
    _id: 14,
  },
  {
    label: 'Rigid Truck - >32 t gross vehicle weight',
    name: 'rigid_truck_32_t_gross_vehicle_weight',
    _id: 15,
  },
  {
    label: 'Truck - Average Size',
    name: 'truck -_average_size',
    _id: 16,
  },
  {
    label: 'Truck - With trailer 28-34 t gross vehicle weight',
    name: 'truck_with_trailer_28_34_t_gross_vehicle_weight',
    _id: 17,
  },
  {
    label: 'Truck - With trailer 50-60 t gross vehicle weight',
    name: 'truck_with_trailer_50_60_t_gross_vehicle_weight',
    _id: 18,
  },
  {
    label: 'Van',
    name: 'van',
    _id: 19,
  },
  {
    label: "I don't know",
    name: 'i_dont_know',
    _id: 20,
  },
];

export const QualityArray = [
  {
    label: 'Average',
    name: 'average',
    _id: 1,
  },
  {
    label: 'EU',
    name: 'eu',
    _id: 2,
  },
  {
    label: 'Japan',
    name: 'japan',
    _id: 3,
  },
  {
    label: 'Unknown',
    name: 'unknown',
    _id: 4,
  },
  {
    label: 'Recycled 45%',
    name: 'recycled_45',
    _id: 5,
  },
  {
    label: 'Recycled Average',
    name: 'recycled_average',
    _id: 6,
  },
  {
    label: '100% Virgin Materials',
    name: '100_virgin_materials',
    _id: 7,
  },
  {
    label: 'Recycled 100% Average',
    name: 'recycled_100_average',
    _id: 8,
  },
  {
    label: '100% Recycled Paper',
    name: '100_recycled_paper',
    _id: 9,
  },
  {
    label: 'Virgin',
    name: 'virgin',
    _id: 10,
  },
  {
    label: 'Recycled 100% US',
    name: 'recycled_100_US',
    _id: 11,
  },
  {
    label: 'Recycled 50% US',
    name: 'recycled_50_US',
    _id: 12,
  },
  {
    label: 'Natural Rubber',
    name: 'natural_rubber',
    _id: 13,
  },
  {
    label: 'Synthetic Rubber',
    name: 'synthetic_rubber',
    _id: 14,
  },
  {
    label: 'Conventional',
    name: 'conventional',
    _id: 15,
  },
  {
    label: 'Recycled',
    name: 'recycled',
    _id: 16,
  },
  {
    label: 'Organic',
    name: 'organic',
    _id: 17,
  },
  {
    label: 'India',
    name: 'india',
    _id: 18,
  },
  {
    label: 'Bangladesh',
    name: 'bangladesh',
    _id: 19,
  },
  {
    label: 'Organic Average',
    name: 'organic_average',
    _id: 20,
  },
  {
    label: 'Hand Made',
    name: 'hand_made',
    _id: 21,
  },
  {
    label: 'Recycled Ocean',
    name: 'recycled_ocean',
    _id: 22,
  },
];

export const supplierToDO = [
  {
    id: 1,
    image: '',
    product_ref: '123',
    supplier: 'abc23',
    completion: '90%',
    location: 'gujarat',
    field: 'field',
    action: 'action',
    isExpanded: false,
    colourWays: ['red', 'blue'],
  },
  {
    id: 2,
    image: '',
    product_ref: '23',
    supplier: 'tyh45',
    completion: '30%',
    location: 'gurugram',
    field: 'field',
    action: 'action',
    isExpanded: false,
    colourWays: ['red', 'blue'],
  },
  {
    id: 3,
    image: '',
    product_ref: '87',
    supplier: 'omn66',
    completion: '45%',
    location: 'delhi',
    field: 'field',
    action: 'action',
    isExpanded: false,
    colourWays: ['red', 'blue'],
  },
];

export const bomConfigData = [
  { id: 1, label: 'Component Description', value: 'bomDescription' },
  { id: 2, label: 'Size', value: 'size' },
  { id: 3, label: 'Weight', value: 'weight' },
  { id: 4, label: 'Quantity', value: 'quantity' },
  { id: 5, label: 'Finish', value: 'finished' },
  { id: 7, label: 'Composition', value: 'composition' },
  { id: 8, label: 'Additional Information', value: 'additionalInfo' },
  { id: 9, label: 'Transport', value: 'transport' },
  { id: 10, label: 'Component Sub Type', value: 'bomComponentSubType' },
  { id: 11, label: 'Quality', value: 'quality' },
];

export const setImage = (img) => {
  if (!img) return noImage;
  if (img.includes('data:image') || img.startsWith('http') || img.includes('static')) {
    return img;
  }
  if (!img.startsWith('http')) {
    if (img.includes('Tier_') || img.includes('Transport_Icon')) {
      return `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${process.env.REACT_APP_COMMON_RESOURCE}/${img}`;
    }
    return `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${img}`;
  }

  return noImage;
};

const replaceTheString = (str, path) => {
  return str?.replace(path, '');
};
export const checkImageExistInCanvasData = async (canvasData, prevCanvasData, isEditModeOn) => {
  let s3ImageToUpload = [];
  let s3SectionImageToUpload = [];
  for (const [i, can] of canvasData.entries()) {
    const imagePyaload = commonS3DppImagePayload(can, i, isEditModeOn, prevCanvasData, false, false);
    if (can?.controls?.length > 0 && can.key === 'custom_section') {
      const cloneControlsPreviousData = cloneDeep(prevCanvasData?.[i]?.controls);
      for (const [ind, con] of can?.controls?.entries() || {}) {
        const sectionImagePayload = commonS3DppImagePayload(con, ind, isEditModeOn, cloneControlsPreviousData, true, false);
        if (sectionImagePayload?.length) {
          s3SectionImageToUpload = concat(s3SectionImageToUpload, sectionImagePayload);
        }
      }
    }
    if (imagePyaload?.length) {
      s3ImageToUpload = concat(s3ImageToUpload, imagePyaload);
    }
  }
  s3ImageToUpload = flatMap(s3ImageToUpload);
  s3SectionImageToUpload = flatMap(s3SectionImageToUpload);
  return { s3ImageToUpload, s3SectionImageToUpload };
};

function generateUniqueId() {
  const randomBytes = CryptoJS.lib.WordArray.random(16);
  return randomBytes.toString(CryptoJS.enc.Hex);
}
export const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const generateColSizeLengthCombinations = async (sizeRanges, legLengths, colourLibraries) => {
  // Default each array to an empty array if not provided
  if (sizeRanges.length === 0 && legLengths.length === 0 && colourLibraries.length === 0) {
    return [];
  }
  // Generate combinations using a Cartesian product approach
  const combinations = [];
  if (sizeRanges.length > 0 && legLengths.length > 0 && colourLibraries.length > 0) {
    sizeRanges.forEach((size) => {
      legLengths.forEach((length) => {
        colourLibraries.forEach((colour) => {
          combinations.push({
            size,
            length,
            colour,
          });
        });
      });
    });
  } else if (sizeRanges.length > 0 && legLengths.length > 0) {
    sizeRanges.forEach((size) => {
      legLengths.forEach((length) => {
        combinations.push({
          size,
          length,
          colour: '',
        });
      });
    });
  } else if (sizeRanges.length > 0 && colourLibraries.length > 0) {
    sizeRanges.forEach((size) => {
      colourLibraries.forEach((colour) => {
        combinations.push({
          size,
          colour,
          length: '',
        });
      });
    });
  } else if (legLengths.length > 0 && colourLibraries.length > 0) {
    legLengths.forEach((length) => {
      colourLibraries.forEach((colour) => {
        combinations.push({
          length: length._id,
          colour: colour._id,
          size: '',
        });
      });
    });
  } else if (legLengths.length > 0) {
    legLengths.forEach((length) => {
      combinations.push({
        length: length._id,
        colour: '',
        size: '',
      });
    });
  } else if (colourLibraries.length > 0) {
    colourLibraries.forEach((colour) => {
      combinations.push({
        colour: colour?._id || colour,
        size: '',
        length: '',
      });
    });
  } else if (sizeRanges.length > 0) {
    sizeRanges.forEach((size) => {
      combinations.push({
        size,
        colour: '',
        length: '',
      });
    });
  }
  return combinations;
};

// care label manual items
export const careManualTypeList = [
  {
    type: shortTextBox,
    name: 'Short_Text',
    key: 'Textbox',
  },
  {
    type: singleSelectDropdown,
    name: 'Single_select_Dropdown',
    key: 'Dropdown',
  },
  {
    type: multiSelectDropdown,
    name: 'Multi_select_Dropdown',
    key: 'Multi_select_Dropdown',
  },
  {
    type: radioButton,
    name: 'Radio_button',
    key: 'Radio_button',
  },
  {
    type: 'Status',
    name: 'Status',
    key: 'Status',
  },
  {
    type: numberDecimal,
    name: 'Number',
    key: 'Number_Decimal',
    subType: 'decimal',
  },
  {
    type: numberPercentage,
    name: 'Number_percentage',
    key: 'Number_percentage',
  },
  {
    type: 'Image',
    name: 'Image',
    key: 'Image',
  },
  {
    type: DatePicker,
    name: 'Date_picker',
    key: 'Date_picker',
  },
];
export const checkImageExistInCanvasDataforProductDPP = async (canvasData, prevCanvasData, isEditModeOn) => {
  let s3ImageToUpload = [];
  let s3SectionImageToUpload = [];

  for (const [i, can] of canvasData.entries()) {
    const imagePyaload = commonS3DppImagePayload(can, i, isEditModeOn, prevCanvasData, false, true);
    if (can?.controls?.length > 0 && can.key === 'custom_section') {
      const cloneControlsPreviousData = cloneDeep(prevCanvasData?.[i]?.controls);
      for (const [ind, con] of can?.controls?.entries() || {}) {
        const sectionImagePayload = commonS3DppImagePayload(con, ind, isEditModeOn, cloneControlsPreviousData, true, true);
        if (sectionImagePayload?.length) {
          s3SectionImageToUpload = concat(s3SectionImageToUpload, sectionImagePayload);
        }
      }
    }
    if (imagePyaload?.length) {
      s3ImageToUpload = concat(s3ImageToUpload, imagePyaload);
    }
  }
  s3ImageToUpload = flatMap(s3ImageToUpload);
  s3SectionImageToUpload = flatMap(s3SectionImageToUpload);
  return { s3ImageToUpload, s3SectionImageToUpload };
};

export const openLinkInNewTab = (url) => {
  const newWindow = window.open(url, '_blank');
  // Fallback in case window.open fails (e.g., if blocked by a popup blocker)
  if (newWindow) {
    newWindow.focus();
  }
};

export const pickKeys = (obj, keys) => {
  return keys.reduce((result, key) => {
    const keyParts = key.split('.');
    let current = obj;

    for (const part of keyParts) {
      if (current?.hasOwnProperty(part)) {
        current = current[part];
      } else {
        current = undefined;
        break;
      }
    }

    if (current !== undefined) {
      result[key] = current;
    }

    return result;
  }, {});
};
// convert dotted object to normal object
export function convertDottedToObject(dottedObj) {
  const result = {};

  Object.keys(dottedObj).forEach((key) => {
    const keys = key.split('.'); // Split the dotted key into parts
    let current = result;

    // Iterate over each part and build the nested structure
    keys.forEach((part, index) => {
      // If it's the last part, assign the value
      if (index === keys.length - 1) {
        current[part] = dottedObj[key];
      } else {
        // If the key doesn't exist, initialize it as an empty object
        current[part] = current[part] || {};
      }
      current = current[part]; // Move deeper into the object
    });
  });

  return result;
}

export const AllNavigationOptions = [
  {
    label: 'Product Impact',
    displayLabel: 'impact',
    value: 'product_impact',
    defaultIcon: productImpact,
    id: 'product-impact-id',
  },
  {
    label: 'Product Journey',
    displayLabel: 'journey',
    value: 'product_journey',
    defaultIcon: productJourney,
    id: 'product-journey-id',
  },
  {
    label: 'Care/User Instructions',
    displayLabel: 'instructions',
    value: 'care_user_instructions',
    defaultIcon: careLabelInstruction,
    id: 'care-user-instructions-id',
  },
  // {
  //   label: "Finer Details",
  //   displayLabel: "finer details",
  //   value: "finer_details",
  //   defaultIcon: finerDetails,
  //   id: "finer-details-id",
  // },
];

export function CustomOption(props) {
  return (
    <components.Option {...props}>
      <div className="cursor-pointer" title={props.data.title}>
        {props.data.label}
      </div>
    </components.Option>
  );
}

const handleFromCustomSection = (isFromCustomSection, trueStatement, falseStatement) => {
  return isFromCustomSection ? trueStatement : falseStatement;
};

const commonS3DppImagePayload = (can, i, isEditModeOn, prevCanvasData, isFromCustomSection, isFromProduct) => {
  const S3ImagePayload = [];
  const customSection = 'custom_section';
  switch (can?.key) {
    case 'testing_control':
      if (can?.testGroup?.length > 0) {
        can?.testGroup?.forEach((teGr, grIn) => {
          if (teGr?.testIconImage && isBase64Image(teGr?.testIconImage)) {
            let dppCurrentImagePath = '';
            const imagPayload = {};

            if (isEditModeOn && !isFromProduct) {
              dppCurrentImagePath = prevCanvasData?.[i]?.testGroup?.[grIn]?.testIconImage;
              if (dppCurrentImagePath) {
                dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                imagPayload.isToReplace = true;
              }
            }
            if (isEditModeOn && isFromProduct && prevCanvasData?.[i]?.testGroup?.[grIn]?.testIconImage?.includes('product_image')) {
              dppCurrentImagePath = prevCanvasData?.[i]?.testGroup?.[grIn]?.testIconImage;
              if (dppCurrentImagePath) {
                dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                imagPayload.isToReplace = true;
              }
            }
            let documentName = '';

            if (isFromProduct) {
              if (isEditModeOn && dppCurrentImagePath) {
                documentName = dppCurrentImagePath;
              } else if (isFromCustomSection) {
                documentName = `${can?.key}_${i}_${customSection}_testgroup_${grIn}_product_image_${generateUniqueId()}`;
              } else {
                documentName = `${can?.key}_${i}_testgroup_${grIn}_product_image_${generateUniqueId()}`;
              }
            } else if (isEditModeOn && dppCurrentImagePath) {
              documentName = dppCurrentImagePath;
            } else if (isFromCustomSection) {
              documentName = `${can?.key}_${i}_${customSection}_testgroup_${grIn}_image_${generateUniqueId()}`;
            } else {
              documentName = `${can?.key}_${i}_testgroup_${grIn}_image_${generateUniqueId()}`;
            }
            imagPayload.document = teGr?.testIconImage;
            imagPayload.documentName = documentName;
            imagPayload.canvasIndex = i;
            imagPayload.testGroupIndex = grIn;
            imagPayload.keyName = 'testIconImage';
            imagPayload.controlKey = can?.key;
            S3ImagePayload.push(imagPayload);
          }
        });
      }
      break;
    case 'product_journey':
      if (can?.tier?.length > 0) {
        can?.tier?.forEach((tierData, tierIndex) => {
          if (tierData?.tierIcon && isBase64Image(tierData?.tierIcon)) {
            let dppCurrentImagePath = '';
            const imagPayload = {};
            if (isEditModeOn && !isFromProduct) {
              dppCurrentImagePath = prevCanvasData?.[i]?.tier?.[tierIndex]?.tierIcon;
              if (dppCurrentImagePath) {
                dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                imagPayload.isToReplace = true;
              }
            }
            if (isEditModeOn && isFromProduct && prevCanvasData?.[i]?.tier?.[tierIndex]?.tierIcon?.includes('product_image')) {
              dppCurrentImagePath = prevCanvasData?.[i]?.tier?.[tierIndex]?.tierIcon;
              if (dppCurrentImagePath) {
                dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                imagPayload.isToReplace = true;
              }
            }
            if (isFromProduct) {
              imagPayload.documentName =
                isEditModeOn && dppCurrentImagePath ? dppCurrentImagePath : `${can?.key}_${i}_tierIcon_${tierIndex}_product_image_${generateUniqueId()}`;
            } else {
              imagPayload.documentName =
                isEditModeOn && dppCurrentImagePath ? dppCurrentImagePath : `${can?.key}_${i}_tierIcon_${tierIndex}_image_${generateUniqueId()}`;
            }

            imagPayload.document = tierData?.tierIcon;
            imagPayload.canvasIndex = i;
            imagPayload.tierIndex = tierIndex;
            imagPayload.keyName = 'tierIcon';
            imagPayload.controlKey = can?.key;

            S3ImagePayload.push(imagPayload);
          }
          if (tierData?.ethicalAuditIcon && isBase64Image(tierData?.ethicalAuditIcon)) {
            let dppCurrentImagePath = '';
            const imagPayload = {};

            if (isEditModeOn && !isFromProduct) {
              dppCurrentImagePath = prevCanvasData?.[i]?.tier?.[tierIndex]?.ethicalAuditIcon;
              if (dppCurrentImagePath) {
                dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                imagPayload.isToReplace = true;
              }
            }
            if (isEditModeOn && isFromProduct && prevCanvasData?.[i]?.tier?.[tierIndex]?.ethicalAuditIcon?.includes('product_image')) {
              dppCurrentImagePath = prevCanvasData?.[i]?.tier?.[tierIndex]?.ethicalAuditIcon;
              if (dppCurrentImagePath) {
                dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                imagPayload.isToReplace = true;
              }
            }

            if (isFromProduct) {
              imagPayload.documentName =
                isEditModeOn && dppCurrentImagePath
                  ? dppCurrentImagePath
                  : `${can?.key}_${i}_ethicalAuditIcon_${tierIndex}_product_image_${generateUniqueId()}`;
            } else {
              imagPayload.documentName =
                isEditModeOn && dppCurrentImagePath ? dppCurrentImagePath : `${can?.key}_${i}_ethicalAuditIcon_${tierIndex}_image_${generateUniqueId()}`;
            }

            imagPayload.document = tierData?.ethicalAuditIcon;
            imagPayload.canvasIndex = i;
            imagPayload.tierIndex = tierIndex;
            imagPayload.keyName = 'ethicalAuditIcon';
            imagPayload.controlKey = can?.key;
            S3ImagePayload.push(imagPayload);
          }
        });
        if (can?.transportIcon && isBase64Image(can?.transportIcon)) {
          let dppCurrentImagePath = '';
          const imagPayload = {};

          if (isEditModeOn && !isFromProduct) {
            dppCurrentImagePath = prevCanvasData?.[i]?.transportIcon;
            if (dppCurrentImagePath) {
              dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
              imagPayload.isToReplace = true;
            }
          }
          if (isEditModeOn && isFromProduct && prevCanvasData?.[i]?.transportIcon?.includes('product_transportIcon_image')) {
            dppCurrentImagePath = prevCanvasData?.[i]?.transportIcon;
            if (dppCurrentImagePath) {
              dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
              imagPayload.isToReplace = true;
            }
          }
          if (isFromProduct) {
            imagPayload.documentName =
              isEditModeOn && dppCurrentImagePath ? dppCurrentImagePath : `${can?.key}_${i}_product_transportIcon_image_${generateUniqueId()}`;
          } else {
            imagPayload.documentName = isEditModeOn && dppCurrentImagePath ? dppCurrentImagePath : `${can?.key}_${i}_transportIcon_image_${generateUniqueId()}`;
          }
          imagPayload.document = can?.transportIcon;
          imagPayload.canvasIndex = i;
          imagPayload.keyName = 'transportIcon';
          imagPayload.controlKey = can?.key;
          S3ImagePayload.push(imagPayload);
        }
      }
      break;
    case 'heading':
      if (can?.iconPath && isBase64Image(can?.iconPath)) {
        let dppCurrentImagePath = '';
        const imagPayload = {};

        if (isEditModeOn && !isFromProduct) {
          dppCurrentImagePath = prevCanvasData?.[i]?.iconPath;
          if (dppCurrentImagePath) {
            dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
            imagPayload.isToReplace = true;
          }
        }
        if (isEditModeOn && isFromProduct && prevCanvasData?.[i]?.iconPath?.includes('product_heading_image')) {
          dppCurrentImagePath = prevCanvasData?.[i]?.iconPath;
          if (dppCurrentImagePath) {
            dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
            imagPayload.isToReplace = true;
          }
        }
        if (isFromProduct) {
          imagPayload.documentName =
            isEditModeOn && dppCurrentImagePath
              ? dppCurrentImagePath
              : handleFromCustomSection(
                  isFromCustomSection,
                  `${can?.key}_${i}_${customSection}_product_heading_image_${generateUniqueId()}`,
                  `${can?.key}_${i}_product_heading_image_${generateUniqueId()}`,
                );
        } else {
          imagPayload.documentName =
            isEditModeOn && dppCurrentImagePath
              ? dppCurrentImagePath
              : handleFromCustomSection(
                  isFromCustomSection,
                  `${can?.key}_${i}_${customSection}_heading_image_${generateUniqueId()}`,
                  `${can?.key}_${i}_heading_image_${generateUniqueId()}`,
                );
        }

        imagPayload.document = can?.iconPath;
        imagPayload.canvasIndex = i;
        imagPayload.keyName = 'iconPath';
        imagPayload.controlKey = can?.key;
        S3ImagePayload.push(imagPayload);
      }
      break;
    case 'company_stamp':
      if (can?.iconCompanyPath && isBase64Image(can?.iconCompanyPath)) {
        let dppCurrentImagePath = '';
        const imagPayload = {};

        if (isEditModeOn && !isFromProduct) {
          dppCurrentImagePath = prevCanvasData?.[i]?.iconCompanyPath;
          if (dppCurrentImagePath) {
            dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
            imagPayload.isToReplace = true;
          }
        }
        if (isEditModeOn && isFromProduct && prevCanvasData?.[i]?.iconCompanyPath?.includes('_company_stamp_image_')) {
          dppCurrentImagePath = prevCanvasData?.[i]?.iconCompanyPath;
          if (dppCurrentImagePath) {
            dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
            imagPayload.isToReplace = true;
          }
        }
        if (isFromProduct) {
          imagPayload.documentName =
            isEditModeOn && dppCurrentImagePath
              ? dppCurrentImagePath
              : handleFromCustomSection(
                  isFromCustomSection,
                  `${can?.key}_${i}_${customSection}_company_stamp_image_${generateUniqueId()}`,
                  `${can?.key}_${i}_company_stamp_image_${generateUniqueId()}`,
                );
        } else {
          imagPayload.documentName =
            isEditModeOn && dppCurrentImagePath
              ? dppCurrentImagePath
              : handleFromCustomSection(
                  isFromCustomSection,
                  `${can?.key}_${i}_${customSection}_company_image_${generateUniqueId()}`,
                  `${can?.key}_${i}_company_image_${generateUniqueId()}`,
                );
        }

        imagPayload.document = can?.iconCompanyPath;
        imagPayload.canvasIndex = i;
        imagPayload.keyName = 'iconCompanyPath';
        imagPayload.controlKey = can?.key;
        S3ImagePayload.push(imagPayload);
      }
      break;
    case 'url_link':
      if (can?.metaData?.image && isBase64Image(can?.metaData?.image)) {
        let dppCurrentImagePath = '';
        const imagPayload = {};

        if (isEditModeOn && !isFromProduct && prevCanvasData?.[i]?.metaData?.image?.includes('dpp_assets')) {
          dppCurrentImagePath = prevCanvasData?.[i]?.metaData?.image;
          if (dppCurrentImagePath) {
            dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
            imagPayload.isToReplace = true;
          }
        }
        if (
          isEditModeOn &&
          isFromProduct &&
          prevCanvasData?.[i]?.metaData?.image?.includes?.('dpp_assets') &&
          prevCanvasData?.[i]?.metaData?.image?.includes?.('product_url_image')
        ) {
          dppCurrentImagePath = prevCanvasData?.[i]?.metaData?.image;
          if (dppCurrentImagePath) {
            dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
            imagPayload.isToReplace = true;
          }
        }
        if (isFromProduct) {
          imagPayload.documentName =
            isEditModeOn && dppCurrentImagePath
              ? dppCurrentImagePath
              : handleFromCustomSection(
                  isFromCustomSection,
                  `${can?.key}_${i}_${customSection}_product_url_image_${generateUniqueId()}`,
                  `${can?.key}_${i}_product_url_image_${generateUniqueId()}`,
                );
        } else {
          imagPayload.documentName =
            isEditModeOn && dppCurrentImagePath
              ? dppCurrentImagePath
              : handleFromCustomSection(
                  isFromCustomSection,
                  `${can?.key}_${i}_${customSection}_url_image_${generateUniqueId()}`,
                  `${can?.key}_${i}_url_image_${generateUniqueId()}`,
                );
        }
        imagPayload.document = can?.metaData?.image;
        imagPayload.canvasIndex = i;
        imagPayload.keyName = 'url_image';
        imagPayload.controlKey = can?.key;
        S3ImagePayload.push(imagPayload);
      }
      break;
    case 'navigation':
      if (can?.anchorMenu?.length > 0) {
        can?.anchorMenu?.forEach((teGr, grIn) => {
          if (teGr?.defaultIcon && isBase64Image(teGr?.defaultIcon)) {
            let dppCurrentImagePath = '';
            const imagPayload = {};
            if (isEditModeOn) {
              dppCurrentImagePath = prevCanvasData?.[i]?.anchorMenu?.[grIn]?.defaultIcon;

              if (!isFromProduct && prevCanvasData?.[i]?.anchorMenu?.[grIn]?.defaultIcon?.includes('dpp_assets')) {
                if (dppCurrentImagePath) {
                  if (dppCurrentImagePath?.includes('/static/')) {
                    dppCurrentImagePath = '';
                  }
                  dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                  if (isBase64Image(dppCurrentImagePath)) {
                    // If it is, create a new image path
                    dppCurrentImagePath = `${can?.key}_${i}_${teGr?.value}_${grIn}_image_${generateUniqueId()}`;
                  } else {
                    imagPayload.isToReplace = true;
                  }
                }
              }
              if (
                isFromProduct &&
                prevCanvasData?.[i]?.anchorMenu?.[grIn]?.defaultIcon?.includes?.('dpp_assets') &&
                prevCanvasData?.[i]?.anchorMenu?.[grIn]?.defaultIcon?.includes?.('product_url_image')
              ) {
                if (dppCurrentImagePath) {
                  if (dppCurrentImagePath?.includes('/static/')) {
                    dppCurrentImagePath = '';
                  }
                  dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                  if (isBase64Image(dppCurrentImagePath)) {
                    // If it is, create a new image path
                    dppCurrentImagePath = `${can?.key}_${i}_${teGr?.value}_${grIn}_image_${generateUniqueId()}`;
                  } else {
                    imagPayload.isToReplace = true;
                  }
                }
              }
            }
            if (isFromProduct) {
              imagPayload.documentName =
                isEditModeOn && dppCurrentImagePath ? dppCurrentImagePath : `${can?.key}_${i}_${teGr?.value}_${grIn}_product_url_image_${generateUniqueId()}`;
            } else {
              imagPayload.documentName =
                isEditModeOn && dppCurrentImagePath ? dppCurrentImagePath : `${can?.key}_${i}_${teGr?.value}_${grIn}_image_${generateUniqueId()}`;
            }
            imagPayload.document = teGr?.defaultIcon;
            imagPayload.canvasIndex = i;
            imagPayload.testGroupIndex = grIn;
            imagPayload.keyName = 'defaultIcon';
            imagPayload.controlKey = can?.key;
            S3ImagePayload.push(imagPayload);
          }
        });
      }
      break;
    case 'care_user_instruction':
      if (can?.careLabelDescriptionArr?.length > 0) {
        can?.careLabelDescriptionArr?.forEach((teGr, grIn) => {
          if (teGr?.image && isBase64Image(teGr?.image)) {
            let dppCurrentImagePath = '';
            const imagPayload = {};

            if (isEditModeOn && !isFromProduct) {
              dppCurrentImagePath = prevCanvasData?.[i]?.careLabelDescriptionArr?.[grIn]?.image;
              if (dppCurrentImagePath) {
                dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                imagPayload.isToReplace = true;
              }
            }
            if (isEditModeOn && isFromProduct && prevCanvasData?.[i]?.careLabelDescriptionArr?.[grIn]?.image?.includes('product_image')) {
              dppCurrentImagePath = prevCanvasData?.[i]?.careLabelDescriptionArr?.[grIn]?.image;
              if (dppCurrentImagePath) {
                dppCurrentImagePath = replaceTheString(dppCurrentImagePath, `${getSlugName()}/dpp_assets/`);
                imagPayload.isToReplace = true;
              }
            }
            if (isFromProduct) {
              imagPayload.documentName =
                isEditModeOn && dppCurrentImagePath
                  ? dppCurrentImagePath
                  : handleFromCustomSection(
                      isFromCustomSection,
                      `${can?.key}_${i}_${customSection}_care_user_label_${grIn}_product_image_${generateUniqueId()}`,
                      `${can?.key}_${i}_care_user_label_${grIn}_product_image_${generateUniqueId()}`,
                    );
            } else {
              imagPayload.documentName =
                isEditModeOn && dppCurrentImagePath
                  ? dppCurrentImagePath
                  : handleFromCustomSection(
                      isFromCustomSection,
                      `${can?.key}_${i}_${customSection}_care_user_label_${grIn}_image_${generateUniqueId()}`,
                      `${can?.key}_${i}_care_user_label_${grIn}_image_${generateUniqueId()}`,
                    );
            }

            imagPayload.document = teGr?.image;
            imagPayload.canvasIndex = i;
            imagPayload.testGroupIndex = grIn;
            imagPayload.keyName = 'image';
            imagPayload.controlKey = can?.key;
            S3ImagePayload.push(imagPayload);
          }
        });
      }
      break;
    default:
      break;
  }
  return S3ImagePayload;
};

export const getAutoIdGenerator = {
  Product_Number_Universal: [
    { label: 'Brand', value: 'brand' },
    { label: 'Product Type', value: 'productType' },
    { label: 'Sub Brand', value: 'subBrand' },
    { label: 'Season', value: 'season' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
    { label: 'Colourway', value: 'colourway' },
    { label: 'Category 1', value: 'category1' },
    { label: 'Category 2', value: 'category2' },
    { label: 'Category 3', value: 'category3' },
    { label: 'Category 4', value: 'category4' },
    { label: '3-digit counter', value: '3', showInput: true, maxLength: 3, disabled: false, isCounterField: true },
    { label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true },
  ],
  Product_Identifier: [
    { label: 'Brand', value: 'brand' },
    { label: 'Product Type', value: 'productType' },
    { label: 'Season', value: 'season' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
    { label: 'Size', value: 'size' },
    { label: 'Colourway', value: 'colourway' },
    { label: 'Category 1', value: 'category1' },
    { label: 'Category 2', value: 'category2' },
    { label: 'Category 3', value: 'category3' },
    { label: 'Category 4', value: 'category4' },
    { label: '3-digit counter', value: '3', showInput: true, maxLength: 3, disabled: false, isCounterField: true },
    { label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true },
    { label: '10-digit counter', value: '10', showInput: true, maxLength: 10, disabled: false, isCounterField: true },
    { label: '11-digit counter', value: '11', showInput: true, maxLength: 11, disabled: false, isCounterField: true },
    { label: '12-digit counter', value: '12', showInput: true, maxLength: 12, disabled: false, isCounterField: true },
    { label: '13-digit counter', value: '13', showInput: true, maxLength: 13, disabled: false, isCounterField: true },
  ],
  Sample_Number: [
    { label: 'Sample Ref', value: 'sample ref' },
    { label: 'Sample Type', value: 'sample type' },
    { label: 'Supplier', value: 'supplier' },
    { label: 'Colour Way', value: 'colour way' },
    { label: 'Grading Group', value: 'grading group' },
    { label: 'Size', value: 'size' },
    { label: 'Length', value: 'length' },
    { label: 'Counter', value: 'counter' },
  ],
  Product_Number: [
    { label: 'Brand', value: 'brand' },
    { label: 'Product Type', value: 'productType' },
    { label: 'Sub Brand', value: 'subBrand' },
    { label: 'Season', value: 'season' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' },
    { label: 'Colourway', value: 'colourway' },
    { label: 'Category 1', value: 'category1' },
    { label: 'Category 2', value: 'category2' },
    { label: 'Category 3', value: 'category3' },
    { label: 'Category 4', value: 'category4' },
    { label: '3-digit counter', value: '3', showInput: true, maxLength: 3, disabled: false, isCounterField: true },
    { label: '5-digit counter', value: '5', showInput: true, maxLength: 5, disabled: false, isCounterField: true },
  ],
};

export const copyToClipboard = async (text) => {
  try {
    // Check if the Clipboard API is available
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
    }
  } catch (err) {
    console.error('Failed to copy text: ', err);
  }
};

const convertRPSheetToJson = async (csv, isHeaderArray) => {
  const lines = csv.split('\n');
  const result = [];
  const headers = parseRow(lines[0]);

  for (let i = 1; i < lines.length; i++) {
    const obj = {};
    const currentline = parseRow(lines[i]);
    if (currentline) {
      for (let j = 0; j < headers.length; j++) {
        obj[headers[j]] = currentline[j] ? currentline[j] : '--';
      }
      result.push(obj);
    }
  }

  const res = JSON.parse(JSON.stringify(result)); // JSON
  const headerObj = headers.reduce((a, v) => ({ ...a, [v]: v }), {});
  const stringArray = [];
  if (isHeaderArray) {
    for (const key in headerObj) {
      stringArray.push(key);
    }
  }
  return {
    data: res.filter((ele) => ele['Product Number*'] && ele['Product Number*'] !== '' && ele['Product Number*'] !== '--'),
    headerKeys: isHeaderArray ? stringArray : headerObj,
  };
};

export const treadRPFile = async (file, isHeaderArray) => {
  const reader = new FileReader();

  const readAsBinaryString = (file) => {
    return new Promise((resolve, reject) => {
      reader.onload = (evt) => {
        resolve(evt.target.result);
      };
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
  };

  const bstr = await readAsBinaryString(file);
  const wb = XLSX.read(bstr, { type: 'binary' });
  const wsname = wb.SheetNames[0];
  const ws = wb.Sheets[wsname];
  const data = XLSX.utils.sheet_to_csv(ws, { header: 1 });

  return convertRPSheetToJson(data, isHeaderArray);
};

// to check uniqness in the array based on multiple key
export const uniqueByKeys = (arr, keys) => {
  return uniqBy(arr, (item) => keys.map((key) => item[key]).join('|'));
};

export function formatDecimalNumber(value) {
  const num = Number(value); // Convert to number
  if (isNaN(num)) return 0; // Handle invalid input

  // Check the number of decimal places
  const decimalPlaces = (num.toString().split('.')[1] || '').length;

  return decimalPlaces > 3 ? num.toFixed(3) : num.toString();
}

export function resetValueInArray(arr) {
  return arr.map((obj) => {
    const newObj = { ...obj };

    // Reset value to null if it exists, but not for "Source" or "Calculation"
    if ('value' in newObj && newObj.value !== null && newObj.value !== undefined && newObj.key !== 'Source' && newObj.key !== 'Calculation') {
      newObj.value = null;
    }

    // Remove isChecked from options if it exists and is true
    if (Array.isArray(newObj.options)) {
      newObj.options = newObj.options.map((option) => {
        if (option.isChecked) {
          const { isChecked, ...rest } = option;
          return rest;
        }
        return option;
      });
    }

    // If obj.key is "Signature", set placeholder to "Signature"
    if (newObj.key === 'Signature') {
      newObj.placeholder = 'Signature';
      newObj.digitalSignPath = '';
    }

    return newObj;
  });
}

// new product costing logic changes as per no mandatory size,length,colourway
export const createCostingDynamicGroupedArray = async (colourway, size, length, setKeysArray, parentKeys) => {
  let array = [];
  array = await Promise.all(updateCostingArray(colourway, size, length));
  if (setKeysArray) {
    setKeysArray.forEach((obj) => {
      array = array.map((data) => {
        data.expandData = _.map(data.expandData, function (element) {
          return _.extend({}, element, { [obj.key]: obj.value });
        });
        return data;
      });
    });
  }
  if (parentKeys) {
    parentKeys.forEach((obj) => {
      array = array.map((data) => {
        return _.extend({}, data, { [obj.key]: obj.value });
      });
    });
  }
  return array;
};

const updateCostingArray = (colourway = [], size = [], length = []) => {
  // ✅ CASE: Colourway + Size + Length
  if (colourway.length > 0 && size.length > 0 && length.length > 0) {
    return colourway.map((col) => ({
      colourway: col?.colourWay || col,
      expandData: getCostingCombinations(size, length, col?.colourWay || col),
      isExpanded: false,
      colourRef: col?.colourRef || col?.newReferences || null,
      sizeRange: size,
      lengthRange: length,
    }));
  }

  // ✅ CASE: Colourway + Size
  if (colourway.length > 0 && size.length > 0) {
    return colourway.map((col) => ({
      colourway: col?.colourWay || col,
      expandData: getCostingCombinations(size, [], col?.colourWay || col),
      isExpanded: false,
      colourRef: col?.colourRef || col?.newReferences || null,
      sizeRange: size,
      lengthRange: [],
    }));
  }

  // ✅ CASE: Colourway only
  if (colourway.length > 0) {
    return colourway.map((col) => ({
      colourway: col?.colourWay || col,
      expandData: [
        {
          size: null,
          length: null,
          colourway: col?.colourWay || col,
        },
      ],
      isExpanded: false,
      colourRef: col?.colourRef || col?.newReferences || null,
      sizeRange: [],
      lengthRange: [],
    }));
  }

  // ✅ CASE: Size + Length (No Colourway)
  if (size.length > 0 && length.length > 0) {
    return [
      {
        colourway: null,
        expandData: getCostingCombinations(size, length, null),
        isExpanded: false,
        colourRef: null,
        sizeRange: size,
        lengthRange: length,
      },
    ];
  }

  // ✅ CASE: Size only (No Colourway, No Length)
  if (size.length > 0) {
    return size.map((siz) => ({
      colourway: null,
      expandData: getCostingSizeCombinations(length, siz),
      isExpanded: false,
      colourRef: null,
      sizeRange: [siz],
      lengthRange: length,
    }));
  }

  // ✅ CASE: Length only
  if (length.length > 0) {
    return [
      {
        colourway: null,
        expandData: getLengthOnlyCombinations(length),
        isExpanded: false,
        colourRef: null,
        sizeRange: [],
        lengthRange: length,
      },
    ];
  }

  // ✅ Default fallback: Nothing provided
  return [
    {
      colourway: null,
      expandData: [
        {
          size: null,
          length: null,
          colourway: null,
        },
      ],
      isExpanded: false,
      colourRef: null,
      sizeRange: [],
      lengthRange: [],
    },
  ];
};

const getLengthOnlyCombinations = (length) => {
  return length.map((valLength) => ({
    size: null,
    length: valLength,
    colourway: null,
  }));
};

export function getCostingSizeCombinations(length, size) {
  const pairs = [];
  if (Array.isArray(length) && length.length > 0) {
    for (const valLength of length) {
      const formatData = {
        size: size.name,
        length: valLength,
        colourway: null,
      };
      pairs.push(formatData);
    }
  }
  if (pairs.length === 0) {
    const formatData = {
      size: size.name,
      length: null,
      colourway: null,
    };
    pairs.push(formatData);
  }
  return pairs;
}

export function getCostingCombinations(size, length, colourway) {
  const pairs = [];
  if (Array.isArray(size) && size.length > 0 && Array.isArray(length) && length.length > 0) {
    for (const eleSize of size) {
      for (const eleLen of length) {
        const formatData = {
          size: eleSize.name,
          length: eleLen,
          colourway,
          colourRef: colourway?.newReferences,
        };
        pairs.push(formatData);
      }
    }
  } else if (Array.isArray(size) && size.length > 0) {
    for (const valSize of size) {
      const formatData = {
        size: valSize.name,
        length: null,
        colourway,
        colourRef: colourway?.newReferences,
      };
      pairs.push(formatData);
    }
  }
  return pairs;
}

export const staticDefaultCostingTemplteColumnArray = [
  {
    title: 'Agents Fee',
    isActive: true,
    showIcon: false,
    isDefault: false,
    tempValue: '0',
    type: 'percentage',
    key: 'percentage',
    aliasName: 'agentsFee',
    isEditable: true,
  },
  {
    title: 'Freight Value',
    type: 'amount',
    key: 'amount',
    tempValue: '0',
    isActive: true,
    isDefault: true,
    aliasName: 'freightValue',
    isEditable: true,
  },
  {
    title: 'Freight %',
    type: 'percentage',
    key: 'percentage',
    tempValue: '0',
    isActive: false,
    isDefault: true,
    aliasName: 'freightPercentage',
    isEditable: true,
  },
  {
    title: 'Freight Automated',
    type: 'amount',
    key: 'amount',
    tempValue: '0',
    isActive: false,
    isDefault: true,
    aliasName: 'freightAutomated',
    isEditable: false,
  },
  {
    title: 'Duty %',
    type: 'percentage',
    key: 'percentage',
    tempValue: '0',
    isActive: true,
    isDefault: true,
    aliasName: 'dutyPercentage',
    isEditable: true,
  },
  {
    title: 'Tariff Cost %',
    type: 'percentage',
    key: 'percentage',
    tempValue: '0',
    isActive: true,
    isDefault: true,
    aliasName: 'tariffCostPercentage',
    isEditable: true,
  },
  {
    title: 'License Royalty Fee',
    type: 'percentage',
    key: 'percentage',
    tempValue: '0',
    isActive: true,
    isDefault: true,
    aliasName: 'licenseRoyaltyFee',
    isEditable: false,
  },
  {
    title: 'Likeness Royalty Fee',
    type: 'percentage',
    key: 'percentage',
    tempValue: '0',
    isActive: true,
    isDefault: true,
    aliasName: 'likenessRoyaltyFee',
    isEditable: false,
  },
];

export const restrictToTwoDecimals = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  // Check if the string contains a decimal point
  const decimalIndex = value.indexOf('.');
  if (decimalIndex === -1) {
    return value; // No decimal point, return as is
  }

  // If there are more than 2 digits after the decimal, truncate
  if (value.length > decimalIndex + 3) {
    return value.substring(0, decimalIndex + 3);
  }

  return value;
};

// sum the price per unit for the testing section product costing
export const sumPricePerUnitTestingAdditionalSection = (data = [], colourWayToggle = false, key = 'pricePerUnit') => {
  if (!Array.isArray(data)) return 0;

  const validItems = data?.filter((item) => !item?.isExcluded && parseFloat(item?.[key] || '0') > 0);

  if (colourWayToggle) {
    const groupedSums = {};

    for (const item of validItems) {
      const colourwayId = item?.colourway?._id;
      const price = parseFloat(item[key]);

      if (!colourwayId) continue;

      if (!groupedSums[colourwayId]) {
        groupedSums[colourwayId] = 0;
      }

      groupedSums[colourwayId] += price;
    }

    return groupedSums; // { colourwayId: sum, ... }
  }
  return validItems?.reduce((sum, item) => {
    return sum + parseFloat(item[key]);
  }, 0);
};

// Default/Static accordion sections configuration
export const RPDefaultAccordionSections = [
  {
    key: 'productInformation',
    title: 'Product Information',
    isEnabled: true,
  },
  {
    key: 'productIdentifier',
    title: 'Product Identifier',
    isEnabled: true,
  },
  {
    key: 'supplier',
    title: 'Supplier',
    isEnabled: true,
  },
  {
    key: 'bom',
    title: 'BOM',
    isEnabled: true,
  },
  {
    key: 'specifications',
    title: 'Specifications',
    isEnabled: true,
  },
  {
    key: 'testing',
    title: 'Testing',
    isEnabled: true,
  },
  {
    key: 'careUser',
    title: 'Care/User',
    isEnabled: true,
  },
  {
    key: 'careUserLayout',
    title: 'Care/User Layout',
    isEnabled: true,
  },
  {
    key: 'sampling',
    title: 'Sampling',
    isEnabled: true,
  },
  {
    key: 'costing',
    title: 'Costing',
    isEnabled: true,
  },
  {
    key: 'purchaseOrder',
    title: 'Purchase Order',
    isEnabled: true,
  },
];

export const mergedMonthYearOptions = (months, years) => {
  if (Array.isArray(months) && months.length > 0 && Array.isArray(years) && years.length > 0) {
    return months.flatMap((month) =>
      years.map((year) => ({
        ...month,
        _id: `${month.value}_${year}`,
        name: `${month.value}_${year}`,
        label: `${month.label} ${year}`,
        value: `${month.value}_${year}`,
        year,
      })),
    );
  }
  return months || [];
};
export const templateSelectStyles = {
  ...SystemMasterSelect,
  menu: (provided) => ({
    ...provided,
    margin: '4px 0',
    border: '1px solid #848484',
    borderRadius: 0,
    position: 'absolute',
    bottom: '100%',
    top: 'auto',
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '150px',
    overflowY: 'auto',
  }),
};

export const mergerYearOptionsToStringValue = (months, years) => {
  if (Array.isArray(months) && months.length > 0 && Array.isArray(years) && years.length > 0) {
    return months.flatMap((month) => years.map((year) => `${month}_${year}`));
  }
  return months || [];
};

export const bomUsageFields = [
  { label: 'Reference Code', value: 'referenceCode' },
  { label: 'Sub BOM List', value: 'subBomList' },
  { label: 'Sub BOM Switch', value: 'subBOMSwitch' },
  { label: 'BOM Type', value: 'bomType' },
  { label: 'BOM Sub Type', value: 'bomSubType' },
  { label: 'BOM Component Sub Type', value: 'bomComponentSubType' },
  { label: 'BOM Approvals', value: 'bomApprovals' },
  { label: 'BOM Description', value: 'bomDescription' },
  { label: 'Additional Info', value: 'additionalInfo' },
  { label: 'Composition', value: 'composition' },
  { label: 'Weight', value: 'weight' },
  { label: 'Weight Unit Of Measure', value: 'weightUnitOfMeasure' },
  { label: 'Size', value: 'size' },
  { label: 'Size Unit Of Measure', value: 'sizeUnitOfMeasure' },
  { label: 'Quantity', value: 'quantity' },
  { label: 'Quantity Unit Of Measure', value: 'quantityUnitOfMeasure' },
  { label: 'Price Per Unit', value: 'pricePerUnit' },
  { label: 'Price Per Unit Currency', value: 'pricePerUnitCurrency' },
  { label: 'Quality', value: 'quality' },
  { label: 'Quality Auto Approve', value: 'qualityAutoApprove' },
  { label: 'Color Auto Approve', value: 'colorAutoApprove' },
  { label: 'Suppliers', value: 'suppliers' },
  { label: 'Finished', value: 'finished' },
  { label: 'New Placement', value: 'newPlacement' },
  { label: 'Transport', value: 'transport' },
];

// new calcultaion wiring array
export const calcualtionControlSymbols = (
  checkFormulaHasStringType,
  counterState,
  checkFormulaHasPercentageAtlast,
  checkRootAtLast,
  disableNumbers,
  disableSpecialCharacter,
  squareAtLast,
  piAtLast,
  closingBreak,
) => [
  {
    name: '+',
    label: '+',
    type: 'symbol',
    className: 'calculation-btn',
    disabled: checkFormulaHasStringType || counterState || checkRootAtLast,
    sequence: 22,
    icon: 'icon-cal-plus',
  },
  {
    name: '-',
    label: '-',
    type: 'symbol',
    className: 'calculation-btn',
    disabled: checkFormulaHasStringType || counterState || checkRootAtLast,
    sequence: 23,
    icon: 'icon-cal-minus',
  },
  {
    name: '×',
    label: '×',
    type: 'symbol',
    className: 'calculation-btn',
    disabled: checkFormulaHasStringType || counterState || checkRootAtLast,
    sequence: 19,
    icon: 'icon-cal-multiply',
  },
  {
    name: '÷',
    label: '÷',
    type: 'symbol',
    className: 'calculation-btn',
    disabled: checkFormulaHasStringType || counterState || checkRootAtLast,
    sequence: 20,
    icon: 'icon-cal-divide',
  },
  {
    name: '%',
    label: '%',
    type: 'symbol',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || counterState || checkRootAtLast || disableNumbers,
    sequence: 14,
    icon: 'icon-cal-percentage',
  },
  {
    name: '(',
    label: '(',
    type: 'symbol',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableSpecialCharacter || squareAtLast || piAtLast,
    sequence: 9,
    icon: 'icon-cal-bracket-left',
  },
  {
    name: ')',
    label: ')',
    type: 'symbol',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasStringType || closingBreak,
    sequence: 10,
    icon: 'icon-cal-bracket-right',
  },
  {
    name: '&',
    label: '&',
    type: 'symbol',
    className: 'calculation-btn-16',
    disabled: !checkFormulaHasStringType,
    sequence: 4,
    icon: 'icon-Ampersand',
  },
  {
    name: '1',
    label: '1',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 16,
    icon: 'icon-cal-one',
  },
  {
    name: '2',
    label: '2',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 17,
    icon: 'icon-cal-two',
  },
  {
    name: '3',
    label: '3',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 18,
    icon: 'icon-cal-three',
  },
  {
    name: '4',
    label: '4',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 11,
    icon: 'icon-cal-four',
  },
  {
    name: '5',
    label: '5',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 12,
    icon: 'icon-cal-five',
  },
  {
    name: '6',
    label: '6',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 13,
    icon: 'icon-cal-six',
  },
  {
    name: '7',
    label: '7',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 6,
    icon: 'icon-cal-seven',
  },
  {
    name: '8',
    label: '8',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 7,
    icon: 'icon-cal-eight',
  },
  {
    name: '9',
    label: '9',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 8,
    icon: 'icon-cal-nine',
  },
  {
    name: '0',
    label: '0',
    type: 'Number',
    className: 'calculation-btn-16',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 21,
    icon: 'icon-cal-zero',
  },
  {
    name: '.',
    label: '.',
    type: 'Number',
    className: 'calculation-btn',
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 15,
    icon: 'icon-cal-dot',
  },
  {
    name: 'π',
    label: 'π',
    type: 'symbol',
    className: 'calculation-btn-16',
    specialCondition: true,
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers,
    sequence: 3,
    icon: 'icon-cal-pi',
  },
  {
    name: '²',
    label: '²',
    type: 'symbol',
    className: 'calculation-btn-16',
    specialCondition: true,
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || counterState || checkRootAtLast || squareAtLast,
    sequence: 2,
    icon: 'icon-cal-x2',
  },
  {
    name: '√',
    label: '√',
    type: 'symbol',
    className: 'calculation-btn-16',
    specialCondition: true,
    disabled: checkFormulaHasPercentageAtlast || checkFormulaHasStringType || disableNumbers || disableSpecialCharacter,
    sequence: 1,
    icon: 'icon-cal-sqrt',
  },
  {
    name: 'C',
    label: 'C',
    type: 'clear',
    className: 'calculation-btn-16',
    disabled: false,
    sequence: 5,
    icon: 'icon-Calculation-clear',
  },
];

export const staticDefaultCostingInfoColumnArray = [
  { title: 'Reference', type: '', aliasDisplayName: '', isDefault: true },
  { title: 'Supplier', type: '', aliasDisplayName: '', isDefault: true },
  { title: 'Factory', type: '', aliasDisplayName: '', isDefault: true },
  { title: 'Costing Template', type: '', aliasDisplayName: '', isDefault: true },
  { title: 'Currency',         type: '', aliasDisplayName: '', isDefault: true },
  { title: 'MOQ Needed',       type: '', aliasDisplayName: '', isDefault: true },
  { title: 'Target Cost',      type: '', aliasDisplayName: '', isDefault: true },
  { title: 'Incoterms',        type: '', aliasDisplayName: '', isDefault: true },
  { title: 'Shipment Method',  type: '', aliasDisplayName: '', isDefault: true },
  { title: 'Sample Ref',       type: '', aliasDisplayName: '', isDefault: true },
];

/**
 * Drops a saved costing filter that still carries the retired `costingStage` key (BM-9901).
 * Every user's browser holds one from before the rename. Its stored ids are costing-stage ids, which
 * are meaningless as sample ids, so the whole blob is discarded rather than translated key by key —
 * a stale key would otherwise be sent to an endpoint that no longer understands it and would light
 * up the "filter applied" pill with nothing behind it.
 * @param {string} storageKey the localStorage key holding the saved filter
 * @returns {void}
 */
export const discardStaleCostingFilter = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'costingStage' in parsed) {
      localStorage.removeItem(storageKey);
    }
  } catch {
    localStorage.removeItem(storageKey);
  }
};

/**
 * Resolve a presigned read URL for an S3 object key and trigger a browser download.
 * Fetches the object as a blob so the filename is honoured even for cross-origin S3 URLs
 * (the anchor `download` attribute is ignored on cross-origin navigations).
 * @param {string} objectKey - S3 object key of the stored document
 * @param {string} fileName - Base name to save the file as (without extension)
 * @param {string} extension - File extension (without leading dot)
 * @returns {Promise<void>}
 */
export const downloadS3ObjectByKey = async (objectKey, fileName, extension) => {
  if (!objectKey) {
    throw new Error('Missing object key for download');
  }
  const safeName = fileName || 'document';
  const downloadName = extension ? `${safeName}.${extension}` : safeName;
  const response = await getPreReadSignedWriteUrl({
    objectKey: String(objectKey),
    expiresInSeconds: 3600,
    disposition: 'attachment',
    downloadName,
  });
  if (response?.code !== 200 || !response?.data) {
    throw new Error('Failed to resolve download URL');
  }
  const link = document.createElement('a');
  link.href = response.data;
  link.download = downloadName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const RETIRED_AUTO_ID_COLOUR_GROUPS_TOKEN = 'colourGroups';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Reads a stored date-only value back as the calendar day it stands for. New values are saved at
 * UTC midnight; older ones were saved as the picker's local midnight, which sits within twelve hours
 * of UTC midnight. Rounding to the nearest UTC midnight recovers the picked day for both.
 * @param {Date|string|null|undefined} value - The stored value.
 * @returns {moment.Moment|null} UTC midnight of the calendar day, or null when unset or unparseable.
 */
export const toStoredCalendarDay = (value) => {
  if (!value) return null;
  const parsed = moment.utc(value);
  if (!parsed.isValid()) return null;
  return moment.utc(Math.round(parsed.valueOf() / DAY_IN_MS) * DAY_IN_MS);
};

/**
 * Formats a stored date-only value as its calendar day.
 * @param {Date|string|null|undefined} value - The stored value.
 * @param {string} dateFormat - moment format string.
 * @returns {string} The formatted day, or '-' when unset.
 */
export const formatStoredCalendarDate = (value, dateFormat) => {
  const day = toStoredCalendarDay(value);
  return day ? day.format(dateFormat) : '-';
};

/**
 * True when a stored date-only value falls before the viewer's today.
 * @param {Date|string|null|undefined} value - The stored value.
 * @returns {boolean} Whether the day has passed.
 */
export const isStoredCalendarDateBeforeToday = (value) => {
  const day = toStoredCalendarDay(value);
  return Boolean(day) && day.format('YYYY-MM-DD') < moment().format('YYYY-MM-DD');
};

/**
 * Turns a stored date-only value into the local Date a date picker shows for that calendar day.
 * @param {Date|string|null|undefined} value - The stored value.
 * @returns {Date|null} Local midnight of the calendar day, or null when unset or unparseable.
 */
export const toStoredCalendarPickerDate = (value) => {
  const day = toStoredCalendarDay(value);
  return day ? new Date(day.year(), day.month(), day.date()) : null;
};

/**
 * The current calendar year as a label — what year dropdowns open scrolled to.
 * @returns {string} The current year, e.g. "2026".
 */
export const getCurrentYearLabel = () => String(new Date().getFullYear());
